// StudioNext.tsx — PREVIEW of the redesigned Studio.
//
// WHY A SEPARATE PAGE: the live Studio ("/") is what real users are on right
// now. This is a parallel implementation so the new goal-first flow can be
// built and reviewed without touching a single line the live page renders. It
// shares the real engine (decode → separate → mixer → export → sections), so
// what you hear here is what shipping would sound like — only the surface is
// new. Nothing links to it.
//
// DESIGN: "Riffloop Studio Refresh **v2**" Claude Design handoff — the console
// revision: left product rail, channel-rack mixer (strip · pan knob · per-stem
// lane), timeline ruler, metronome lane, hardware-style transport.
//
// Four review corrections carried over from the v1 audit, all of which v2 still
// had: (1) the CTA sits BELOW the section picker it names, (2) export gating is
// our real rule — Pro + upload-only, all-or-nothing, not the mockup's invented
// per-stem "Ready/Pro" tiers, (3) no "with export" promise in the stem hint,
// (4) the keyboard legend matches the real bindings (move / resize, not A / B).
//
// Controls in the mockup are wired to real behaviour or left out — pan, the
// metronome, tempo and time signature all do what they say.
// The one thing drawn but never rendered in v2 (a 14-segment level meter) is
// omitted rather than faked.
//
// SCOPE: the Upload source is fully wired. YouTube / SoundCloud capture stays
// on the live Studio and says so plainly.
import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { AppShell } from '../components/AppShell';
import { setRailCollapsed } from '../lib/railState';
import { CLICK_SR, CLICK_ACCENT_B64, CLICK_NORMAL_B64 } from '../lib/clickSample';
import { RightsModal } from '../components/RightsModal';
import { UpgradeModal, type UpgradeReason } from '../components/UpgradeModal';
import { FullSongUpsell } from '../components/FullSongUpsell';
import { Waveform, computeEnvelope, computePeaks, seededBars } from '../components/Waveform';
import { Icons, STEM_UI, STEM_COLOR, stemMark } from '../components/studioIcons';
import {
  auth as authApi,
  sections as sectionsApi,
  usage as usageApi,
  loops as loopsApi,
  type Section,
  type UsageSummary,
} from '../lib/api';
import { separateClientSide, decodeFileToStereo, preloadEngine, prefetchStandardEngine, ensureEngineReady, engineIsCached, type SepEngine } from '../lib/clientSep';
import {
  loadYouTubeAPI, parseYtVideoId, embedErrorMessage, tabCaptureSupported, isMobileTier, startCapture,
  captureErrorMessage, CaptureError, YT_STATE,
  type YtPlayer, type CapturePhase, type CaptureHandle,
} from '../lib/ytCapture';
import { createScPlayer, parseSoundCloudUrl, scEmbedErrorMessage } from '../lib/scCapture';
import { isTwoStemResult, TWO_STEM_OTHER_NAME } from '../lib/deviceTier';
import { ContinueOnDesktop } from '../components/ContinueOnDesktop';
import { fetchYtSuggestions, type Suggestion } from '../lib/ytSuggest';
import { detectKeyBpm, type KeyBpmResult } from '../lib/keyBpm';
import { detectChords, chordsBetaFor, chordsPreferFlats, formatChord, keyFromChords, simplifyChords, type ChordSegment } from '../lib/chordDetect';
import { snapChordsToGrid, gridFromNeuralBeats, BEAT_GRID_VERSION, type BeatGrid } from '../lib/beatGrid';
import { detectBeatsNeural } from '../lib/beatDetect';
import { shapeForChord, type ChordShape, type ShapeStyle } from '../lib/chordShapes';
import { soloStem, type SoloRegion } from '../lib/soloDetect';
import {
  StemEngine,
  DEFAULT_MIX,
  MIX_PRESETS,
  STEMS,
  audibleVolume,
  type MixState,
  type StemName,
} from '../lib/studioEngine';
import {
  renderAndEncode, renderClickTrack, encodeBuffer, saveBlob, safeBaseName, m4aSupported,
  FORMAT_EXT, type ExportFormat,
} from '../lib/audioExport';
import { track, sepErrorReason } from '../lib/track';
import { funnelEvent } from '../lib/funnel';
import { STEM_META, fmtTime } from '../lib/format';
import { useAuth } from '../lib/auth';
import { hasAcknowledgedLocally } from '../lib/rights';
import { cacheSeparation, linkSection, newSeparationKey, getSeparation, getSourceBlob, loadSectionIndex, setSeparationKeyBpm, setSeparationChords, setSeparationBeatGrid, stemUrlsFrom, listSeparations } from '../lib/stemCache';
import { rememberUpload, listRecentUploads, getUploadBlob, forgetUpload, type RecentUploadMeta } from '../lib/uploadCache';
import { claimHandoff } from '../lib/handoffClaim';
import './studio-next.css';

// ── goals ─────────────────────────────────────────────────────────────────
// Copy, order and icons are the extension's GOAL_DEFS verbatim — this flow is
// the one users already know from the YouTube widget, so drift here is a
// regression in familiarity, not a design choice.
type GoalKey = 'loop' | 'karaoke' | 'backing' | 'studio';

interface GoalDef {
  key: GoalKey;
  title: string;
  desc: string;
  hint: string;
  icon: string; // 24×24 stroke path
}

const GOALS: GoalDef[] = [
  {
    key: 'loop',
    title: 'Loop a section',
    desc: 'Repeat any A→B part. Original audio, untouched.',
    hint: 'Drag A and B to set the part of the song you want to loop',
    icon: 'M17 2l4 4-4 4M3 11v-1a4 4 0 014-4h14M7 22l-4-4 4-4M21 13v1a4 4 0 01-4 4H3',
  },
  {
    key: 'karaoke',
    title: 'Remove vocals',
    desc: 'Instant — vocals out, sing over the band.',
    // Names the OPERATION, not one reason for wanting it: "sing over" was
    // borrowed from the card's pitch above, but people cut vocals to learn the
    // backing, to transcribe, or to practise — the hint sits on the waveform
    // and should just say which stretch gets processed.
    hint: 'Drag A and B to set the part you want to remove vocals from',
    icon: 'M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v3M4 4l16 16',
  },
  {
    key: 'backing',
    title: 'Backing track',
    desc: 'Remove one instrument and play its part yourself.',
    // Same rule as the karaoke hint above: the operation, not the reason.
    hint: 'Drag A and B to set the part you want a backing track for',
    icon: 'M11.9 12.1l4.514-4.514M20.1 2.3a1 1 0 0 0-1.4 0l-1.114 1.114A2 2 0 0 0 17 4.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 17.828 7h1.344a2 2 0 0 0 1.414-.586L21.7 5.3a1 1 0 0 0 0-1.4zM6 16l2 2M8.23 9.85A3 3 0 0 1 11 8a5 5 0 0 1 5 5 3 3 0 0 1-1.85 2.77l-.92.38A2 2 0 0 0 12 18a4 4 0 0 1-4 4 6 6 0 0 1-6-6 4 4 0 0 1 4-4 2 2 0 0 0 1.85-1.23z',
  },
  {
    key: 'studio',
    title: 'Stem separation',
    desc: 'Full control — mix every instrument yourself.',
    // Same rule again: the operation, not the reason — and it names WHAT you
    // get (stems), never how it is produced.
    hint: 'Drag A and B to set the part you want to separate into stems',
    icon: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  },
];

const goalDef = (k: GoalKey) => GOALS.find((g) => g.key === k) || GOALS[0];

// Opening mix per goal — the extension's mixForIntent(). Every branch returns
// all six stems so nothing can be left undefined downstream.
function mixForGoal(goal: GoalKey, opts: { guide: boolean; mutes: StemName[] }): Record<StemName, number> {
  const mix = Object.fromEntries(STEMS.map((s) => [s, 1])) as Record<StemName, number>;
  if (goal === 'karaoke') {
    mix.vocals = opts.guide ? 0.2 : 0;
    return mix;
  }
  if (goal === 'backing') {
    const mutes = opts.mutes.length ? opts.mutes : (['drums'] as StemName[]);
    for (const m of mutes) mix[m] = 0;
    return mix;
  }
  return mix; // studio (and loop, which never separates)
}

// The stem a run is *nominally* about — drives the section chip's colour/icon
// and the `stem` field on the quota/telemetry call.
function nominalStem(goal: GoalKey, mutes: StemName[]): StemName {
  if (goal === 'backing') return mutes[0] || 'drums';
  return 'vocals';
}

const PREFS_KEY = 'rl_next_goal_v1';
const RAW_LOOPS_KEY = 'rl_next_rawloops_v1';
// Last local date the daily quota heads-up was shown, so it interrupts once a
// day rather than on every page load.
const QUOTA_NOTICE_KEY = 'rl_quota_notice_day';
// Once per SESSION, not per account: the 60s export cap is invisible on short
// sections (the trim gate only fires past it), so a free user exporting 20s
// loops never learns full-length exports are the thing Pro buys. Per-account
// would teach it once and vanish; every export would nag.
const PLAN_NOTICE_KEY = 'rl_export_plan_notice';
const SESSION_KEY = 'rl_next_session_v1';
const MIN_REGION = 3;

/**
 * The free-plan ceiling on ONE separated section, in seconds. Whole songs are
 * what Pro buys — the same rule the extension has always had (its 60s loop cap)
 * and the same one /splitter states on its plan table. Before this, the Studio
 * separated any length free, which made both of those walls decorative: a user
 * who met the limit anywhere could get the whole song here for nothing.
 *
 * Looping is NOT affected (it costs nothing and stays unlimited), and neither
 * is anything already separated — this bounds new work only.
 */
const FREE_SECTION_SECONDS = 60;

// Where an untouched selection starts out: centred in the track, ported from
// the extension's centeredStagingWindow(). Deliberately NOT hugging 0:00 — a
// region parked at the head reads as "where you are", a status; a box sitting
// mid-waveform reads as an object placed there for you to MOVE, which together
// with the drag call-out is what teaches that the section is yours to
// position. (Playback is untouched — the track still plays from the head while
// the box sits mid-wave, and that mismatch is the point.) Length is the
// extension's recipe too: ~8% of the song (its 72px-at-900px legibility
// target) so a long track's suggestion never renders as a hairline that reads
// as a single marker, bounded 10..30s and clamped to the song.
function centeredRegion(dur: number) {
  const len = Math.max(MIN_REGION, Math.min(30, Math.max(10, Math.round(dur * 0.08)), dur));
  const start = Math.max(0, Math.round((dur - len) / 2));
  return { start, end: Math.min(dur, start + len) };
}

// 'all' = every channel as its own file, Moises' "Export All Channels".
// 'metronome' = the click track, generated rather than separated.
type ExportTarget = 'mix' | 'all' | 'metronome' | StemName;

// What the single pre-export gate has to say. 'wav' is a dead end (the format
// is Pro-only, there is nothing to hand over); 'export' can always complete,
// carrying the plan trim, the one-time rights attestation, or both at once.
type ExportGate =
  | { kind: 'wav' }
  | {
      kind: 'export';
      target: ExportTarget;
      format: ExportFormat;
      trimmed: boolean;
      needsRights: boolean;
      /** Free-plan awareness notice (section under the cap, nothing else to
          say). Fires only ALONE — see requestExport. secLen feeds the copy. */
      planNotice?: boolean;
      secLen?: number;
    };

// Free-plan exports aren't blocked outright — they're trimmed to the first
// N seconds of the separated section (with a short fade), Moises-style: the
// plan gate fires when the export starts, not as a dead menu. Pro is untouched.
const FREE_EXPORT_PREVIEW_SECONDS = 60;
// The click is free through the first 30 s of a song, matching the chords.
const FREE_METRONOME_SECONDS = 30;
// Chords are read for the first 30 seconds on free.
const FREE_CHORDS_SECONDS = 30;
const PREVIEW_FADE_SECONDS = 1;

// Glyphs local to the export surfaces (locked-format chip, plan-gate art, and
// the two channel rows that aren't stems).
const I_LOCK = 'M8 10.2V7.5a4 4 0 0 1 8 0v2.7M6.2 10.2h11.6v9.3H6.2Z';
const I_DL = 'M12 4v10m0 0 4.2-4.2M12 14 7.8 9.8M5 19h14';
const I_METRO = 'M9.4 3.5h5.2l3.6 17H5.8ZM12 20.5 16.5 7';
const I_MIX = 'M5 7h14M5 12h14M5 17h14M9 5v4M15 10v4M11 15v4';
// Vertical kebab — the phone header's "Song options" trigger. Drawn as three
// short strokes so the shared <Ico> stroke rendering gives round dots.
const I_DOTS = 'M12 5.2v.1M12 12v.1M12 18.8v.1';

/**
 * What a reload needs to put you back exactly where you were. The audio itself
 * already lives in IndexedDB under `key`; this is only the small amount of
 * state around it (which mix, which section, which goal).
 */
interface MixerSession {
  key: string;
  sourceRef: string;
  sourceTitle: string;
  goal: GoalKey;
  primaryStem: StemName;
  region: { start: number; end: number };
  vols: Record<string, number>;
  pans: Record<string, number>;
  loop: boolean;
  speed: number;
  transpose: number;
  savedAt: number;
}

interface RawLoop {
  id: string;
  sourceRef: string;
  label: string;
  start: number;
  end: number;
  createdAt: number;
}

interface LinkMeta {
  kind: 'yt' | 'sc';
  /** YouTube video id, or the canonical SoundCloud track URL. */
  ref: string;
  url: string;
  title: string;
  author: string;
  thumb: string;
  /** From /api/yt-meta, merged in after oEmbed. Absent until it answers, and
      absent forever if it can't — the card is designed to read fine without. */
  views?: number | null;
  likes?: number | null;
  durationSec?: number;
}

// Views/likes/duration for the preview card, from our own /api/yt-meta proxy
// (the YouTube Data API key lives server-side; oEmbed returns none of this).
// Session-cached per video id so re-pasting the same link doesn't re-hit the
// backend. Returns null on any failure — quota, offline, an older backend
// without the route — and the caller then keeps the oEmbed-only card.
// Ported from the previous Studio, which had this before the v2 cutover.
async function ytMeta(videoId: string): Promise<{ views: number | null; likes: number | null; durationSec: number } | null> {
  const cacheKey = 'rl_ytmeta_' + videoId;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch { /* private mode */ }
  try {
    const res = await fetch('/api/yt-meta?v=' + encodeURIComponent(videoId), { credentials: 'include' });
    if (!res.ok) return null;
    const j = (await res.json()) as { ok?: boolean; views?: number | null; likes?: number | null; durationSec?: number };
    if (!j.ok) return null;
    const out = { views: j.views ?? null, likes: j.likes ?? null, durationSec: j.durationSec ?? 0 };
    try { sessionStorage.setItem(cacheKey, JSON.stringify(out)); } catch { /* private mode */ }
    return out;
  } catch {
    return null;
  }
}

// 651196 → "651K", 8473 → "8.5K", 185927253 → "186M".
function compactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const k = n / 1000;
    return (k >= 100 ? Math.round(k) : Math.round(k * 10) / 10) + 'K';
  }
  const m = n / 1_000_000;
  return (m >= 100 ? Math.round(m) : Math.round(m * 10) / 10) + 'M';
}

interface Loaded {
  stemUrls: Record<StemName, string>;
  emptyStems: Set<StemName>;
  peaks: number[];
  duration: number;
  goal: GoalKey;
  primaryStem: StemName;
  sourceTitle: string;
  sourceRef: string;
  clientStemsKey: string;
  solos: SoloRegion[];
}

function cleanTrackName(name: string): string {
  return name.replace(/\.(webm|mp3|wav|m4a|flac|ogg|opus|aac|mp4|mov)$/i, '').trim() || name;
}

function fmtBytes(n: number): string {
  return n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
}

// Pull a YouTube id out of a stored section ref so the resume card can show the
// real thumbnail, exactly as the live rail does.
/**
 * True for a SoundCloud link. Matches on the parsed hostname rather than a
 * substring: the previous test required `^` or `.` immediately before
 * "soundcloud.com", which a real URL never has (it's preceded by "//"), so
 * every SoundCloud ref was silently classified as YouTube.
 */
function isSoundCloudRef(ref: string): boolean {
  try {
    return /(^|\.)soundcloud\.com$/i.test(new URL(ref).hostname);
  } catch {
    return false;
  }
}

function ytIdOf(ref: string): string | null {
  const m = /(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/.exec(ref || '');
  if (m) return m[1];
  return /^[A-Za-z0-9_-]{11}$/.test(ref || '') ? ref : null;
}

function hashSeed(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h || 7;
}

function MiniBars({ seed }: { seed: number }) {
  return (
    <span className="sx-minibars" aria-hidden>
      {seededBars(22, seed).map((h, i) => (
        <i key={i} style={{ height: `${Math.round(h * 100)}%` }} />
      ))}
    </span>
  );
}

/** The recents row's trace — the REAL envelope of the stored file, downsampled
 *  from the 150 points the picker already computed. MiniBars fakes a waveform
 *  from a hash because a library row has no audio to read; here we do, and a
 *  true shape is how you recognise a take at a glance. */
function UpWave({ peaks }: { peaks: number[] }) {
  const bars = useMemo(() => {
    const N = 22;
    if (!peaks.length) return seededBars(N, 7);
    const step = peaks.length / N;
    return Array.from({ length: N }, (_, i) => {
      let peak = 0;
      for (let j = Math.floor(i * step); j < Math.min(peaks.length, Math.floor((i + 1) * step) || 1); j++) {
        if (peaks[j] > peak) peak = peaks[j];
      }
      return peak || 0.06;
    });
  }, [peaks]);
  return (
    <span className="sx-minibars" aria-hidden>
      {bars.map((h, i) => (
        <i key={i} style={{ height: `${Math.round(h * 100)}%` }} />
      ))}
    </span>
  );
}

// The sidebar-panel glyph Moises uses for the rail toggle.
function PanelIcon({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <line x1="9" y1="4" x2="9" y2="20" />
    </svg>
  );
}

/** The result header's track name, truncated on WORD boundaries.
 *
 * CSS `text-overflow: ellipsis` cuts by character, so a long name lands as a
 * half-word ("…Alex Claffy, Micha…"). This measures the real rendered font and
 * drops whole words until the name fits, so the cut always falls on a space.
 *
 * The width it fits into is derived from the ROW — the header's width minus its
 * fixed-size controls — never from the title's own box. That box hugs its text
 * (`flex: 0 1 auto`), so measuring it would mean shorter text → wider box →
 * re-expand → re-truncate, an oscillation that never settles.
 */
/** The result header's musical-metadata glyphs.
 *
 * A component rather than the single `d` string <Ico> takes: the note needs a
 * filled head sitting against stroked staff lines, and the metronome a filled
 * weight — neither of which one path can express.
 */
function MetaIcon({ kind, size = 19 }: { kind: 'tempo' | 'key' | 'meter'; size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
    >
      {kind === 'tempo' && (
        <>
          {/* Case, scale bar, and the pendulum pivoting off that bar with its
              weight. The rod deliberately starts at the bar instead of crossing
              the whole case — a full diagonal reads as a strike-through. */}
          <path d="M9.9 3.9h4.2l4 16.5H5.9z" />
          <path d="M7.4 15.9h9.2" />
          <path d="M12 15.9 14.9 6.2" />
          <circle cx="14.1" cy="8.9" r="1.5" fill="currentColor" stroke="none" />
        </>
      )}
      {kind === 'key' && (
        <>
          {/* A note on the staff. The staff is held back at 0.7 so the note
              reads first at this size; the previous bare sharp was being taken
              for a hash. */}
          <path d="M4 7.5h16M4 12h16M4 16.5h16" opacity="0.7" />
          <ellipse cx="9.4" cy="16.2" rx="3" ry="2.3" fill="currentColor" stroke="none" transform="rotate(-22 9.4 16.2)" />
          <path d="M12.2 15.6V5.6" strokeWidth="1.9" />
        </>
      )}
      {kind === 'meter' && (
        <>
          {/* One bar between two barlines: strong beat, then weak. Deliberately
              NOT staff-based — on staff lines it rhymed with the key glyph next
              to it and the two read as a matched pair rather than two controls.
              This also says what the chip does, which is set where the
              metronome accents. Common time (C) was the prettier option but
              means 4/4 specifically, so it would be wrong on 3/4, 6/8 and 5/4. */}
          <path d="M4.4 5.6v12.8M19.6 5.6v12.8" />
          <circle cx="9" cy="12" r="1.9" fill="currentColor" stroke="none" />
          <circle cx="14.4" cy="12" r="1.4" fill="currentColor" stroke="none" opacity="0.65" />
        </>
      )}
    </svg>
  );
}

/** One mixer lane's waveform: a canvas-drawn min/max envelope.
 *
 * Ported from how Moises paints theirs (read off their live canvas): a
 * continuous filled envelope at better-than-pixel horizontal resolution — the
 * backing store runs 2× device pixels and the envelope carries ~10 buckets a
 * second, so the downscale antialiases columns into a smooth dense trace. The
 * old renderer was 420 flex <div>s per lane, which is why it read as a bar
 * chart: ~2.3px steps and a hard edge on every bar.
 *
 * Ink comes from the element's computed `color` (CSS decides, incl. the dim
 * state), painted as the same 0.86→0.64 top-to-bottom fade the divs had.
 */
// Guitar voicing as an SVG grid: strings vertical, frets horizontal, the way
// every chord book prints them. Sized by `w` so the same component serves the
// big current chord and the small upcoming ones. Colours come from currentColor
// so the dimmed states are pure CSS.
function ChordDiagram({ shape, w = 108 }: { shape: ChordShape; w?: number }) {
  const played = shape.frets.filter((f) => f > 0);
  const maxF = played.length ? Math.max(...played) : 0;
  const minF = played.length ? Math.min(...played) : 1;
  // Shapes above the 4th fret are drawn from their own lowest fret with a
  // position label, exactly as printed chord charts do.
  const base = maxF > 4 ? minF : 1;
  // `w` sizes the GRID, not the element: a position label adds width beside it
  // instead of shrinking the strings, so every chord in the carousel draws at
  // the same scale whether or not it carries an "8fr". (Declaring a viewBox
  // wider than the width would just scale the whole thing down instead.)
  const SP = w / 5.62;                // string spacing
  const x0 = SP * 0.62;
  const gridW = SP * 5;
  const top = SP * 0.86;              // room for the x/o row
  const FH = SP * 1.05;               // fret height — charts run taller than wide
  const h = top + FH * 4 + SP * 0.34 + SP * 0.52; // last term: string-name row
  const tw = w + (base > 1 ? SP * 1.15 : SP * 0.24);
  const STRINGS = ['E', 'A', 'D', 'G', 'B', 'E'];
  const sx = (i: number) => x0 + i * SP;
  const fy = (f: number) => top + (f - base + 1) * FH;

  return (
    <svg className="sx-cdiag" width={tw} height={h} viewBox={`0 0 ${tw} ${h}`} aria-hidden="true">
      {/* nut (open position) or the top fret line */}
      <line
        x1={x0} y1={top} x2={x0 + gridW} y2={top}
        strokeWidth={base === 1 ? 3.4 : 1.2} strokeLinecap="round"
      />
      {[1, 2, 3, 4].map((i) => (
        <line key={i} x1={x0} y1={top + i * FH} x2={x0 + gridW} y2={top + i * FH} strokeWidth={1.2} />
      ))}
      {shape.frets.map((_, i) => (
        <line key={i} x1={sx(i)} y1={top} x2={sx(i)} y2={top + 4 * FH} strokeWidth={1.2} />
      ))}

      {shape.barre && shape.barre.fret >= base && (
        <>
          <rect
            className="sx-cdiag-dot"
            x={sx(shape.barre.from) - SP * 0.3} y={fy(shape.barre.fret) - FH * 0.5 - SP * 0.3}
            width={(shape.barre.to - shape.barre.from) * SP + SP * 0.6} height={SP * 0.6}
            rx={SP * 0.3} stroke="none"
          />
          <text
            className="sx-cdiag-fg" x={sx(shape.barre.from)} y={fy(shape.barre.fret) - FH * 0.5 + SP * 0.15}
            fontSize={SP * 0.42} textAnchor="middle" stroke="none"
          >
            1
          </text>
        </>
      )}

      {shape.frets.map((f, i) => {
        if (f < 0) {
          return (
            <g key={i} className="sx-cdiag-mark">
              <line x1={sx(i) - SP * 0.17} y1={top - SP * 0.5} x2={sx(i) + SP * 0.17} y2={top - SP * 0.16} strokeWidth={1.5} />
              <line x1={sx(i) + SP * 0.17} y1={top - SP * 0.5} x2={sx(i) - SP * 0.17} y2={top - SP * 0.16} strokeWidth={1.5} />
            </g>
          );
        }
        if (f === 0) {
          return <circle key={i} className="sx-cdiag-mark" cx={sx(i)} cy={top - SP * 0.33} r={SP * 0.17} fill="none" strokeWidth={1.5} />;
        }
        // Fingers under a barre are already covered by the bar itself.
        const onBarre = shape.barre && shape.barre.fret === f && i >= shape.barre.from && i <= shape.barre.to;
        if (onBarre) return null;
        const fg = shape.fingers[i];
        return (
          <g key={i}>
            <circle className="sx-cdiag-dot" cx={sx(i)} cy={fy(f) - FH * 0.5} r={SP * 0.3} stroke="none" />
            {fg > 0 && (
              <text
                className="sx-cdiag-fg" x={sx(i)} y={fy(f) - FH * 0.5 + SP * 0.15}
                fontSize={SP * 0.42} textAnchor="middle" stroke="none"
              >
                {fg}
              </text>
            )}
          </g>
        );
      })}

      {/* Which string is which — the reference prints these, and without them
          a 4-string shape is ambiguous about where it sits. */}
      {STRINGS.map((n, i) => (
        <text
          key={`s${i}`} className="sx-cdiag-str" x={sx(i)} y={top + FH * 4 + SP * 0.5}
          fontSize={SP * 0.36} textAnchor="middle" stroke="none"
        >
          {n}
        </text>
      ))}

      {base > 1 && (
        <text className="sx-cdiag-fr" x={x0 + gridW + SP * 0.26} y={top + FH * 0.68} fontSize={SP * 0.44}>
          {base}fr
        </text>
      )}
    </svg>
  );
}

function LaneWave({ env, dim }: { env: Float32Array; dim: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const draw = () => {
      const r = c.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = window.devicePixelRatio || 1;
      const W = Math.max(1, Math.round(r.width * dpr * 2));
      const H = Math.max(1, Math.round(r.height * dpr));
      if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      const m = getComputedStyle(c).color.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/);
      const [cr, cg, cb] = m ? [m[1], m[2], m[3]] : ['14', '6', '34'];
      const fill = ctx.createLinearGradient(0, 0, 0, H);
      fill.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, 0.86)`);
      fill.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0.64)`);
      ctx.fillStyle = fill;
      const n = env.length >> 1;
      const mid = H / 2;
      const half = (H / 2) * 0.92;
      // More buckets than columns, so each column takes the extremes of the
      // buckets it covers — never sampling one and skipping its neighbour.
      for (let x = 0; x < W; x++) {
        const b0 = Math.floor((x * n) / W);
        const b1 = Math.max(b0 + 1, Math.floor(((x + 1) * n) / W));
        let mn = 0, mx = 0;
        for (let b = b0; b < b1 && b < n; b++) {
          if (env[b * 2] < mn) mn = env[b * 2];
          if (env[b * 2 + 1] > mx) mx = env[b * 2 + 1];
        }
        if (mx - mn <= 0) continue; // silence draws nothing — no floor
        ctx.fillRect(x, mid - mx * half, 1, (mx - mn) * half);
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(c);
    return () => ro.disconnect();
  }, [env, dim]);
  return <canvas ref={ref} className="sx-lane-canvas" aria-hidden />;
}

// Amplitude envelope of the actual click sample, bucketed once. Both variants
// are scaled by the ACCENT's peak, so a bar line stands taller than an off-beat
// by exactly the amount it is louder — the lane is the click track's waveform.
let CLICK_ENV: { a: Float32Array; n: Float32Array } | null = null;
function clickEnvelopes(): { a: Float32Array; n: Float32Array } {
  if (!CLICK_ENV) {
    const BUCKETS = 48;
    const peaks = (b64: string): Float32Array => {
      const bin = atob(b64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const i16 = new Int16Array(u8.buffer);
      const out = new Float32Array(BUCKETS);
      for (let b = 0; b < BUCKETS; b++) {
        const s0 = Math.floor((b * i16.length) / BUCKETS);
        const s1 = Math.max(s0 + 1, Math.floor(((b + 1) * i16.length) / BUCKETS));
        let m = 0;
        for (let i = s0; i < s1 && i < i16.length; i++) m = Math.max(m, Math.abs(i16[i]));
        out[b] = m;
      }
      return out;
    };
    const a = peaks(CLICK_ACCENT_B64), n = peaks(CLICK_NORMAL_B64);
    let top = 0;
    for (const v of a) if (v > top) top = v;
    if (top > 0) { for (let i = 0; i < a.length; i++) a[i] /= top; for (let i = 0; i < n.length; i++) n[i] /= top; }
    CLICK_ENV = { a, n };
  }
  return CLICK_ENV;
}

const LockGlyph = (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" />
    <path d="M8 10.5V7a4 4 0 018 0v3.5" />
  </svg>
);

// The click track drawn as a real lane. These marks are the truth, not
// decoration: they are the beats the metronome will actually hit — the song's
// tracked beats while the lane is locked, an even grid from the BPM chip when
// it isn't. Bar lines are full height, like the reference's click track.
function ClickLane({ beats, accents, clicks, duration, maxTime, dim }: { beats: number[]; accents: Set<number> | null; clicks: number[]; duration: number; maxTime: number; dim: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const draw = () => {
      const r = c.getBoundingClientRect();
      if (!r.width || !r.height || duration <= 0) return;
      const dpr = window.devicePixelRatio || 1;
      const W = Math.max(1, Math.round(r.width * dpr));
      const H = Math.max(1, Math.round(r.height * dpr));
      if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      const m = getComputedStyle(c).color.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/);
      const [cr, cg, cb] = m ? [m[1], m[2], m[3]] : ['14', '6', '34'];
      const mid = H / 2;
      const w = Math.max(1, Math.round(dpr * 0.75));
      // TWO WEIGHTS, the way the reference draws it: a faint ruler of beat
      // lines (bars heavier) UNDER the click track's own waveform.
      const span = beats.length > 1 ? beats[beats.length - 1] - beats[0] : 0;
      const gap = span > 0 ? (W / duration) * (span / (beats.length - 1)) : W;
      const drawBeats = gap >= 2 * dpr;
      // The ruler is deliberately FAINT. It is background: on a purple lane a
      // mid-strength grid line and the click mark land on the same pixel and
      // read as one tick, which is exactly what made this look untouched.
      for (let i = 0; i < beats.length; i++) {
        const t = beats[i];
        if (t < 0 || t > duration || t > maxTime) continue;
        const bar = accents ? accents.has(i) : false;
        if (!bar && !drawBeats) continue;
        ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${dim ? 0.1 : bar ? 0.3 : 0.13})`;
        const h = (bar ? 0.96 : 0.86) * H;
        ctx.fillRect(Math.round((t / duration) * W), mid - h / 2, 1, h);
      }
      // The clicks themselves, in the lane's own accent — this is the audio,
      // so its height comes from the sample's envelope, not from a constant.
      const env = clickEnvelopes();
      const CLICK_SEC = 0.08;
      const spanPx = Math.max(1.4 * dpr, (CLICK_SEC / duration) * W);
      // Their lane separates the click from the ruler by HUE (blue on green).
      // Purple has no such partner, so ours separates by VALUE: the clicks are
      // near-black and solid, the loudest thing in the lane.
      const barTimes = new Set<number>();
      if (accents) for (let i = 0; i < beats.length; i++) if (accents.has(i)) barTimes.add(Math.round(beats[i] * 1000));
      ctx.fillStyle = dim ? `rgba(${cr}, ${cg}, ${cb}, 0.34)` : `rgba(${cr}, ${cg}, ${cb}, 0.92)`;
      for (const t of clicks) {
        if (t < 0 || t > duration || t > maxTime) continue;
        const shape = barTimes.has(Math.round(t * 1000)) ? env.a : env.n;
        const x0 = (t / duration) * W;
        for (let px = 0; px < spanPx; px++) {
          const x = Math.round(x0 + px);
          if (x < 0 || x >= W) break;
          const v = shape[Math.min(shape.length - 1, Math.floor((px / spanPx) * shape.length))];
          const h = v * H * 0.62;
          if (h < 0.75) continue;
          ctx.fillRect(x, mid - h / 2, w, h);
        }
      }
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(c);
    return () => ro.disconnect();
  }, [beats, accents, clicks, duration, maxTime, dim]);
  return <canvas ref={ref} className="sx-click-canvas" aria-hidden />;
}

function ClampedTitle({ text, className }: { text: string; className?: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [shown, setShown] = useState(text);

  useEffect(() => {
    const el = ref.current;
    const row = el?.parentElement;
    if (!el || !row) return;

    const fit = () => {
      const cs = getComputedStyle(el);
      // On phones the title is given its own wrapping row, so there is nothing
      // to shorten — show the whole name.
      if (cs.whiteSpace !== 'nowrap') { setShown(text); return; }

      // Every sibling but the spacer is `flex-shrink: 0`, so their widths are
      // fixed: whatever the row has left over is the title's budget. The spacer
      // is skipped precisely because it's the one that flexes.
      //
      // All of it in painted pixels, never CSS pixels: the shell runs `zoom:
      // 0.9`, so getBoundingClientRect() and clientWidth report different
      // scales, and mixing the two overstates the budget by ~11% — enough that
      // the CSS ellipsis then re-cuts the trimmed name mid-word anyway.
      const rowCs = getComputedStyle(row);
      const rowW = row.getBoundingClientRect().width;
      const scale = row.offsetWidth ? rowW / row.offsetWidth : 1;
      const gap = (parseFloat(rowCs.columnGap) || 0) * scale;
      const padX = (parseFloat(rowCs.paddingLeft) + parseFloat(rowCs.paddingRight)) * scale;
      const kids = Array.from(row.children) as HTMLElement[];
      let taken = padX + gap * Math.max(0, kids.length - 1);
      for (const kid of kids) {
        if (kid === el || kid.classList.contains('sx-spacer')) continue;
        taken += kid.getBoundingClientRect().width;
      }
      const avail = rowW - taken;

      // Measure in a detached span carrying this element's own font metrics —
      // exact, and without the browser-support caveats of canvas letterSpacing
      // (the title runs a negative tracking, which would skew a canvas guess).
      const probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;left:-9999px;top:0;white-space:pre;visibility:hidden';
      probe.style.fontFamily = cs.fontFamily;
      probe.style.fontSize = cs.fontSize;
      probe.style.fontWeight = cs.fontWeight;
      probe.style.fontStyle = cs.fontStyle;
      probe.style.letterSpacing = cs.letterSpacing;
      // Inside the row, not on <body>: the probe has to inherit the same zoom
      // its measurements are compared against. `position: absolute` keeps it
      // out of flow, so it can't disturb the flex row it's borrowing.
      row.appendChild(probe);
      const widthOf = (t: string) => { probe.textContent = t; return probe.getBoundingClientRect().width; };

      try {
        if (avail <= 0 || widthOf(text) <= avail) { setShown(text); return; }
        let out = '';
        for (const w of text.split(/\s+/)) {
          const next = out ? `${out} ${w}` : w;
          if (widthOf(`${next}…`) > avail) break;
          out = next;
        }
        // A first word wider than the whole row can't be cut on a space; the
        // CSS ellipsis stays as the last resort for exactly that case.
        setShown(out ? `${out}…` : text);
      } finally {
        probe.remove();
      }
    };

    fit();
    // Webfonts land after first paint and change every metric under us.
    void document.fonts?.ready.then(fit).catch(() => {});
    const ro = new ResizeObserver(fit);
    ro.observe(row);
    return () => ro.disconnect();
  }, [text]);

  // `title` keeps the full name reachable on hover, whatever got trimmed.
  return <h1 ref={ref} className={className} title={text}>{shown}</h1>;
}

function Ico({ d, size = 18, sw = 1.9 }: { d: string; size?: number; sw?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

// ── metronome ─────────────────────────────────────────────────────────────
// A real click, not a drawn one: lookahead-scheduled Web Audio blips on their
// own context, so the stem graph's mute/solo state can never silence it. Beat
// one is accented. Kept deliberately small — the extension owns the elaborate
// version (tap tempo, auto-detect); this is the part the design shows.
class Metronome {
  private ctx: AudioContext | null = null;
  private timer: number | null = null;
  private nextTime = 0;
  private beat = 0;
  bpm = 120;
  beatsPerBar = 4;
  /** 0..1 fader position for the click. At 0 nothing is scheduled at all. */
  volume = 0.7;
  // Song-locked mode (beta): when tracked beats and a song clock are
  // provided, clicks land on the SONG's actual beats instead of free-running
  // from the moment play was pressed. All of these stay null outside the
  // beta, which keeps this class on its old behaviour for everyone else.
  grid: { beats: number[]; downbeatIdx: number; accentSet?: Set<number> | null } | null = null;
  songPos: (() => number) | null = null;
  speed = 1;
  // Output bus for locked clicks: the ENGINE's own context, so click and
  // stems share one output-latency path. pipeDelay() adds what is currently
  // buffered inside the engine's pitch pipe — the element clock runs that
  // far AHEAD of what the listener hears.
  bus: AudioContext | null = null;
  pipeDelay: (() => number) | null = null;
  /** Song time the click must not sound past (the free ceiling), or null. */
  limitSong: number | null = null;
  /** Dips the song under a click (locked mode only — same clock as the bus). */
  duck: ((at: number) => void) | null = null;
  /** Engine limiter bus. Clicks join the song there so their SUM is bounded. */
  out: AudioNode | null = null;
  /** 0.5 = every other beat, 1 = every beat, 2 = eighths. */
  subdiv = 1;
  private lastBeatIdx = Number.NEGATIVE_INFINITY;

  private ensure(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext();
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  // The click is the LITERAL SAMPLE from the file the owner picked
  // (metronome-C-woodblock.wav): isolated offline, embedded as int16 PCM in
  // clickSample.ts, verified by rebuilding that file byte-for-byte from
  // song + clicks. There is NO click synthesiser any more — any change to
  // the click sound must go through regenerating clickSample.ts.
  /** Loudest the click may peak. Full scale is safe now that it lands on the
      engine's limiter bus rather than raw destination — the bus soft-clips the
      SUM of click and song, so this can be as loud as the format allows. */
  private static readonly CLICK_CEILING = 1.0;
  private clickPcm: { a: Float32Array; n: Float32Array } | null = null;
  /** Resampled + normalised source, per output rate. */
  private pcmByRate = new Map<number, { a: Float32Array; n: Float32Array; norm: number }>();
  private clickBufs = new WeakMap<BaseAudioContext, Map<string, AudioBuffer>>();

  private base(): { a: Float32Array; n: Float32Array } {
    if (!this.clickPcm) {
      const dec = (b64: string): Float32Array => {
        const bin = atob(b64);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        const i16 = new Int16Array(u8.buffer);
        const f = new Float32Array(i16.length);
        for (let i = 0; i < i16.length; i++) f[i] = i16[i] / 32768;
        return f;
      };
      this.clickPcm = { a: dec(CLICK_ACCENT_B64), n: dec(CLICK_NORMAL_B64) };
    }
    return this.clickPcm;
  }

  /** Lanczos-3 resample. The asset is a 44.1 kHz render; on a device running
      any other rate the browser would otherwise resample it live, with an
      algorithm that differs between browsers — which is exactly how the same
      build ends up sounding different on two machines. Doing it here once
      makes the click identical everywhere. Rate match = untouched samples. */
  private resample(src: Float32Array, from: number, to: number): Float32Array {
    if (from === to) return src;
    const ratio = to / from;
    const n = Math.max(1, Math.round(src.length * ratio));
    const out = new Float32Array(n);
    const A = 3;
    const sinc = (x: number) => (Math.abs(x) < 1e-9 ? 1 : Math.sin(Math.PI * x) / (Math.PI * x));
    for (let i = 0; i < n; i++) {
      const centre = i / ratio;
      const i0 = Math.floor(centre);
      let acc = 0;
      for (let k = i0 - A + 1; k <= i0 + A; k++) {
        if (k < 0 || k >= src.length) continue;
        const x = centre - k;
        if (Math.abs(x) >= A) continue;
        acc += src[k] * sinc(x) * sinc(x / A);
      }
      out[i] = acc;
    }
    return out;
  }

  private pcmFor(rate: number): { a: Float32Array; n: Float32Array; norm: number } {
    let got = this.pcmByRate.get(rate);
    if (!got) {
      const b = this.base();
      const a = this.resample(b.a, CLICK_SR, rate);
      const n = this.resample(b.n, CLICK_SR, rate);
      // ONE factor for both, taken from the accent — scaling them apart would
      // change the accent-to-beat relationship the render was approved with.
      let peak = 0;
      for (let i = 0; i < a.length; i++) { const v = Math.abs(a[i]); if (v > peak) peak = v; }
      // The click still bypasses masterGain (mute/solo must never silence it),
      // but it now sums into the engine's limiter bus, so a full-scale click
      // can no longer crackle against the music.
      got = { a, n, norm: peak > 0 ? Metronome.CLICK_CEILING / peak : 1 };
      this.pcmByRate.set(rate, got);
    }
    return got;
  }

  /** The level is baked into the sample — no node downstream to delay it —
      and the fader is now PURELY a scalar: the click keeps the approved
      render's exact shape at every position, right up to full scale, and can
      never distort. Loudest = the sample at 0 dBFS, nothing beyond it. */
  private clickBuf(c: BaseAudioContext, accent: boolean): AudioBuffer {
    let byLevel = this.clickBufs.get(c);
    if (!byLevel) { byLevel = new Map(); this.clickBufs.set(c, byLevel); }
    // Bucketed so dragging the fader reuses buffers instead of rebuilding one
    // per pixel; 2% steps are well under an audible level change.
    const step = Math.round(this.volume * 50) / 50;
    const key = `${accent ? 'a' : 'n'}:${step}`;
    let buf = byLevel.get(key);
    if (!buf) {
      const set = this.pcmFor(c.sampleRate);
      const src = accent ? set.a : set.n;
      const g = step * set.norm;
      // Buffer is built AT the context's rate, so nothing resamples it later.
      buf = c.createBuffer(1, src.length, c.sampleRate);
      const ch = buf.getChannelData(0);
      for (let i = 0; i < src.length; i++) ch[i] = src[i] * g;
      byLevel.set(key, buf);
    }
    return buf;
  }

  // Headroom. The sample peaks at 0.84, so a plain 0..1 fader topped out
  // barely above the song and read as "too quiet". The fader now drives up to
  // MAX_GAIN — the picked render's own click level sits around 55% — and the
  // click passes through a soft-clip curve so the extra push saturates
  // (a harder hit) instead of clipping into digital crackle.

  private click(at: number, accent: boolean, ctx?: AudioContext) {
    if (this.volume < 0.001) return; // fully down — schedule nothing
    const c = ctx ?? this.ensure();
    const src = c.createBufferSource();
    src.buffer = this.clickBuf(c, accent);
    // Straight to the output: every node in this path is latency the click
    // does not have. Level lives in the buffer.
    // Into the limiter when we're on the engine's context; otherwise this is a
    // private context with no bus of its own and destination is all there is.
    src.connect((ctx && this.out) ? this.out : c.destination);
    src.start(at);
    // Locked clicks ride on the engine's own context (ctx set), where the
    // song can be dipped under the hit on the same clock. Free-run clicks
    // live on a private context whose clock the engine can't read — no duck.
    if (ctx) this.duck?.(at);
  }

  /** Free-running clicks from "now" — the original behaviour. */
  private freeTick(ctx: AudioContext) {
    // A mode switch (or a long suspension) can leave nextTime in the past;
    // without this guard the catch-up loop machine-guns clicks at "now".
    if (this.nextTime < ctx.currentTime) this.nextTime = ctx.currentTime + 0.06;
    const ahead = ctx.currentTime + 0.2;
    while (this.nextTime < ahead) {
      this.click(this.nextTime, this.beat % this.beatsPerBar === 0);
      this.nextTime += 60 / Math.max(20, this.bpm);
      this.beat += 1;
    }
  }

  /** Song-locked clicks: schedule every TRACKED beat inside the lookahead,
      mapping song time onto the output clock through the play rate, delayed
      by whatever the engine's pitch pipe is currently holding — so the click
      is heard when the beat is heard, not when it is decoded. */
  private gridTick(fallbackCtx: AudioContext) {
    const g = this.grid;
    const posFn = this.songPos;
    if (!g || !posFn || !g.beats.length) return;
    const ctx = this.bus ?? fallbackCtx;
    const now = posFn();
    const rate = Math.max(0.25, this.speed);
    const delay = this.pipeDelay ? this.pipeDelay() : 0;
    // First beat at/after "now" via binary search on the tracked list.
    let kNow = 0;
    { let lo = 0, hi = g.beats.length; while (lo < hi) { const mid = (lo + hi) >> 1; if (g.beats[mid] < now) lo = mid + 1; else hi = mid; } kNow = lo; }
    // Seek back / loop wrap: fall back to the beat under the new position.
    if (kNow < this.lastBeatIdx - 1) this.lastBeatIdx = kNow - 1;
    const aheadSong = now + 0.22 * rate;
    for (let k = Math.max(kNow, this.lastBeatIdx + 1, 0); k < g.beats.length; k++) {
      const tSong = g.beats[k];
      if (tSong > aheadSong) break;
      if (this.limitSong != null && tSong > this.limitSong) break;
      const at = ctx.currentTime + Math.max(0.005, (tSong - now) / rate + delay);
      const rel = (((k - g.downbeatIdx) % this.beatsPerBar) + this.beatsPerBar) % this.beatsPerBar;
      // The tracker marks real bar "one"s per beat; counting is the fallback.
      const accent = g.accentSet ? g.accentSet.has(k) : rel === 0;
      // Half time drops every other beat, keeping the one it starts from
      // anchored to the downbeat so the accent never drifts off bar one.
      const half = this.subdiv === 0.5 && ((((k - g.downbeatIdx) % 2) + 2) % 2) !== 0;
      if (!half) this.click(at, accent, ctx);
      // Double time adds the off-beat between this beat and the next.
      if (this.subdiv === 2 && k + 1 < g.beats.length) {
        const tMid = (tSong + g.beats[k + 1]) / 2;
        if (this.limitSong == null || tMid <= this.limitSong) {
          this.click(ctx.currentTime + Math.max(0.005, (tMid - now) / rate + delay), false, ctx);
        }
      }
      this.lastBeatIdx = k;
    }
  }

  /** Start clicking. Returns immediately; `startBeat` offsets the accent. */
  start(startBeat = 0) {
    this.stop();
    const ctx = this.ensure();
    this.beat = startBeat;
    this.nextTime = ctx.currentTime + 0.06;
    this.lastBeatIdx = Number.NEGATIVE_INFINITY;
    // Mode is decided per tick, so flipping the grid on/off mid-play (BPM
    // override, analysis landing late) takes effect without a restart.
    const tick = () => (this.grid && this.songPos ? this.gridTick(ctx) : this.freeTick(ctx));
    tick();
    this.timer = window.setInterval(tick, 60);
  }

  stop() {
    if (this.timer != null) window.clearInterval(this.timer);
    this.timer = null;
  }

  dispose() {
    this.stop();
    if (this.ctx) void this.ctx.close().catch(() => {});
    this.ctx = null;
  }
}

type Screen = 'setup' | 'mixer' | 'library';

// Longest window a real-time capture will record. Not a plan gate — it's the
// cost of recording in real time, and it applies to everyone.
const MAX_CAPTURE_SEC = 300;

/**
 * Owns the embedded player for a link source.
 *
 * YouTube gets a real IFrame Player (so the capture can be keyed to the content
 * clock and skip ads); SoundCloud gets a Widget adapter exposing the same
 * interface. Both are torn down when the panel closes, and the poll that drives
 * the playhead only runs while a player exists.
 */
function useLinkPlayer(
  meta: LinkMeta | null,
  open: boolean,
  onError: (m: string) => void,
  regionRef: React.MutableRefObject<{ start: number; end: number }>,
  previewRef: React.MutableRefObject<boolean>,
  busyRef: React.MutableRefObject<boolean>,
) {
  const ytHostRef = useRef<HTMLDivElement | null>(null);
  const scFrameRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<YtPlayer | null>(null);
  const [ready, setReady] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  // SoundCloud's widget ignores programmatic play until the user has clicked
  // the widget itself once — so we watch for real playback before allowing a run.
  const [scUnlocked, setScUnlocked] = useState(false);

  const errRef = useRef(onError);
  errRef.current = onError;

  useEffect(() => {
    if (!open || !meta) return;
    let dead = false;
    setReady(false);
    setDuration(0);
    setPlaying(false);
    setPos(0);
    setScUnlocked(false);

    (async () => {
      try {
        if (meta.kind === 'yt') {
          const YT = await loadYouTubeAPI();
          if (dead || !ytHostRef.current) return;
          const p = new YT.Player(ytHostRef.current, {
            videoId: meta.ref,
            width: '100%',
            height: '100%',
            playerVars: { rel: 0, playsinline: 1, origin: window.location.origin },
            events: {
              onReady: (e) => {
                if (dead) return;
                playerRef.current = e.target;
                setDuration(e.target.getDuration() || 0);
                setReady(true);
              },
              onError: (e) => { if (!dead) errRef.current(embedErrorMessage(e.data)); },
              onStateChange: (e) => {
                if (dead) return;
                setPlaying(e.data === YT_STATE.PLAYING);
                if (e.data === YT_STATE.PLAYING) setScUnlocked(true);
              },
            },
          });
          playerRef.current = p;
        } else {
          if (!scFrameRef.current) return;
          const p = await createScPlayer(scFrameRef.current, meta.url);
          if (dead) { try { p.destroy(); } catch { /* ignore */ } return; }
          playerRef.current = p;
          setDuration(p.getDuration() || 0);
          setReady(true);
        }
      } catch {
        if (!dead) errRef.current(meta.kind === 'sc' ? scEmbedErrorMessage() : 'That video could not be loaded.');
      }
    })();

    return () => {
      dead = true;
      const p = playerRef.current;
      playerRef.current = null;
      try { p?.destroy(); } catch { /* already gone */ }
    };
  }, [meta, open]);

  // Playhead + late duration (SoundCloud reports it a beat after load) + the
  // SoundCloud unlock signal, all from one cheap poll.
  useEffect(() => {
    if (!open || !ready) return;
    const id = window.setInterval(() => {
      const p = playerRef.current;
      if (!p) return;
      try {
        const t = p.getCurrentTime();
        const st = p.getPlayerState();
        const isPlaying = st === YT_STATE.PLAYING;
        setPlaying(isPlaying);
        // SoundCloud only counts as unlocked once it has really played.
        if (isPlaying && t > 0.05) setScUnlocked(true);
        const d = p.getDuration();
        if (d && Math.abs(d - duration) > 0.5) setDuration(d);
        // While recording, the capture engine owns the player — don't fight it.
        if (busyRef.current) return;
        setPos(t);
        if (previewRef.current) {
          const r = regionRef.current;
          if (t >= r.end - 0.05 || t < r.start - 0.5) {
            try { p.seekTo(r.start, true); } catch { /* ignore */ }
          }
        }
      } catch { /* player mid-teardown */ }
    }, 100);
    return () => window.clearInterval(id);
  }, [open, ready, duration]);

  return { ytHostRef, scFrameRef, playerRef, ready, duration, playing, pos, scUnlocked };
}

export function StudioNext({ initialScreen }: { initialScreen?: Screen } = {}) {
  const { user, logout } = useAuth();
  const engineRef = useRef<StemEngine | null>(null);
  if (!engineRef.current) engineRef.current = new StemEngine();
  const engine = engineRef.current;
  const metroRef = useRef<Metronome | null>(null);
  if (!metroRef.current) metroRef.current = new Metronome();
  const metro = metroRef.current;

  // ── shell ───────────────────────────────────────────────────────────────
  const [screen, setScreen] = useState<Screen>(initialScreen ?? 'setup');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'),
  );

  // ── source / decode ─────────────────────────────────────────────────────
  const [tab, setTab] = useState<'upload' | 'url' | 'soundcloud'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const decodedRef = useRef<{ left: Float32Array; right: Float32Array; sampleRate: number; durationSec: number } | null>(null);
  const [srcPeaks, setSrcPeaks] = useState<number[]>([]);
  const [duration, setDuration] = useState(0);
  const [keyBpm, setKeyBpm] = useState<KeyBpmResult | null>(null);
  // Mirror for async readers: the cache write happens deep in save flows whose
  // closures would otherwise hold a stale null from before detection resolved.
  const keyBpmRef = useRef<KeyBpmResult | null>(null);
  const applyKeyBpm = (kb: KeyBpmResult | null) => { keyBpmRef.current = kb; setKeyBpm(kb); };
  // Which separation the mixer currently shows. A reopen-triggered re-analysis
  // resolves seconds later, by which time the user may be on another track —
  // this is what stops that result landing on the wrong song's header.
  const activeSepKeyRef = useRef<string | null>(null);
  /** Reopened sections cached before keyBpm was stored: re-detect from the
      cached original audio, then persist onto the record so it's one-time. */
  const redetectKeyBpm = useCallback(async (key: string, source?: Blob, sourceRef?: string) => {
    try {
      const blob = source ?? (sourceRef ? await getSourceBlob(sourceRef) : null);
      if (!blob) return; // link-sourced records may have no original — chip stays hidden, as before
      const dec = await decodeFileToStereo(new File([blob], 'source'));
      const kb = await detectKeyBpm(dec.left, dec.right, dec.sampleRate);
      if (!kb || activeSepKeyRef.current !== key) return; // null = clip too short to call
      applyKeyBpm(kb);
      void setSeparationKeyBpm(key, kb);
    } catch { /* analysis is a nicety — never surface a failure */ }
  }, []);
  const [analysing, setAnalysing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // ── chords (beta, gated by account) ─────────────────────────────────────
  // Analysis runs once per separation over the exact window the mixer plays;
  // everything below — run, render, restore — checks the gate, so accounts
  // outside the beta see a byte-identical Studio and never fetch the model.
  const chordsEnabled = chordsBetaFor(user?.email);
  const [chords, setChords] = useState<ChordSegment[] | null>(null);
  const [chordsBusy, setChordsBusy] = useState(false);
  // Mirrors for the imperative painter (rAF) — same stale-closure reasoning
  // as keyBpmRef above.
  const chordViewRef = useRef<{ segs: ChordSegment[]; labels: (string | null)[] } | null>(null);
  const chordLaneRef = useRef<HTMLDivElement | null>(null);
  const chordIdxRef = useRef(-1);
  const chordsRef = useRef<ChordSegment[] | null>(null);
  const applyChords = (c: ChordSegment[] | null) => { chordsRef.current = c; setChords(c); chordIdxRef.current = -1; };
  /** Chords currently held for `key`, or null if the user moved on. */
  function chordsRefFor(key: string): ChordSegment[] | null {
    return activeSepKeyRef.current === key ? chordsRef.current : null;
  }
  // Chord stage: the diagram view that replaces the lanes. Open state lives in
  // a ref too — the painter runs off rAF and must not pay for a closure.
  const [chordStage, setChordStage] = useState(false);
  // Voicing difficulty. Easy = open/partial shapes (the reference's default
  // and what a learner can fret); Barre = the movable full-barre forms.
  const [shapeStyle, setShapeStyle] = useState<ShapeStyle>('easy');
  const chordStageRef = useRef(false);
  chordStageRef.current = chordStage;
  // Current chord index as REACT state — the ribbon highlight is a class swap
  // (imperative, cheap) but the stage swaps whole diagrams, so it needs a
  // render. Only written while the stage is open, so the lanes view keeps
  // exactly today's ~0 re-renders during playback.
  const [curChordIdx, setCurChordIdx] = useState(-1);
  const chordProgRef = useRef<HTMLElement | null>(null);
  const [beatGrid, setBeatGrid] = useState<BeatGrid | null>(null);
  // Ref mirror: the tempo-seeding effect must not clobber a grid-derived BPM
  // when a later keyBpm update (the chord-key upgrade) re-fires it.
  const beatGridRef = useRef<BeatGrid | null>(null);
  // Applying a grid also seeds the tempo chip — every path (fresh analysis,
  // stored restore, backfill) must agree with the lock condition below.
  const applyBeatGrid = (g: BeatGrid | null) => {
    beatGridRef.current = g;
    setBeatGrid(g);
    if (g) setBpm(Math.round(g.bpm));
  };
  /** The chord timeline is a denoised harmonic analysis, so it beats the
      chromagram key detector whenever it exists — re-derive the key from it
      and persist, overriding keyBpm's key fields (BPM stays untouched). Only
      when a keyBpm already exists: the header renders key and tempo as one
      unit, and inventing a 0-BPM result to carry a key would read as data. */
  function upgradeKeyFromChords(key: string, segs: ChordSegment[]): void {
    const base = keyBpmRef.current;
    const est = keyFromChords(segs);
    if (!base || !est) return;
    const merged: KeyBpmResult = {
      ...base,
      keyName: est.keyName,
      keyShort: est.keyShort,
      tonic: est.tonic,
      mode: est.mode,
      altKeyShort: null,
      keyConfidence: est.confidence,
    };
    applyKeyBpm(merged);
    void setSeparationKeyBpm(key, merged);
  }
  async function analyzeChords(key: string, left: Float32Array, right: Float32Array, sampleRate: number) {
    if (!chordsEnabled) return;
    applyChords(null);
    applyBeatGrid(null);
    setChordsBusy(true);
    let segs: ChordSegment[] | null = null;
    try {
      segs = await detectChords(left, right, sampleRate);
      if (activeSepKeyRef.current !== key) return; // user moved on — drop it
      applyChords(segs);
      if (segs && segs.length) {
        // Await so the record exists before the key write below piggybacks.
        await setSeparationChords(key, segs);
        if (activeSepKeyRef.current !== key) return;
        upgradeKeyFromChords(key, segs);
      }
    } catch { /* analysis is a nicety — never surface a failure */ }
    finally { if (activeSepKeyRef.current === key) setChordsBusy(false); }
    // Beats run after chords so the two heavyweight model sessions never
    // coexist in memory. The tracker is the neural one — the DSP grid failed
    // a different way on every real song and is retired.
    void analyzeBeats(key, async () => ({ left, right, sampleRate }));
  }
  /** Rebuild a stereo mix by summing the cached stems — the restore-path
      input for beat tracking (the record's `source` is the FULL song, while
      the stems are exactly the separated window, so stems are the only
      correctly-aligned audio a reopened session has). */
  async function stemsToStereo(
    urls: Partial<Record<StemName, string>>,
  ): Promise<{ left: Float32Array; right: Float32Array; sampleRate: number } | null> {
    let left: Float32Array | null = null;
    let right: Float32Array | null = null;
    let sr = 44100;
    const ac = new AudioContext();
    try {
      for (const stem of STEMS) {
        const u = urls[stem];
        if (!u) continue;
        const ab = await fetch(u).then((r) => r.arrayBuffer());
        const buf = await ac.decodeAudioData(ab);
        sr = buf.sampleRate;
        const c0 = buf.getChannelData(0);
        const c1 = buf.numberOfChannels > 1 ? buf.getChannelData(1) : c0;
        if (!left || !right || left.length < c0.length) {
          const nl = new Float32Array(c0.length);
          const nr = new Float32Array(c0.length);
          if (left) nl.set(left);
          if (right) nr.set(right);
          left = nl;
          right = nr;
        }
        for (let i = 0; i < c0.length; i++) {
          left[i] += c0[i];
          right[i] += c1[i];
        }
      }
    } finally {
      void ac.close().catch(() => {});
    }
    return left && right ? { left, right, sampleRate: sr } : null;
  }
  /** Neural beat tracking over the mixer window. `audio` is lazy so the fresh
      path reuses buffers already in memory while restores decode stems only
      when a recompute is actually needed. */
  async function analyzeBeats(
    key: string,
    audio: () => Promise<{ left: Float32Array; right: Float32Array; sampleRate: number } | null>,
  ) {
    if (!chordsEnabled) return;
    try {
      const a = await audio();
      if (!a) return;
      const nb = await detectBeatsNeural(a.left, a.right, a.sampleRate);
      if (!nb || activeSepKeyRef.current !== key) return;
      const chordChanges = (chordsRefFor(key) ?? []).filter((c) => c.label !== 'N').map((c) => c.start);
      const grid = gridFromNeuralBeats(nb.beats, nb.downbeats, beatsPerBar, chordChanges);
      if (!grid) return;
      applyBeatGrid(grid); // also seeds the tempo chip
      void setSeparationBeatGrid(key, grid);
    } catch { /* analysis is a nicety — never surface a failure */ }
  }

  /**
   * Analysis backfill for a REOPENED track: chords and/or the beat grid when
   * the cached record has neither. Two kinds of record land here — /splitter
   * handoffs (cached without chords) and anything older than the chords
   * feature.
   *
   * The restore path used to say a chord re-detect "couldn't be aligned". That
   * was true of the cached ORIGINAL — it is the whole song, while the record's
   * timeline covers only the separated window, and the offset between them was
   * never stored. Summing the cached STEMS has no such problem: they ARE the
   * window. It is the same input the beat grid has always backfilled from, so
   * this only extends a proven path — and one decode now feeds both analyses
   * instead of two.
   */
  async function backfillAnalysis(
    key: string,
    audio: () => Promise<{ left: Float32Array; right: Float32Array; sampleRate: number } | null>,
    needChords: boolean,
    needGrid: boolean,
  ) {
    if (!chordsEnabled || (!needChords && !needGrid)) return;
    try {
      const a = await audio();
      if (!a || activeSepKeyRef.current !== key) return;
      if (needChords) {
        setChordsBusy(true);
        try {
          const segs = await detectChords(a.left, a.right, a.sampleRate);
          if (activeSepKeyRef.current !== key) return;
          applyChords(segs);
          if (segs && segs.length) {
            await setSeparationChords(key, segs);
            if (activeSepKeyRef.current !== key) return;
            upgradeKeyFromChords(key, segs);
          }
        } finally { if (activeSepKeyRef.current === key) setChordsBusy(false); }
      }
      // Beats after chords, so the two heavyweight model sessions never
      // coexist in memory — the same order analyzeChords uses.
      if (needGrid) await analyzeBeats(key, async () => a);
    } catch { /* analysis is a nicety — never surface a failure */ }
  }

  // ── link sources (YouTube / SoundCloud) ─────────────────────────────────
  // A link source is confirmed first (oEmbed for YouTube, URL shape for
  // SoundCloud), then the capture panel embeds the real player and records the
  // chosen A→B window off the tab. Nothing is ever fetched server-side.
  const [linkInput, setLinkInput] = useState('');
  const [linkState, setLinkState] = useState<'idle' | 'validating' | 'ok' | 'bad' | 'unknown'>('idle');
  const [linkMeta, setLinkMeta] = useState<LinkMeta | null>(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [phase, setPhase] = useState<CapturePhase | null>(null);
  // Where Chrome's Allow button lands, in PAGE coordinates. Hardcoding it never
  // held up: Chrome anchors that prompt to the browser WINDOW, so the page's
  // own origin shifts by however tall the chrome is — the bookmarks bar alone
  // moves it ~34px, and tab-strip/toolbar heights differ per OS and per zoom.
  // `outerHeight - innerHeight` is exactly that offset, so measuring it turns a
  // fixed guess into something that tracks the browser it is actually running in.
  const [allowY, setAllowY] = useState<number | null>(null);
  const [capturedSec, setCapturedSec] = useState(0);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [previewingLink, setPreviewingLink] = useState(false);
  const [editA, setEditA] = useState<string | null>(null);
  const [editB, setEditB] = useState<string | null>(null);
  const captureRef = useRef<CaptureHandle | null>(null);
  // Audition bookkeeping: last seek time (ms) and target (s) for the throttle,
  // and the drag-settle timer that flips scrubbing into an A→B loop.
  const lastSeekRef = useRef(0);
  const lastAuditRef = useRef(-1);
  const scrubStopRef = useRef(0);
  const previewRef = useRef(false);
  // Upload picker: the target the drag last asked for (the throttle below can
  // drop it) and the timer that applies it once the drag settles.
  const upSettleRef = useRef(0);
  const upPendingRef = useRef(-1);
  // Set when a pasted link should open the picker as soon as it validates.
  const autoOpenRef = useRef(false);

  /**
   * The link a PHONE pastes to hand to its own computer. Deliberately separate
   * from `linkInput`: that one drives an oEmbed lookup and, once it resolves,
   * opens the capture panel — a flow this device cannot complete. Sharing it
   * needs no lookup at all, only a URL we can recognise, so it stays its own
   * small piece of state and can never start a capture by accident.
   */
  const [sendLinkInput, setSendLinkInput] = useState('');
  const sendLinkOk = useMemo(() => {
    const v = sendLinkInput.trim();
    if (!v) return false;
    return tab === 'soundcloud' ? !!parseSoundCloudUrl(v) : !!parseYtVideoId(v);
  }, [sendLinkInput, tab]);

  // Locate Chrome's Allow button while its prompt is up. The prompt hangs off
  // the toolbar, so Allow sits a near-fixed distance below the WINDOW's top —
  // ALLOW_FROM_WINDOW_TOP — and converting that to page coordinates is just a
  // matter of subtracting the chrome above the viewport. Re-measured on resize
  // because that is when the browser can grow or shed toolbars.
  useEffect(() => {
    if (phase !== 'permission') { setAllowY(null); return; }
    const ALLOW_FROM_WINDOW_TOP = 506;
    // Calibrated against a real screenshot of the prompt in a Chrome window
    // with the bookmarks bar showing.
    const FALLBACK_Y = 384;
    const measure = () => {
      const outer = window.outerHeight;
      const inner = window.innerHeight;
      // outerHeight reads 0 inside some embedded webviews, and a chrome height
      // outside this band means we are not looking at ordinary browser
      // furniture. Either way the measurement is not trustworthy, so take the
      // calibrated default rather than position off a bogus number.
      const chromeH = outer - inner;
      const usable = outer > 0 && chromeH >= 20 && chromeH <= 400;
      const raw = usable ? ALLOW_FROM_WINDOW_TOP - chromeH : FALLBACK_Y;
      // Clamped so it can still only park the card somewhere sane on screen.
      setAllowY(Math.min(Math.max(raw, 120), Math.max(160, inner - 210)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [phase]);

  // ── goal state ──────────────────────────────────────────────────────────
  const [goal, setGoal] = useState<GoalKey>('loop');
  const [collapsed, setCollapsed] = useState(false);
  const [guide, setGuide] = useState(false);
  /* ⚠️ Must agree with the chips the backing-track picker RENDERS. A phone is
     offered only Vocals (the 2-stem engine returns nothing else), so a
     'drums' default would leave no chip lit and still mute drums on the run —
     i.e. spend a separation and return silence. Desktop keeps 'drums'. */
  const [mutes, setMutes] = useState<StemName[]>(isMobileTier() ? ['vocals'] : ['drums']);
  const [region, setRegion] = useState({ start: 0, end: 30 });
  const [sepEngine, setSepEngine] = useState<SepEngine>('demucs');
  // "Best" runs on a much larger engine than "Standard", so its first use stalls
  // for minutes with nothing on screen explaining why. Warn — but only while it
  // genuinely is not stored yet. Starts true so the note cannot flash before the
  // async check answers.
  const [bestEngineReady, setBestEngineReady] = useState(true);

  // ── run / result ────────────────────────────────────────────────────────
  const [mode, setMode] = useState<'input' | 'working' | 'playing'>('input');
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [mix, setMix] = useState<MixState>(DEFAULT_MIX);
  const [pans, setPans] = useState<Record<StemName, number>>(
    () => Object.fromEntries(STEMS.map((s) => [s, 0])) as Record<StemName, number>,
  );
  const [lanePeaks, setLanePeaks] = useState<Partial<Record<StemName, Float32Array>>>({});
  // The rack renders greyscale and breathing until the real per-stem waveforms
  // have decoded — a true loading state, not a decorative delay.
  const [hydrating, setHydrating] = useState(false);
  const hydrateStartRef = useRef(0);
  const [pos, setPos] = useState(0);
  // Rack scrubber: the ruler is the ONE element we measure against, and the
  // playhead overlay reuses its column, so click, drag and paint all share a
  // single px→time mapping.
  const rulerRef = useRef<HTMLDivElement>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [masterVol, setMasterVolState] = useState(1);
  const lastMasterVol = useRef(1);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── tempo / metronome ───────────────────────────────────────────────────
  const [bpm, setBpm] = useState(120);
  const [beatsPerBar, setBeatsPerBar] = useState(4);
  const [metroOn, setMetroOn] = useState(false);
  // Click level. Persisted on its own — it's a per-person preference, not a
  // property of any one song's mix.
  const [metroVol, setMetroVol] = useState<number>(() => {
    // Guard the ABSENT case explicitly: Number(null) is 0, which passes a
    // plain range check and would ship a silent metronome by default.
    const raw = localStorage.getItem('rl_metro_vol_v1');
    const v = raw == null ? NaN : Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0.7;
  });
  // Solo the click, exactly like a stem's S: everything else drops out and the
  // mix you built is untouched underneath, so releasing it restores the balance.
  const [metroSolo, setMetroSolo] = useState(false);
  // The click lane's lock. Engaged, the metronome plays the song's own tracked
  // beats; released, it free-runs at the BPM chip.
  const [gridLock, setGridLock] = useState(true);
  // True once playback has carried the click past the free minute. The click
  // goes quiet, but the channel stays ON: seek back inside the first minute —
  // or loop there — and it simply resumes.
  // 0.5× / 1× / 2× — how often the click lands against the tracked beat.
  const [clickSubdiv, setClickSubdiv] = useState(1);
  const [metroPastFree, setMetroPastFree] = useState(false);
  const metroGateShown = useRef(false);

  // ── audition player (setup screen) — also the whole "Loop a section" goal ─
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [srcUrl, setSrcUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewPos, setPreviewPos] = useState(0);
  const regionRef = useRef(region);
  regionRef.current = region;

  // ── persistence ─────────────────────────────────────────────────────────
  const [savedSections, setSavedSections] = useState<Section[]>([]);
  const [rawLoops, setRawLoops] = useState<RawLoop[]>([]);
  // sectionId → local stem-cache key, so a saved row can be re-opened and
  // actually play instead of just naming a track.
  const [sectionIndex, setSectionIndex] = useState<Map<string, string>>(new Map());
  const [resuming, setResuming] = useState(false);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  // Plan-ceiling dialog. `anon` picks "Sign in" over "Upgrade Plan" — only the
  // server knows a visitor is anonymous, so it defaults false and is set when
  // the gate says so.
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeAnon, setUpgradeAnon] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<UpgradeReason>('quota');
  const [loopCap, setLoopCap] = useState(3);
  /** Name typed on the range itself, before it is saved. */
  const [draftName, setDraftName] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  // ── modals / export ─────────────────────────────────────────────────────
  const [rightsOpen, setRightsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Phone only: the result header's tool chips live in a bottom sheet. Desktop
  // never renders the trigger (CSS display:none) so this stays false there.
  const [toolsOpen, setToolsOpen] = useState(false);
  // Phone only: which stem has its L/R pan row revealed. Desktop shows every
  // pan row always, so this stays null there.
  const [panOpen, setPanOpen] = useState<string | null>(null);
  // Wraps the Export button + its menu; read by the outside-click effect above.
  const exportWrapRef = useRef<HTMLDivElement | null>(null);
  const [format, setFormat] = useState<ExportFormat>('mp3');
  const [m4aOk, setM4aOk] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Batch-export progress label ("Exporting 3/6…") shown on the Export button.
  const [exportProg, setExportProg] = useState<string | null>(null);
  // ONE gate in front of every export. It carries whatever the user needs to
  // agree to or know about — the plan limit, the rights attestation, or both —
  // so an export is never fronted by two modals in a row.
  const [gate, setGate] = useState<ExportGate | null>(null);
  // Which run the rights modal is holding up, so accepting resumes the right one.
  const pendingRun = useRef<'upload' | 'link' | null>(null);

  const gd = goalDef(goal);
  const isPro = user?.plan === 'pro';
  /**
   * How long ONE separated section may be. Pro is unbounded; free stops at the
   * same minute the extension and /splitter stop at. Declared here, above both
   * audition handlers, because they clamp against it on every drag.
   */
  const maxSectionSec = isPro ? Infinity : FREE_SECTION_SECONDS;
  /**
   * Surfaced when a drag actually hits the ceiling — once per loaded track, not
   * on every pixel of the drag, so the box just slides and the reason is stated
   * exactly once. `capNoticeRef` resets whenever a new song is loaded.
   */
  const capNoticeRef = useRef(false);
  /**
   * The ribbon over the region is transient — it says its piece and gets out of
   * the way, because it sits ON the waveform the user is working in. It
   * re-arms on EVERY clamp, so each fresh attempt to grow past the cap brings
   * it back; the permanent `max` tag on the length readout is what keeps the
   * limit legible in between (a silently refusing handle reads as broken).
   */
  const [capFlash, setCapFlash] = useState(false);
  const capFlashTimer = useRef<number | null>(null);
  function flashCapRibbon() {
    setCapFlash(true);
    if (capFlashTimer.current) window.clearTimeout(capFlashTimer.current);
    capFlashTimer.current = window.setTimeout(() => setCapFlash(false), 3000);
  }
  useEffect(() => () => { if (capFlashTimer.current) window.clearTimeout(capFlashTimer.current); }, []);
  function noteSectionCap() {
    flashCapRibbon();
    if (capNoticeRef.current) return;
    capNoticeRef.current = true;
    // Mirrored to our own DB (not GA4-only): this is the moment the 60s cap is
    // FELT, and until 2026-09-03 the Studio's copy of it existed only in GA4 —
    // ad-blocked and EEA/UK-denied — so the cap's reach here was unmeasurable
    // while the extension's was not. `rl_wall_hit` is already on the server's
    // accepted list, so no backend change. ⚠️ Two things to know when reading it:
    //   • the capNoticeRef guard above means this fires ONCE PER DECODED SONG,
    //     not once per refused drag — so it will track modal opens closely, and
    //     the real signal is its ratio against the separations table.
    //   • feature events are SIGNED-IN ONLY, so an anonymous /splitter visitor's
    //     cap hit is dropped here. Signed-in users are the ones who can buy.
    funnelEvent('rl_wall_hit', { reason: 'length_cap', tier: !user ? 'anonymous' : 'free', surface: 'studio' }, 'studio');
    setUpgradeReason('length');
    setUpgradeOpen(true);
  }
  // Who the click ceiling applies to. Pro plays it through the whole song;
  // free members and signed-out visitors get the first minute.
  // Gated by PLAN for everyone, exactly like the chords — the two ceilings
  // have to agree or their padlocks land in different places. (The song-LOCK
  // below stays beta-exempt; that is a capability, not a ceiling.)
  const metroFree = !isPro;
  // Chords gate by PLAN only — unlike the click, the beta allowlist is not
  // exempt, because the owner asked to see this ceiling on their own account.
  const chordsFree = !isPro;
  const chordSpanSec = chordsFree
    ? Math.min(FREE_CHORDS_SECONDS, loaded?.duration || FREE_CHORDS_SECONDS)
    : loaded?.duration || 0;
  const chordsGated = chordsFree && (loaded?.duration || 0) > chordSpanSec + 0.5;
  /**
   * This separation came back with only vocals + `other` — i.e. the 2-stem
   * engine produced it. The rack's usual name for `other` is "Solo & Lead",
   * which on a 2-stem run labels the WHOLE instrumental as if the drums and
   * guitar were missing from a lane that contains both. Keyed off the RESULT,
   * not the device, so the label can never claim more than the audio backs.
   * (Same rule and same helper as /splitter.)
   */
  const twoStem = !!loaded && isTwoStemResult(loaded.emptyStems as Set<string>);
  const stemLabel = (id: StemName, name: string) =>
    twoStem && id === 'other' ? TWO_STEM_OTHER_NAME : name;
  const captureOk = tabCaptureSupported();
  // Only to pick which sentence to show when capture is unavailable — a phone
  // needs "use a computer", an unsupported desktop needs "use Chrome or Edge".
  const onPhone = isMobileTier();
  const capturing = phase !== null;
  const capturingRef = useRef(false);
  capturingRef.current = capturing;
  const link = useLinkPlayer(linkMeta, captureOpen, (m) => setErrorMsg(m), regionRef, previewRef, capturingRef);

  // THE ACTIVE TAB DECIDES THE SOURCE. `captureOpen`/`linkMeta` survive a tab
  // switch on purpose (so you can go back to a YouTube session you set up), but
  // that means a leftover link must never speak for an uploaded file: it would
  // ask for tab-capture permission and record the embed instead of the file.
  const usingLink = tab !== 'upload' && captureOpen && !!linkMeta;

  // ── boot: prefs, saved rows, usage, engine prefetch ─────────────────────
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(PREFS_KEY) || 'null');
      if (p && GOALS.some((g) => g.key === p.goal)) setGoal(p.goal);
      // `collapsed` is deliberately NOT restored: the Minimize control was
      // removed, so a saved `true` would strand the chooser in its compact
      // form with no way back.
    } catch { /* first run */ }
    try {
      const r = JSON.parse(localStorage.getItem(RAW_LOOPS_KEY) || '[]');
      if (Array.isArray(r)) setRawLoops(r);
    } catch { /* ignore */ }
    sectionsApi.list().then(setSavedSections).catch(() => {});
    usageApi.me().then(setUsage).catch(() => {});
    m4aSupported().then(setM4aOk).catch(() => {});
    loadSectionIndex().then(setSectionIndex).catch(() => {});
    prefetchStandardEngine();
    engineIsCached('roformer').then(setBestEngineReady).catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ goal, collapsed })); } catch { /* quota */ }
  }, [goal, collapsed]);

  useEffect(() => () => { engine.dispose(); metro.dispose(); }, [engine, metro]);
  useEffect(() => () => { if (srcUrl) URL.revokeObjectURL(srcUrl); }, [srcUrl]);

  // Theme: same global attribute + storage key as the live app, so the choice
  // carries in both directions rather than the preview keeping its own.
  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('rl-theme', next); } catch { /* ignore */ }
  }

  // Result playhead.
  useEffect(() => {
    engine.setCallbacks({
      onTimeUpdate: (t) => setPos(t),
      onEnded: () => { setMix((m) => ({ ...m, playing: false })); metro.stop(); },
    });
  }, [engine, metro]);

  // Detected tempo seeds the metronome (and the tempo chip) — unless the
  // drums-stem grid already set a better number this pass must not clobber.
  useEffect(() => {
    if (keyBpm?.bpm && !beatGridRef.current) setBpm(Math.round(keyBpm.bpm));
  }, [keyBpm]);

  // A tracked grid has to exist before the lock means anything; a manual BPM
  // override releases it on its own — that IS the override.
  const gridReady = chordsEnabled && !!beatGrid && (beatGrid.beats?.length || 0) > 0;
  // ⚠️ Pro ONLY. This used to read `(isPro || chordsEnabled)` so the owner could
  // reach it from their free-plan account while chords were beta-gated — now
  // that chordsEnabled is true for everyone, that OR would make the song-lock
  // free for all. Same for the three call sites below.
  const gridLocked = gridLock && isPro && gridReady && !!beatGrid && bpm === Math.round(beatGrid.bpm);

  useEffect(() => {
    metro.bpm = bpm;
    metro.beatsPerBar = beatsPerBar;
    metro.volume = metroVol;
    metro.speed = mix.speed;
    const locked = gridLocked;
    metro.grid = locked && beatGrid
      ? { beats: beatGrid.beats, downbeatIdx: beatGrid.downbeatIdx, accentSet: beatGrid.downbeatIdxs?.length ? new Set(beatGrid.downbeatIdxs) : null }
      : null;
    metro.songPos = locked ? () => engine.currentTime : null;
    // Locked clicks play on the engine's own context, shifted by what its
    // pitch pipe is buffering — heard-time alignment, not decode-time.
    metro.bus = locked ? engine.clickContext : null;
    metro.pipeDelay = locked ? () => engine.pipeDelaySec(mix.speed) : null;
    metro.duck = locked ? (at) => engine.duckAt(at) : null;
    metro.out = locked ? engine.outputNode : null;
    metro.limitSong = metroFree ? FREE_METRONOME_SECONDS : null;
    metro.subdiv = clickSubdiv;
  }, [bpm, beatsPerBar, metro, metroVol, mix.speed, beatGrid, gridLocked, engine, metroFree, clickSubdiv]);

  // Chords: same crossing rule as the click's ceiling — once per pass, and
  // only while the chord row is actually on screen.
  const chordGateShown = useRef(false);
  useEffect(() => {
    // Only while the shapes view is open: the ribbon simply stops at the
    // ceiling and its padlock says why, which is not worth a dialog.
    if (!chordsGated || !chordsEnabled || !chordStage) { chordGateShown.current = false; return; }
    if (pos <= chordSpanSec) { chordGateShown.current = false; return; }
    if (chordGateShown.current) return;
    chordGateShown.current = true;
    setUpgradeAnon(!user);
    setUpgradeReason('chords');
    setUpgradeOpen(true);
  }, [pos, chordsGated, chordsEnabled, chordStage, chordSpanSec, user]);

  // The free ceiling. Fires on the crossing, not on a click — someone who
  // never touches the metronome is never interrupted, and the dialog shows
  // once per pass rather than every frame.
  useEffect(() => {
    // Only interrupt when the click is genuinely audible — muted, or fader at
    // zero, means the ceiling costs them nothing right now.
    if (!metroFree || !metroOn || metroVol < 0.001) {
      if (metroPastFree) setMetroPastFree(false);
      metroGateShown.current = false;
      return;
    }
    const past = pos > FREE_METRONOME_SECONDS;
    if (past === metroPastFree) return;
    setMetroPastFree(past);
    if (past) {
      metro.stop();
      if (!metroGateShown.current) {
        metroGateShown.current = true;
        setUpgradeAnon(!user);
        setUpgradeReason('metronome');
        setUpgradeOpen(true);
      }
    } else {
      metroGateShown.current = false;
      if (mix.playing) metro.start();
    }
  }, [pos, metroFree, metroOn, metroVol, metroPastFree, metro, mix.playing, user]);

  // How much of the timeline the click lane covers. On free that is the first
  // minute and no more — the reference SHOWS the ceiling by ending the lane
  // there rather than only enforcing it when you reach it.
  const laneSpanSec = metroFree
    ? Math.min(FREE_METRONOME_SECONDS, loaded?.duration || FREE_METRONOME_SECONDS)
    : loaded?.duration || 0;

  // What the click lane draws.
  const clickMarks = useMemo(() => {
    const dur = loaded?.duration || 0;
    const none = { beats: [] as number[], accents: null as Set<number> | null };
    if (!dur) return none;
    if (gridLocked && beatGrid?.beats?.length) {
      const acc = beatGrid.downbeatIdxs?.length
        ? new Set<number>(beatGrid.downbeatIdxs)
        : new Set<number>(beatGrid.beats.map((_, i) => i).filter((i) => (((i - beatGrid.downbeatIdx) % beatsPerBar) + beatsPerBar) % beatsPerBar === 0));
      return { beats: beatGrid.beats, accents: acc };
    }
    const period = 60 / Math.max(20, bpm);
    const beats: number[] = [];
    for (let t = 0; t < dur; t += period) beats.push(t);
    return { beats, accents: new Set<number>(beats.map((_, i) => i).filter((i) => i % beatsPerBar === 0)) };
  }, [loaded?.duration, gridLocked, beatGrid, bpm, beatsPerBar]);

  // Where the clicks actually land, given the subdivision — the lane draws
  // these, so what you see is what you will hear.
  const clickTimes = useMemo(() => {
    const { beats, accents } = clickMarks;
    if (!beats.length) return [] as number[];
    const anchor = gridLocked && beatGrid ? beatGrid.downbeatIdx : 0;
    const out: number[] = [];
    for (let i = 0; i < beats.length; i++) {
      if (clickSubdiv === 0.5 && ((((i - anchor) % 2) + 2) % 2) !== 0) continue;
      out.push(beats[i]);
      if (clickSubdiv === 2 && i + 1 < beats.length) out.push((beats[i] + beats[i + 1]) / 2);
    }
    void accents;
    return out;
  }, [clickMarks, clickSubdiv, gridLocked, beatGrid]);

  // Validate a pasted link as it's typed. YouTube gets a real oEmbed lookup so
  // the card can show the actual video; SoundCloud is shape-checked here and
  // gets its title from the widget once it loads. A network failure never
  // blocks a well-formed link — it degrades to 'unknown' and lets you through.
  useEffect(() => {
    if (tab === 'upload') return;
    const raw = linkInput.trim();
    if (!raw) { setLinkState('idle'); setLinkMeta(null); return; }

    if (tab === 'soundcloud') {
      const u = parseSoundCloudUrl(raw);
      if (!u) { setLinkState('bad'); setLinkMeta(null); return; }
      setLinkMeta({ kind: 'sc', ref: u, url: u, title: 'SoundCloud track', author: '', thumb: '' });
      setLinkState('ok');
      return;
    }

    const id = parseYtVideoId(raw);
    if (!id) { setLinkState('bad'); setLinkMeta(null); return; }
    setLinkState('validating');
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const url = `https://www.youtube.com/watch?v=${id}`;
      const fallback: LinkMeta = {
        kind: 'yt', ref: id, url, title: 'YouTube video', author: '',
        thumb: `https://i.ytimg.com/vi/${id}/mqdefault.jpg`,
      };
      try {
        const r = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
        if (cancelled) return;
        if (!r.ok) { setLinkState('bad'); setLinkMeta(null); return; }
        const j = await r.json();
        if (cancelled) return;
        setLinkMeta({
          kind: 'yt', ref: id, url,
          title: j.title || 'YouTube video',
          author: j.author_name || '',
          thumb: j.thumbnail_url || fallback.thumb,
        });
        setLinkState('ok');
        // Then enrich, without holding up the card. oEmbed alone can't give
        // views/likes/duration; /api/yt-meta can. Strictly additive — a
        // failure here leaves the card exactly as it already renders, and the
        // id is re-checked on arrival so a fast re-paste can't cross wires.
        void ytMeta(id).then((extra) => {
          if (cancelled || !extra) return;
          setLinkMeta((prev) => (prev && prev.ref === id ? { ...prev, ...extra } : prev));
        });
      } catch {
        if (cancelled) return;
        setLinkMeta(fallback);
        setLinkState('unknown');
      }
    }, 350);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [linkInput, tab]);

  useEffect(() => {
    if (!autoOpenRef.current) return;
    if (linkState === 'ok' && linkMeta) { autoOpenRef.current = false; openCapture(); }
    else if (linkState === 'bad') autoOpenRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkState, linkMeta]);

  function clearLink() {
    setLinkInput('');
    autoOpenRef.current = false;
    setLinkState('idle');
    setLinkMeta(null);
    setCaptureOpen(false);
    setSuggestions([]);
  }

  // Opening the panel seeds a sensible first window and (YouTube only) asks the
  // server proxy for most-replayed peaks + chapters to offer as jump-to chips.
  /**
   * Set when we open a track whose stems are gone: the link is filled in for
   * you, and this carries you into the goal chooser as soon as the oEmbed
   * lookup resolves, instead of stopping at a Continue button.
   */
  const autoCapture = useRef(false);
  // One centred seed per capture session (extension: stagingCenteredForId).
  const linkSeededRef = useRef(false);
  useEffect(() => {
    if (!autoCapture.current || !linkMeta) return;
    autoCapture.current = false;
    openCapture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkMeta]);

  function openCapture() {
    if (!linkMeta) return;
    setCaptureOpen(true);
    setErrorMsg(null);
    // Placeholder until the player reports the real duration — the picker is
    // hidden until then, and the seed effect below re-centres it once known.
    linkSeededRef.current = false;
    setRegion({ start: 0, end: 30 });
    setSuggestions([]);
    setEditA(null);
    setEditB(null);
  }

  // ── A/B audition against the embed ──────────────────────────────────────
  // There is no PCM yet, so the "waveform" is decorative and the selection is
  // auditioned through the player itself: dragging seeks (throttled, or the
  // player stutters), and 350ms after you let go it drops into an A→B loop.
  function auditionRegion(nextRaw: { start: number; end: number }) {
    const prev = regionRef.current;
    let live = 0;
    try { live = link.playerRef.current?.getDuration() || 0; } catch { /* not ready */ }
    const D = Math.max(1, live > 0 ? live : link.duration);

    const clamp = (n: number) => Math.max(0, Math.min(n, D));
    let next = { start: clamp(nextRaw.start), end: clamp(nextRaw.end) };

    // Hold the ceiling by moving the edge the user ISN'T dragging. Two ceilings
    // apply: the 5-minute capture limit (real-time recording) and, for a free
    // plan on anything but a loop, the section cap.
    const capSec = goal === 'loop' ? MAX_CAPTURE_SEC : Math.min(MAX_CAPTURE_SEC, maxSectionSec);
    if (next.end - next.start > capSec) {
      const movedEnd = Math.abs(next.end - prev.end) >= Math.abs(next.start - prev.start);
      next = movedEnd
        ? { start: next.start, end: Math.min(next.start + capSec, D) }
        : { start: Math.max(0, next.end - capSec), end: next.end };
      if (capSec === maxSectionSec) noteSectionCap();
    }
    setRegion(next);

    const p = link.playerRef.current;
    if (!p) return;

    // Dragging B auditions the run-up INTO the end; anything else plays the start.
    const dStart = Math.abs(next.start - prev.start);
    const dEnd = Math.abs(next.end - prev.end);
    const auditT = dEnd > dStart + 0.01 ? Math.max(next.start, next.end - 2.5) : next.start;

    const now = Date.now();
    if (now - lastSeekRef.current > 140 && Math.abs(auditT - lastAuditRef.current) > 0.25) {
      lastSeekRef.current = now;
      lastAuditRef.current = auditT;
      previewRef.current = false; // scrubbing — don't let the loop yank us back
      try { p.seekTo(auditT, true); p.playVideo(); } catch { /* ignore */ }
    }

    window.clearTimeout(scrubStopRef.current);
    scrubStopRef.current = window.setTimeout(() => {
      previewRef.current = true;
      setPreviewingLink(true);
      try { link.playerRef.current?.playVideo(); } catch { /* ignore */ }
    }, 350);
  }

  function stopLinkPlayback() {
    window.clearTimeout(scrubStopRef.current);
    lastAuditRef.current = -1;
    previewRef.current = false;
    setPreviewingLink(false);
    try { link.playerRef.current?.pauseVideo(); } catch { /* ignore */ }
  }

  function toggleLinkPreview() {
    const p = link.playerRef.current;
    if (!p) return;
    if (previewRef.current) { stopLinkPlayback(); return; }
    previewRef.current = true;
    setPreviewingLink(true);
    try {
      // Same resume rule as the upload picker: only jump to A from outside.
      const t = p.getCurrentTime();
      if (t < region.start || t >= region.end - 0.05) p.seekTo(region.start, true);
      p.playVideo();
    } catch { /* ignore */ }
  }

  // ── typed A/B fields ────────────────────────────────────────────────────
  const MIN_GAP = 1.5;
  function parseTimeInput(str: string, fallback: number): number {
    const s = str.trim();
    let sec = NaN;
    if (/^\d+:\d{1,2}$/.test(s)) {
      const [m, ss] = s.split(':');
      sec = Number(m) * 60 + Number(ss);
    } else if (/^\d+(\.\d+)?$/.test(s)) {
      sec = Number(s);
    }
    if (Number.isNaN(sec)) return fallback;
    const D = link.duration > 0 ? link.duration : sec;
    return Math.max(0, Math.min(D, sec));
  }
  function commitA() {
    if (editA == null) return;
    const sec = parseTimeInput(editA, region.start);
    setRegion({ start: Math.min(sec, region.end - MIN_GAP), end: region.end });
    setEditA(null);
  }
  function commitB() {
    if (editB == null) return;
    const sec = parseTimeInput(editB, region.end);
    setRegion({ start: region.start, end: Math.max(sec, region.start + MIN_GAP) });
    setEditB(null);
  }

  // Keyboard for the capture panel — same bindings as the upload picker.
  useEffect(() => {
    if (!captureOpen || !link.duration || capturing) return;
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const D = link.duration;
      const r = regionRef.current;
      if (e.code === 'Space') {
        e.preventDefault();
        const p = link.playerRef.current;
        if (!p) return;
        try {
          if (link.playing) p.pauseVideo();
          else { stopAuditionTimers(); p.playVideo(); }
        } catch { /* ignore */ }
        return;
      }
      const step = e.shiftKey ? 5 : 1;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const d = e.key === 'ArrowLeft' ? -step : step;
        const w = r.end - r.start;
        const ns = Math.max(0, Math.min(D - w, r.start + d));
        setRegion({ start: ns, end: ns + w });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const d = e.key === 'ArrowUp' ? step : -step;
        setRegion({ start: r.start, end: Math.max(r.start + MIN_GAP, Math.min(D, r.end + d)) });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureOpen, link.duration, capturing, link.playing]);

  function stopAuditionTimers() {
    window.clearTimeout(scrubStopRef.current);
    previewRef.current = false;
    setPreviewingLink(false);
  }

  useEffect(() => {
    if (!captureOpen || !linkMeta || linkMeta.kind !== 'yt' || !link.duration) return;
    let dead = false;
    fetchYtSuggestions(linkMeta.ref, link.duration)
      .then((s) => { if (!dead) setSuggestions(s); })
      .catch(() => {});
    return () => { dead = true; };
  }, [captureOpen, linkMeta, link.duration]);

  // Seed the window against the real duration once the player reports it —
  // centred (see centeredRegion). Once per capture session: openCapture resets
  // the flag so a re-opened picker re-centres, while a later duration
  // refinement can only clamp, never yank a window the user has placed.
  useEffect(() => {
    if (!captureOpen || !link.duration) return;
    if (!linkSeededRef.current) {
      linkSeededRef.current = true;
      setRegion(centeredRegion(link.duration));
      return;
    }
    setRegion((r) => (r.end > link.duration ? { start: 0, end: Math.min(30, link.duration) } : r));
  }, [captureOpen, link.duration]);

  useEffect(() => () => {
    window.clearTimeout(scrubStopRef.current);
    window.clearTimeout(upSettleRef.current);
  }, []);

  // Close the account menu on an outside click or Escape.
  useEffect(() => {
    if (!userMenuOpen) return;
    function onDown(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setUserMenuOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  // Close the export menu on an outside click. `pointerdown`, not `mousedown`,
  // for the reason the account sheet in AppShell.tsx documents: the synthesized
  // mouse event only lands once a touch is RELEASED, so on a phone the menu sat
  // there under your finger for the length of the tap.
  // The ref is on the wrapper, which holds the trigger AND the menu — so a tap
  // on the Export button itself falls through to its own toggle instead of
  // being closed here and immediately reopened.
  useEffect(() => {
    if (!exportOpen) return;
    const onDown = (e: PointerEvent) => {
      if (exportWrapRef.current && !exportWrapRef.current.contains(e.target as Node)) setExportOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [exportOpen]);

  // A suggestion sets the section to the whole chapter / hot stretch. If that's
  // longer than the cap, centre the 5 minutes on the peak rather than truncating.
  function applySuggestion(s: Suggestion) {
    const D = link.duration > 0 ? link.duration : s.end;
    let start = Math.max(0, Math.min(s.start, D));
    let end = Math.min(D, Math.max(s.end, start + MIN_GAP));
    if (end - start > MAX_CAPTURE_SEC) {
      const peak = Math.max(start, Math.min(s.peak, end));
      start = Math.max(0, Math.min(peak - MAX_CAPTURE_SEC / 2, D - MAX_CAPTURE_SEC));
      end = start + MAX_CAPTURE_SEC;
    }
    setRegion({ start, end });
    const p = link.playerRef.current;
    if (p) {
      previewRef.current = true;
      setPreviewingLink(true);
      try { p.seekTo(start, true); p.playVideo(); } catch { /* ignore */ }
    }
  }

  // ── the capture run ─────────────────────────────────────────────────────
  // Records the tab while the embed plays A→B, then hands the take straight
  // into the same decode → separate → mixer path an upload takes. The A/B
  // picker WAS the region choice, so there is no second selection step.
  async function runLinkCapture() {
    const p = link.playerRef.current;
    if (!p || !linkMeta) return;
    if (captureRef.current) return; // already recording
    if (region.end - region.start < MIN_REGION) {
      setErrorMsg('Grab at least a few seconds — drag the B handle right.');
      return;
    }
    if (linkMeta.kind === 'sc' && !link.scUnlocked) {
      setErrorMsg('First press play on the SoundCloud player above — one click there lets this page control it, then hit the button again.');
      return;
    }
    // Start the engine download NOW so it overlaps the real-time recording
    // instead of adding to it.
    preloadEngine(sepEngine === 'roformer' && isPro ? 'roformer' : 'demucs');
    stopLinkPlayback();
    setErrorMsg(null);
    setCapturedSec(0);

    const handle = startCapture(
      p,
      { start: region.start, end: Math.min(region.end, region.start + MAX_CAPTURE_SEC) },
      (ph) => setPhase(ph),
      (sec) => setCapturedSec(sec),
    );
    captureRef.current = handle;

    let blob: Blob | null = null;
    try {
      blob = await handle.done;
    } catch (e) {
      const msg = e instanceof CaptureError ? captureErrorMessage(e) : 'Recording failed. Try again.';
      if (msg) setErrorMsg(msg);
      setPhase(null);
      captureRef.current = null;
      return;
    }
    setPhase(null);
    captureRef.current = null;
    if (!blob) return; // cancelled

    // The take becomes a normal File and runs the upload path from here on.
    const name = `${(linkMeta.title || 'clip').replace(/[\\/:*?"<>|]/g, '')}.webm`;
    const takeFile = new File([blob], name, { type: blob.type || 'audio/webm' });
    try {
      const dec = await decodeFileToStereo(takeFile);
      decodedRef.current = dec;
      setFile(takeFile);
      setDuration(dec.durationSec);
      setSrcPeaks(computePeaks(dec.left, dec.right, 150));
      if (srcUrl) URL.revokeObjectURL(srcUrl);
      setSrcUrl(URL.createObjectURL(takeFile));
      detectKeyBpm(dec.left, dec.right, dec.sampleRate).then(applyKeyBpm).catch(() => {});
      // The captured window IS the section — separate all of it.
      const whole = { start: 0, end: dec.durationSec };
      setRegion(whole);
      setCaptureOpen(false);
      await runSeparation(whole, takeFile, {
        sourceKind: 'url',
        sourceRef: linkMeta.url,
        sourceTitle: linkMeta.title,
      });
    } catch {
      setErrorMsg("That take couldn't be read back. Try grabbing the section again.");
    }
  }

  function cancelCapture() {
    captureRef.current?.cancel();
    captureRef.current = null;
    setPhase(null);
  }
  function keepWhatWeHave() {
    captureRef.current?.stop();
  }

  // ── recent uploads ──────────────────────────────────────────────────────
  // The files this browser has loaded before, so picking one up again skips the
  // OS file dialog. Local only — the audio never leaves the machine.
  const [recents, setRecents] = useState<RecentUploadMeta[]>([]);
  const refreshRecents = useCallback(() => {
    listRecentUploads().then(setRecents).catch(() => setRecents([]));
  }, []);
  useEffect(refreshRecents, [refreshRecents]);

  // ── file intake ─────────────────────────────────────────────────────────
  const pickFile = useCallback(async (f: File) => {
    setErrorMsg(null);
    setDecoding(true);
    applyKeyBpm(null);
    try {
      const dec = await decodeFileToStereo(f);
      decodedRef.current = dec;
      const peaks = computePeaks(dec.left, dec.right, 150);
      setFile(f);
      setDuration(dec.durationSec);
      setSrcPeaks(peaks);
      setRegion(centeredRegion(dec.durationSec));
      capNoticeRef.current = false; // a new song earns one explanation
      // Only files that actually decoded get remembered — a rejected file in
      // the recents list is a trap, not a shortcut. Fire-and-forget: the
      // upload must not wait on, or fail with, the cache.
      void rememberUpload(f, { duration: dec.durationSec, peaks }).then(refreshRecents);
      // A new source means a new timeline — don't let the previous track's
      // audition target throttle the first drag on this one.
      window.clearTimeout(upSettleRef.current);
      upPendingRef.current = -1;
      lastAuditRef.current = -1;
      lastSeekRef.current = 0;
      if (srcUrl) URL.revokeObjectURL(srcUrl);
      setSrcUrl(URL.createObjectURL(f));
      setMode('input');
      setLoaded(null);
      preloadEngine();
      setAnalysing(true);
      detectKeyBpm(dec.left, dec.right, dec.sampleRate)
        .then(applyKeyBpm)
        .catch(() => {})
        .finally(() => setAnalysing(false));
    } catch {
      setErrorMsg("That file couldn't be read. Try MP3, WAV, M4A, FLAC or OGG.");
    } finally {
      setDecoding(false);
    }
  }, [srcUrl, refreshRecents]);

  /** Load a remembered upload back into the picker — the whole point of the
   *  recents list. Runs the ordinary intake path, so the file behaves exactly
   *  as it would fresh off the disk (decode, peaks, key/BPM, region). */
  const openRecent = useCallback(async (r: RecentUploadMeta) => {
    const blob = await getUploadBlob(r.id);
    if (!blob) {
      // Evicted under storage pressure between the list and the click.
      setErrorMsg('That file is no longer stored here. Choose it again to reload it.');
      await forgetUpload(r.id);
      refreshRecents();
      return;
    }
    // Identity is name+size, so this rebuild lands on the SAME record and just
    // refreshes its position. lastModified is carried anyway to keep the File
    // honest about the original.
    await pickFile(new File([blob], r.name, { type: r.type, lastModified: r.lastModified }));
  }, [pickFile, refreshRecents]);

  const dropRecent = useCallback(async (id: string) => {
    await forgetUpload(id);
    refreshRecents();
  }, [refreshRecents]);

  /** "Separate tracks" from the mixer — go pick a DIFFERENT part of this song.
   *
   * Reopening a saved section loads its stems only; the decoded song itself is
   * not in memory. So this used to land on the EMPTY upload screen, and the
   * only way to grab another part was to paste the link and sit through
   * capture a second time — the exact "how do I pick a different piece?" dead
   * end the owner hit (2026-08-15).
   *
   * Two sources, two restores:
   *  - Upload: the original file is cached beside the stems (`source` on
   *    CachedSeparation) — decode it back into the picker. No re-upload.
   *  - Link (YouTube/SoundCloud): the cached audio is only the captured
   *    WINDOW, not the whole song, so a different part means a fresh capture.
   *    Rebuild the link and auto-open the capture flow — no re-pasting, the
   *    one thing that CAN be skipped.
   *
   * Deliberately NOT `pickFile`: that clears `loaded` because it's for a
   * genuinely new song. Here the mixer you came from stays one tab away, so
   * changing your mind costs nothing and no separation is spent.
   */
  // `onMissingSource` runs when the original audio is no longer on this device
  // (evicted, or separated before sources were cached). Callers decide where
  // that should land — "Separate tracks" wants the uploader, but the mixer's
  // back arrow must not dump you on an empty page.
  const openNewSection = useCallback(async (onMissingSource?: () => void) => {
    setScreen('setup');
    // Two sources at once otherwise: the rack keeps playing while the picker
    // auditions the raw track underneath it.
    engine.pause();
    stopPreview();
    if (file || !loaded) return; // source already decoded — the picker is live

    const ytId = ytIdOf(loaded.sourceRef);
    const isSc = isSoundCloudRef(loaded.sourceRef);
    if (ytId || isSc || /^https?:\/\//i.test(loaded.sourceRef)) {
      const href = ytId ? `https://www.youtube.com/watch?v=${ytId}` : loaded.sourceRef;
      setTab(isSc ? 'soundcloud' : 'url');
      setLinkInput(href);
      autoCapture.current = true; // straight into the capture flow, as reopenTrack does
      return;
    }

    setDecoding(true);
    try {
      const blob = await getSourceBlob(loaded.sourceRef);
      if (!blob) { onMissingSource?.(); return; } // nothing cached to reopen
      // Named by sourceRef, not display title: a new separation takes its ref
      // from the File's name, and only the original name keeps every section
      // of this song grouped as ONE library row (and the source-blob dedupe
      // keyed to it).
      const f = new File([blob], loaded.sourceRef, { type: blob.type || 'audio/wav' });
      const dec = await decodeFileToStereo(f);
      decodedRef.current = dec;
      setFile(f);
      setDuration(dec.durationSec);
      setSrcPeaks(computePeaks(dec.left, dec.right, 150));
      setRegion(centeredRegion(dec.durationSec));
      capNoticeRef.current = false; // a new song earns one explanation
      upPendingRef.current = -1;
      lastAuditRef.current = -1;
      lastSeekRef.current = 0;
      if (srcUrl) URL.revokeObjectURL(srcUrl);
      setSrcUrl(URL.createObjectURL(f));
      setTab('upload');
      setMode('input');
      preloadEngine();
    } catch {
      /* leaves the ordinary setup screen standing */
    } finally {
      setDecoding(false);
    }
  }, [engine, file, loaded, srcUrl]);

  /** Drop the restore pointer — otherwise a reload drags you back into a mixer
      you deliberately left. */
  function forgetSession() {
    try {
      localStorage.removeItem(SESSION_KEY);
      const url = new URL(window.location.href);
      if (url.searchParams.has('t')) {
        url.searchParams.delete('t');
        window.history.replaceState(null, '', url.toString());
      }
    } catch { /* ignore */ }
  }

  function clearTrack() {
    stopPreview();
    forgetSession();
    engine.dispose();
    metro.stop();
    decodedRef.current = null;
    setFile(null);
    setLoaded(null);
    setLanePeaks({});
    setSrcPeaks([]);
    applyKeyBpm(null);
    applyChords(null);
    applyBeatGrid(null);
    setChordsBusy(false);
    setChordStage(false);
    activeSepKeyRef.current = null;
    setMode('input');
    if (srcUrl) URL.revokeObjectURL(srcUrl);
    setSrcUrl(null);
  }

  // ── audition / raw-loop playback ────────────────────────────────────────
  // One <audio> on the source file serves both the setup-screen "Preview loop"
  // and the entire "Loop a section" goal — a raw loop IS the original audio,
  // so there is nothing to separate and nothing to charge for.
  function stopPreview() {
    window.clearTimeout(upSettleRef.current);
    upPendingRef.current = -1;
    const a = audioRef.current;
    if (a) a.pause();
    setPreviewing(false);
  }

  // Dragging the section IS auditioning — same rules as the capture picker and
  // the extension: A (or moving the whole region) plays from the new start, B
  // plays the run-up into the new end, seeks are throttled so playback doesn't
  // stutter, and the A→B wrap in onAudioTime keeps it looping.
  function auditionUpload(nextRaw: { start: number; end: number }) {
    const prev = regionRef.current;
    let next = {
      start: Math.max(0, nextRaw.start),
      end: Math.min(duration, Math.max(nextRaw.start + MIN_REGION, nextRaw.end)),
    };
    // Hold the plan's ceiling by moving the edge the user ISN'T dragging —
    // same trick the link picker already uses for its 5-minute capture cap, so
    // the box slides instead of refusing to move. Looping is exempt.
    if (goal !== 'loop' && next.end - next.start > maxSectionSec) {
      const movedEnd = Math.abs(next.end - prev.end) >= Math.abs(next.start - prev.start);
      next = movedEnd
        ? { start: next.start, end: Math.min(next.start + maxSectionSec, duration) }
        : { start: Math.max(0, next.end - maxSectionSec), end: next.end };
      noteSectionCap();
    }
    setRegion(next);

    const a = audioRef.current;
    if (!a || !srcUrl) return;
    const dStart = Math.abs(next.start - prev.start);
    const dEnd = Math.abs(next.end - prev.end);
    const auditT = dEnd > dStart + 0.01 ? Math.max(next.start, next.end - 2.5) : next.start;

    const now = Date.now();
    upPendingRef.current = auditT;
    if (now - lastSeekRef.current > 140 && Math.abs(auditT - lastAuditRef.current) > 0.25) {
      lastSeekRef.current = now;
      lastAuditRef.current = auditT;
      a.currentTime = auditT;
      setPreviewPos(auditT);
      // The drag itself is the user gesture, so play is allowed. No error
      // banner from here — a blocked play mid-drag would just be noise.
      void a.play().then(() => setPreviewing(true)).catch(() => {});
    }

    // The throttle drops every move inside its 140ms window — including the
    // LAST one, which is the position the user actually chose. Without this
    // trailing seek a quick drag leaves the playhead wherever the first move
    // landed (usually the old A), and since the A→B wrap below only catches a
    // playhead past B, nothing ever pulls it into the new section. The link
    // picker settles the same way; this is the upload picker's version.
    window.clearTimeout(upSettleRef.current);
    upSettleRef.current = window.setTimeout(() => {
      const el = audioRef.current;
      const target = upPendingRef.current;
      // Compare against the last target we actually seeked to, not against the
      // live clock — the clock has moved on, and re-seeking to the same target
      // would just stutter.
      if (!el || target < 0 || Math.abs(target - lastAuditRef.current) <= 0.05) return;
      lastSeekRef.current = Date.now();
      lastAuditRef.current = target;
      el.currentTime = target;
      setPreviewPos(target);
      void el.play().then(() => setPreviewing(true)).catch(() => {});
    }, 180);
  }

  function togglePreview() {
    const a = audioRef.current;
    if (!a || !srcUrl) return;
    if (previewing) { stopPreview(); return; }
    // Resume where you paused if that's still inside the section; only jump
    // back to A when the playhead sits outside it — the extension's behaviour.
    const r = regionRef.current;
    if (a.currentTime < r.start || a.currentTime >= r.end - 0.05) a.currentTime = r.start;
    setPreviewPos(a.currentTime);
    a.play().then(() => setPreviewing(true)).catch(() => setErrorMsg('Playback was blocked — click the page and try again.'));
  }

  function onAudioTime() {
    const a = audioRef.current;
    if (!a) return;
    const r = regionRef.current;
    if (a.currentTime >= r.end) a.currentTime = r.start;
    setPreviewPos(a.currentTime);
  }

  // timeupdate only fires ~4×/s — too coarse for a moving playhead. While the
  // preview plays, follow the clock every frame, the way the extension does.
  useEffect(() => {
    if (!previewing) return;
    let raf = 0;
    const tick = () => {
      const a = audioRef.current;
      if (a) setPreviewPos(a.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [previewing]);

  // ── keyboard: move / resize the section (the real bindings) ─────────────
  useEffect(() => {
    if (screen !== 'setup' || mode !== 'input' || !file) return;
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const step = e.shiftKey ? 5 : 1;
      const r = regionRef.current;
      const len = r.end - r.start;
      if (e.code === 'Space') { e.preventDefault(); togglePreview(); return; }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const d = e.key === 'ArrowLeft' ? -step : step;
        let s = Math.max(0, r.start + d);
        let en = s + len;
        if (en > duration) { en = duration; s = Math.max(0, en - len); }
        setRegion({ start: s, end: en });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const d = e.key === 'ArrowUp' ? step : -step;
        setRegion({ start: r.start, end: Math.min(duration, Math.max(r.start + MIN_REGION, r.end + d)) });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, mode, file, duration, previewing]);

  // ── the CTA ─────────────────────────────────────────────────────────────
  const regionLen = Math.max(0, region.end - region.start);
  /** True when the current selection is longer than this plan allows. */
  const sectionTooLong = goal !== 'loop' && regionLen > maxSectionSec + 0.5;
  /** The plan is HOLDING the edge right now — the drag stopped growing here,
      not because the song ended. Drives the inline `max` marker: the modal
      explains once per song, this says why the box won't grow every time
      after that, without another interruption. */
  const atSectionCap = goal !== 'loop' && Number.isFinite(maxSectionSec)
    && regionLen >= maxSectionSec - 0.5 && duration > maxSectionSec + 0.5;
  const muteNames = mutes.map((m) => STEM_META[m]?.label || m);
  const ctaVerb =
    goal === 'loop' ? 'Save this loop'
      : goal === 'karaoke' ? 'Remove vocals'
        : goal === 'backing'
          ? (muteNames.length <= 2 ? `Mute ${muteNames.join(' + ')}` : `Mute ${muteNames.length} instruments`)
          : 'Separate to stems';
  const ctaLabel = `${ctaVerb} · ${fmtTime(region.start)}–${fmtTime(region.end)}`;

  // Terminology note: the design handoff said "credit"; the live Studio has
  // never used that word (it counts "separations"). Kept ours until the owner
  // picks one, so the two surfaces don't teach different vocabularies.
  const HINTS: Record<GoalKey, string> = {
    loop: 'Free — looping keeps the original audio exactly as it is.',
    karaoke: 'Uses one of your daily separations. You still get all six parts — this only sets your starting mix.',
    backing: 'Uses one of your daily separations. Muted parts stay in the mixer, so you can bring them back any time.',
    studio: 'Uses one of your daily separations. Opens the full six-track mixer.',
  };

  // Once-a-day heads-up: on the first Studio load of each calendar day, a free
  // user is told where they stand before they start work, rather than finding
  // out at the CTA. Keyed on the local date, so it shows at most once a day
  // however many times the page is opened.
  //
  // The date is a deliberate simplification: the quota itself runs on a rolling
  // 24h window (see the PHP), so the two don't line up exactly — but "once per
  // calendar day" is what makes the interruption predictable, and the copy
  // names the real refill time rather than implying a midnight reset.
  useEffect(() => {
    if (isPro || !usage || usage.daily_quota == null) return;
    const left = Math.max(0, usage.daily_quota - (usage.today ?? 0));
    // An untouched allowance is nothing to announce: "you have 5 of 5" is
    // friction with no information, shown at the worst moment — most visitors
    // are still before their FIRST separation (see the sign-in-funnel note),
    // and a plan dialog is the last thing to put between them and trying the
    // product. The interruption is earned only once the count means something:
    // any use today, including none left. The flag is only burned when the
    // dialog actually shows, so a full-quota morning visit doesn't swallow a
    // low-quota afternoon one.
    if (left >= usage.daily_quota) return;
    const today = new Date().toDateString();
    let seen: string | null = null;
    try { seen = localStorage.getItem(QUOTA_NOTICE_KEY); } catch { /* private mode */ }
    if (seen === today) return;
    try { localStorage.setItem(QUOTA_NOTICE_KEY, today); } catch { /* private mode */ }
    // Out already? Say so plainly instead of "you have 0 left".
    setUpgradeReason(left > 0 ? 'remaining' : 'quota');
    setUpgradeOpen(true);
    // Deliberately keyed on the arrival of `usage` only: re-running on every
    // usage refresh (each separation updates it) would reopen mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usage != null, isPro]);

  // Out of separations for today? `usage` is the same figure the rail and the
  // header count from, so this agrees with what the user can already see.
  // Pro has no cap; a missing `usage` (request still in flight, or it failed)
  // reads as "not exhausted" so a network hiccup can never lock someone out —
  // the server-side gate in runSeparation is still the authority.
  const outOfSeparations =
    !isPro && usage?.daily_quota != null && Math.max(0, usage.daily_quota - (usage.today ?? 0)) <= 0;

  function onCta() {
    if (regionLen < 1) return;
    // Check the ceiling BEFORE anything expensive begins. This used to live
    // only inside runSeparation — which, on the link path, runs *after* the
    // tab capture has recorded the section in real time. So the user sat
    // through a 30-second recording, then got told they had no separations
    // left. Looping is exempt: it costs nothing, so it stays available.
    if (goal !== 'loop' && outOfSeparations) { setUpgradeReason('quota'); setUpgradeOpen(true); return; }
    // Backstop for the drag clamps above: a region restored from a saved
    // session or section predates this cap, so it can arrive over-length
    // without any drag having happened. Never separate more than the plan
    // allows without saying so.
    if (sectionTooLong) { setUpgradeReason('length'); setUpgradeOpen(true); return; }
    // Link sources: "Loop a section" needs no recording at all — the embed
    // itself loops A→B, so it costs nothing and starts instantly.
    if (usingLink) {
      if (goal === 'loop') { saveRawLoop(); toggleLinkPreview(); return; }
      const ok = user?.rights_acknowledged_at || hasAcknowledgedLocally();
      if (!ok) { pendingRun.current = 'link'; setRightsOpen(true); return; }
      void runLinkCapture();
      return;
    }
    // On a link tab with no confirmed link there is nothing to run — a file
    // left over from the upload tab must not be separated behind the user's back.
    if (tab !== 'upload') return;
    if (!file) return;
    if (goal === 'loop') { saveRawLoop(); return; }
    const acked = user?.rights_acknowledged_at || hasAcknowledgedLocally();
    if (!acked) { pendingRun.current = 'upload'; setRightsOpen(true); return; }
    void runSeparation();
  }

  function saveRawLoop() {
    const ref = usingLink ? linkMeta!.url : file?.name;
    if (!ref) return;
    const row: RawLoop = {
      id: `raw_${Date.now().toString(36)}`,
      sourceRef: ref,
      label: `Loop ${fmtTime(region.start)}`,
      start: region.start,
      end: region.end,
      createdAt: Date.now(),
    };
    // Raw loops stay on this device: they carry no stems, and the backend's
    // section rows are meant to be playable elsewhere. Same rule as the
    // extension, which filters `raw` out of its cloud sync.
    const next = [...rawLoops, row];
    setRawLoops(next);
    try { localStorage.setItem(RAW_LOOPS_KEY, JSON.stringify(next)); } catch { /* quota */ }
    flashSaved();
    const a = audioRef.current;
    if (a) { a.currentTime = region.start; void a.play().then(() => setPreviewing(true)).catch(() => {}); }
  }

  function flashSaved() {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1600);
  }

  interface SrcInfo { sourceKind: 'upload' | 'url'; sourceRef: string; sourceTitle: string }

  async function runSeparation(win?: { start: number; end: number }, srcFile?: File, src?: SrcInfo) {
    const activeFile = srcFile ?? file;
    const dec = decodedRef.current;
    if (!activeFile || !dec) return;
    const w = win ?? region;
    const info: SrcInfo = src ?? {
      sourceKind: 'upload',
      sourceRef: activeFile.name,
      sourceTitle: cleanTrackName(activeFile.name),
    };
    stopPreview();
    setErrorMsg(null);

    const stem = nominalStem(goal, mutes);
    // Best is Pro-only — resolve it once here so the warm-up below and the run
    // further down can never disagree about which model is in play.
    const runEngine: SepEngine = sepEngine === 'roformer' && !isPro ? 'demucs' : sepEngine;

    // Separation funnel. Fired on the user's actual attempt — before the engine
    // warm-up — so the gap between `start` and `rl_separate` finally exposes how
    // many runs die while the weights download.
    const sepBase = sepEventBase(info, stem, Math.max(0, w.end - w.start));
    const sepStartedAt = Date.now();
    track('rl_separate_start', sepBase);

    setMode('working');
    setProgress(0);

    // ── 1. Engine BEFORE quota ────────────────────────────────────────────
    // On a first visit this downloads ~142MB (64MB on the mobile tier) and can
    // run for minutes. It used to sit AFTER the consume call, so a user who
    // gave up — or whose connection dropped — still lost a separation and got
    // nothing: measured at roughly a third of first-time engine downloads, and
    // the cause of one user burning all five free runs in under four minutes.
    // Warming first makes an abandoned download cost nothing. `ensureReady` is
    // memoised, so the separateClientSide call below reuses this same engine.
    try {
      await ensureEngineReady(runEngine, (frac, label) => {
        setProgress(Math.round(frac * 100));
        void label;
      });
    } catch (e) {
      setMode('input');
      setErrorMsg('Could not load the audio engine. Check your connection and try again — nothing was used up.');
      track('rl_separate_error', {
        ...sepBase,
        reason: 'engine_load',
        err_name: (e as Error)?.name || '',
        seconds: Math.round((Date.now() - sepStartedAt) / 1000),
      });
      return; // deliberately BEFORE consume — no quota is spent
    }

    // ── 2. Now charge ─────────────────────────────────────────────────────
    // Same authoritative server-side gate as before, just moved behind the
    // warm-up. onCta already pre-checks the ceiling, so reaching an exhausted
    // verdict here means the local count was stale (another tab/device).
    try {
      const gate = await loopsApi.consume({
        source_kind: info.sourceKind,
        stem,
        video_id: info.sourceRef,
        surface: 'studio',
        source_url: info.sourceKind === 'url' ? info.sourceRef : '',
        quality: sepEngine === 'roformer' ? 'best' : 'standard',
      });
      if (gate && gate.exhausted) {
        usageApi.me().then(setUsage).catch(() => {});
        setUpgradeAnon(gate.plan === 'anonymous');
        setUpgradeReason('quota');
        setUpgradeOpen(true);
        setMode('input'); // the warm-up already put us in 'working'
        return;
      }
    } catch { /* fail open */ }

    try {
      const { left, right, sampleRate, durationSec } = dec;
      const s0 = Math.max(0, Math.min(w.start, Math.max(0, durationSec - 0.1)));
      const s1 = Math.min(w.end, durationSec);
      const i0 = Math.floor(s0 * sampleRate);
      const i1 = Math.max(i0 + sampleRate, Math.floor(s1 * sampleRate));
      const L = left.subarray(i0, i1);
      const R = right.subarray(i0, i1);
      const windowPeaks = computePeaks(L, R, 120);

      const { stemUrls, emptyStems, solos } = await separateClientSide(
        L, R, sampleRate,
        (frac) => setProgress(Math.round(frac * 100)),
        // Already warmed above; passing the same engine keeps the memoised
        // worker rather than tearing it down for a different model.
        { engine: runEngine },
      );

      const l: Loaded = {
        stemUrls,
        emptyStems,
        peaks: windowPeaks,
        duration: (i1 - i0) / sampleRate,
        goal,
        primaryStem: stem,
        sourceTitle: info.sourceTitle,
        sourceRef: info.sourceRef,
        clientStemsKey: newSeparationKey(),
        solos,
      };
      setLoaded(l);
      setLanePeaks({});
      beginHydrate();
      // Fresh separation = this is now the active record. Also closes a latent
      // race: without this, a slow reopen-analysis from a PREVIOUS track could
      // land its result on this song (the guard compared against a stale key).
      activeSepKeyRef.current = l.clientStemsKey;
      void analyzeChords(l.clientStemsKey, L, R, sampleRate);
      await engine.load(stemUrls);
      engine.setWindow(0, l.duration);

      const gains = mixForGoal(goal, { guide, mutes });
      const stems = Object.fromEntries(
        STEMS.map((s) => [s, { volume: gains[s], solo: false, mute: false }]),
      ) as MixState['stems'];
      const nextMix: MixState = { ...DEFAULT_MIX, stems, loop: true };
      setMix(nextMix);
      engine.applyMix(nextMix);
      const centred = Object.fromEntries(STEMS.map((s) => [s, 0])) as Record<StemName, number>;
      setPans(centred);
      for (const s of STEMS) engine.setPan(s, 0);
      setPos(0);
      setMode('playing');
      setScreen('mixer');
      // Same event name the extension uses on success, so both surfaces land in
      // one funnel and `surface`/`source_kind` split them.
      track('rl_separate', { ...sepBase, seconds: Math.round((Date.now() - sepStartedAt) / 1000) });
      usageApi.me().then(setUsage).catch(() => {});
      void computeLanePeaks(l);
      void persistSession(l, nextMix, centred, { start: 0, end: l.duration });
    } catch (e) {
      setMode('input');
      setErrorMsg((e as Error)?.message || 'Separation failed. Try a shorter section.');
      track('rl_separate_error', {
        ...sepBase,
        reason: sepErrorReason(e),
        err_name: (e as Error)?.name || '',
        seconds: Math.round((Date.now() - sepStartedAt) / 1000),
      });
    }
  }

  // ── survive a refresh ───────────────────────────────────────────────────
  // Stems are cached the moment the mixer opens (not just on Save), the mixer
  // state is written alongside, and the track id goes in the URL — so a reload,
  // or that link later, lands on exactly this mixer. Same-device only: the
  // audio never leaves the browser, so there is nothing to fetch back.
  async function persistSession(l: Loaded, mixNow: MixState, pansNow: Record<StemName, number>, reg: { start: number; end: number }) {
    try {
      const blobs: Partial<Record<StemName, Blob>> = {};
      await Promise.all(
        STEMS.filter((s) => l.stemUrls[s]).map(async (s) => {
          blobs[s] = await fetch(l.stemUrls[s]).then((r) => r.blob());
        }),
      );
      await cacheSeparation(l.clientStemsKey, {
        stems: blobs,
        emptyStems: [...l.emptyStems],
        peaks: l.peaks,
        duration: l.duration,
        sourceRef: l.sourceRef,
        sourceTitle: l.sourceTitle,
        keyBpm: keyBpmRef.current ?? undefined,
        // The original upload, so "Separate tracks" on a reopened session can
        // restore the full-song picker instead of an empty screen. The field
        // existed for exactly this and was never passed. Uploads only: a link
        // capture is just the chosen window, not the song, and would masquerade
        // as one (cacheSeparation dedupes per sourceRef, so this stores once).
        source: file && !ytIdOf(l.sourceRef) && !isSoundCloudRef(l.sourceRef) && !/^https?:/.test(l.sourceRef) ? file : undefined,
      });
      const sess: MixerSession = {
        key: l.clientStemsKey,
        sourceRef: l.sourceRef,
        sourceTitle: l.sourceTitle,
        goal: l.goal,
        primaryStem: l.primaryStem,
        region: reg,
        vols: Object.fromEntries(STEMS.map((s) => [s, mixNow.stems[s].volume])),
        pans: { ...pansNow },
        loop: mixNow.loop,
        speed: mixNow.speed,
        transpose: mixNow.transpose,
        savedAt: Date.now(),
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(sess));
      const url = new URL(window.location.href);
      url.searchParams.set('t', l.clientStemsKey);
      window.history.replaceState(null, '', url.toString());
    } catch { /* a refresh just won't restore — never break the mixer over it */ }
  }

  // Coming back to a loaded mixer means playing again. `openNewSection` flips
  // mode to 'input' so the region picker can audition; without this the mix
  // never re-persisted after a there-and-back. Guarded on 'input' so it can't
  // interrupt a separation in flight ('working').
  useEffect(() => {
    if (screen === 'mixer' && loaded && mode === 'input') setMode('playing');
  }, [screen, loaded, mode]);

  // Keep the stored mix in step with what you're actually hearing, so a reload
  // restores the mix you left rather than the one you started with.
  useEffect(() => {
    if (mode !== 'playing' || !loaded) return;
    const t = window.setTimeout(() => {
      try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return;
        const prev = JSON.parse(raw) as MixerSession;
        if (prev.key !== loaded.clientStemsKey) return;
        localStorage.setItem(SESSION_KEY, JSON.stringify({
          ...prev,
          vols: Object.fromEntries(STEMS.map((s) => [s, mix.stems[s].volume])),
          pans: { ...pans },
          loop: mix.loop,
          speed: mix.speed,
          transpose: mix.transpose,
          region,
          savedAt: Date.now(),
        } satisfies MixerSession));
      } catch { /* quota / private mode */ }
    }, 400);
    return () => window.clearTimeout(t);
  }, [mix, pans, region, mode, loaded]);

  /** Rebuild the mixer from cache on boot when the URL carries a track id. */
  const restoreSession = useCallback(async (explicitKey?: string, arm?: { start: number; end: number; id?: string }) => {
    let key: string | null = explicitKey ?? null;
    if (!key) { try { key = new URL(window.location.href).searchParams.get('t'); } catch { /* ignore */ } }
    if (!key) return false;
    let sess: MixerSession | null = null;
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const p = JSON.parse(raw) as MixerSession;
        if (p.key === key) sess = p;
      }
    } catch { /* ignore */ }

    const rec = await getSeparation(key);
    // /splitter's "Open in the Studio" leaves its key behind; seeing it here
    // means the free doorway landed (after sign-in, if they were anonymous).
    try {
      if (sessionStorage.getItem('rl_splitter_handoff') === key) {
        sessionStorage.removeItem('rl_splitter_handoff');
        funnelEvent('rl_splitter_studio_arrived', { restored: !!rec }, 'studio');
      }
    } catch { /* ignore */ }
    if (!rec) {
      // The id is real but its audio has been evicted — say so instead of
      // silently dropping the user on an empty upload screen.
      setErrorMsg("That track's audio is no longer stored on this device. Separate it again to pick up where you left off.");
      const url = new URL(window.location.href);
      url.searchParams.delete('t');
      window.history.replaceState(null, '', url.toString());
      return false;
    }

    const stemUrls = stemUrlsFrom(rec);
    const l: Loaded = {
      stemUrls,
      emptyStems: new Set(rec.emptyStems),
      peaks: rec.peaks,
      duration: rec.duration,
      goal: sess?.goal || 'studio',
      primaryStem: sess?.primaryStem || 'vocals',
      sourceTitle: sess?.sourceTitle || cleanTrackName(rec.sourceRef),
      sourceRef: rec.sourceRef,
      clientStemsKey: key,
      solos: [],
    };
    setLoaded(l);
    setLanePeaks({});
    beginHydrate();
    activeSepKeyRef.current = key;
    // The record carries the analysis the intake ran; without this the header
    // fell back to "120 BPM, no key" on every reload. Older records lack it —
    // re-analyse from the cached original and persist, so it's one-time.
    applyKeyBpm(rec.keyBpm ?? null);
    if (!rec.keyBpm) void redetectKeyBpm(key, rec.source, rec.sourceRef);
    // Chords restore straight off the record; no re-detect backfill — the
    // cached original is the FULL song while the record's timeline covers the
    // separated window, and the window offsets aren't stored, so a re-run
    // couldn't be aligned. Pre-feature records simply show no chord lane.
    applyChords(chordsEnabled ? rec.chords ?? null : null);
    setChordsBusy(false);
    // Records saved before the chord-derived key existed carry the raw
    // chromagram key — upgrade in place (also persists the fix).
    if (chordsEnabled && rec.chords?.length && rec.keyBpm) upgradeKeyFromChords(key, rec.chords);
    {
      // Grids stored before beat TRACKING existed lack `beats` — treat as
      // absent and recompute from the cached stems.
      const storedGrid = rec.beatGrid?.beats?.length && rec.beatGrid.v === BEAT_GRID_VERSION ? rec.beatGrid : null;
      applyBeatGrid(chordsEnabled ? storedGrid : null);
      void backfillAnalysis(key, () => stemsToStereo(stemUrls), !rec.chords?.length, !storedGrid);
    }
    if (sess?.region) setRegion(sess.region);
    await engine.load(stemUrls);
    engine.setWindow(0, rec.duration);

    const stems = Object.fromEntries(
      STEMS.map((s) => [s, { volume: sess && typeof sess.vols[s] === 'number' ? sess.vols[s] : 1, solo: false, mute: false }]),
    ) as MixState['stems'];
    const nextMix: MixState = {
      ...DEFAULT_MIX,
      stems,
      loop: sess?.loop ?? true,
      speed: sess?.speed ?? 1,
      transpose: sess?.transpose ?? 0,
    };
    setMix(nextMix);
    engine.applyMix(nextMix);
    const p = Object.fromEntries(STEMS.map((s) => [s, sess?.pans?.[s] ?? 0])) as Record<StemName, number>;
    setPans(p);
    for (const s of STEMS) engine.setPan(s, p[s]);
    // Arm the loop we were opened for — AFTER setWindow(0, …), which always seeks
    // to its start. This is the path an exact section key takes, so without it
    // passing that key skipped the arming altogether.
    const armed = arm
      ? {
          a: Math.max(0, Math.min(arm.start, Math.max(0, rec.duration - 0.75))),
          b: Math.min(rec.duration, Math.max(arm.end, Math.min(arm.start, rec.duration) + 0.75)),
        }
      : null;
    if (armed) {
      setArmedId(arm?.id ?? null); // reopened FROM a saved loop → show it as armed
      setAb(armed);
      engine.setBounds(armed.a, armed.b);
      engine.seekAll(armed.a);
    }
    setPos(armed ? armed.a : 0);
    setMode('playing');
    setScreen('mixer');
    void computeLanePeaks(l);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);













  /**
   * Hand-off from the Dashboard's "Continue where you left off" cards, which
   * navigate here with `state.openTrack`. The previous Studio read this; the
   * redesigned one didn't, so those cards dropped you on an empty separator.
   * `reopenTrack` covers both endings: cached stems go straight to the mixer,
   * and a link source with no cache lands on the link tab already filled in.
   */
  // TWO callers, two key names. The dashboard cards send `openTrack`; **Your
  // sections' ↺ has always sent `reopen`** — the legacy Studio read both, this one
  // only ever read `openTrack`, so ↺ silently dropped users on an empty separator
  // (reported by a customer). Read both.
  const routeState = useLocation().state as { openTrack?: Section; reopen?: Section } | null;
  const handedOff = useRef(false);
  useEffect(() => {
    const target = routeState?.reopen || routeState?.openTrack;
    if (!target?.video_id || handedOff.current) return;
    // Wait for the section index, or the cache lookup can't resolve the track.
    if (!savedSections.length) return;
    // For a specific saved loop, wait for the cache index too: without it we would
    // fall back to the NEWEST separation for that track, which is not the one the
    // user clicked.
    if (routeState?.reopen && !sectionIndex.size) return;
    handedOff.current = true;
    const exactKey = routeState?.reopen ? sectionIndex.get(routeState.reopen.id) : undefined;
    // Hand the range to reopenTrack so it arms it at the right moment (see there).
    const arm = routeState?.reopen
      ? { start: routeState.reopen.start, end: routeState.reopen.end, id: routeState.reopen.id }
      : undefined;
    void reopenTrack(target.video_id, exactKey, arm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeState, savedSections, sectionIndex]);

  // (The loop is armed inside reopenTrack now — an effect here raced its
  //  `await engine.load` and got overwritten by setWindow.)

  useEffect(() => {
    void restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Funnel handoff (?handoff=<id>): a tool page on riffloop.app stashed the
   * visitor's picked file in the same-site bridge DB and sent them through
   * sign-in with this id. The legacy Studio claimed it; this one didn't, so
   * every funnel upload landed on an EMPTY separator after login (reported by
   * the owner, 2026-08-31). Claim it once and run the ordinary intake path —
   * the song opens on the setup screen with the goal chooser, exactly as if it
   * had been dropped here, and no separation is spent without the user asking.
   */
  // Guards the claim to exactly once (StrictMode double-invokes effects in
  // dev; the bridge deletes the record on first read, so a second claim would
  // resolve null and overwrite the success with the error message).
  const handoffClaimRef = useRef(false);
  useEffect(() => {
    let handoffId: string | null = null;
    try { handoffId = new URL(window.location.href).searchParams.get('handoff'); } catch { /* ignore */ }
    if (!handoffId || handoffClaimRef.current) return;
    handoffClaimRef.current = true;
    // Strip the param before the async claim: the record is one-shot, so a
    // reload must not try (and fail) to claim it again.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('handoff');
      window.history.replaceState(null, '', url.toString());
    } catch { /* ignore */ }
    void claimHandoff(handoffId).then((file) => {
      if (file) {
        setScreen('setup');
        setTab('upload');
        void pickFile(file);
      } else {
        // Expired, already claimed, or the bridge couldn't be reached. The
        // visitor is on the upload tab either way — tell them instead of
        // leaving the "nothing happened" black hole this replaces.
        setErrorMsg('That song didn’t carry over — choose the file again to load it.');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * `?src=<youtube|soundcloud url>` — the phone→computer hand-off landing.
   *
   * A phone cannot capture tab audio, so the link tabs there offer to send the
   * song's URL to the user's own machine (ContinueOnDesktop). This is the other
   * end of that: the link opens the Studio with the track already in the field
   * and goes straight into the capture flow, so the trip costs one tap rather
   * than a re-paste. Same shape as `reopenTrack`'s link branch, which is why it
   * reuses `autoCapture` rather than inventing a second path.
   *
   * `?d=1` rides along only to mark the arrival as a hand-off; nothing reads it
   * yet. Both params are stripped so a reload isn't a second arrival.
   *
   * Ignored on a device that can't capture anyway — a phone opening its own
   * hand-off link would be walked into the dead end it just escaped.
   */
  const srcClaimRef = useRef(false);
  useEffect(() => {
    if (srcClaimRef.current) return;
    let raw: string | null = null;
    try { raw = new URL(window.location.href).searchParams.get('src'); } catch { /* ignore */ }
    if (!raw) return;
    srcClaimRef.current = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('src');
      url.searchParams.delete('d');
      window.history.replaceState(null, '', url.toString());
    } catch { /* ignore */ }
    if (!tabCaptureSupported()) return;
    // Only shapes we recognise: `src` is attacker-controllable in the sense
    // that anyone can send anyone a link, and it ends up in an <iframe> src,
    // so it must be a YouTube video id or a SoundCloud track URL or nothing.
    const isSc = !!parseSoundCloudUrl(raw);
    const ytId = parseYtVideoId(raw);
    if (!isSc && !ytId) return;
    setScreen('setup');
    setTab(isSc ? 'soundcloud' : 'url');
    setLinkInput(isSc ? raw : `https://www.youtube.com/watch?v=${ytId}`);
    autoCapture.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Leaving the mixer for the setup screen drops the ?t= restore pointer. The
   * URL is the answer to "where am I", so once you're back on the separator it
   * must not still name a track — otherwise a reload drags you into a mixer you
   * deliberately left. Keyed off the transition rather than the current value so
   * the first render (and StrictMode's double-invoke) can't clear the pointer
   * out from under restoreSession on boot.
   */
  const prevScreen = useRef(screen);
  useEffect(() => {
    const from = prevScreen.current;
    prevScreen.current = screen;
    if (screen === 'setup' && from !== 'setup') forgetSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  /**
   * Opening the mixer, or landing a track on the separator, is a "get out of
   * the way" moment — the rail collapses so the waveforms get the width. Fires
   * on the transition only, so if you expand it again afterwards it stays open.
   */
  const prevCollapseTrigger = useRef({ screen, hasFile: !!file });
  useEffect(() => {
    const prev = prevCollapseTrigger.current;
    const hasFile = !!file;
    prevCollapseTrigger.current = { screen, hasFile };
    const enteredMixer = screen === 'mixer' && prev.screen !== 'mixer';
    const gotTrack = hasFile && !prev.hasFile;
    if (enteredMixer || gotTrack) setRailCollapsed(true);
  }, [screen, file]);

  function beginHydrate() {
    hydrateStartRef.current = Date.now();
    setHydrating(true);
  }

  // Hold the skeleton until every audible stem has its waveform, then release.
  // A floor stops it flashing on a fast decode; a ceiling stops a stem that
  // fails to decode from freezing the rack in grey forever.
  useEffect(() => {
    if (!hydrating) return;
    const need = loaded ? STEMS.filter((s) => loaded.stemUrls[s] && !loaded.emptyStems.has(s)) : [];
    const ready = need.length > 0 && need.every((s) => lanePeaks[s]);
    const elapsed = Date.now() - hydrateStartRef.current;
    // Long enough to read as a deliberate load rather than a flicker — the
    // waveforms usually decode faster than that, so this is what you actually
    // see. Ceiling keeps a failed decode from parking the rack in grey.
    const MIN = 1500;
    const MAX = 3200;
    const wait = ready ? Math.max(0, MIN - elapsed) : Math.max(0, MAX - elapsed);
    const t = window.setTimeout(() => setHydrating(false), wait);
    return () => window.clearTimeout(t);
  }, [hydrating, lanePeaks, loaded]);

  // Real per-stem waveforms for the channel lanes. The design draws six
  // distinct shapes, and decorative noise would be a lie about which part is
  // playing where — so decode each stem once, off the critical path, and let
  // the lanes fill in a beat after the mixer opens.
  async function computeLanePeaks(l: Loaded) {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ac = new AC();
    try {
      for (const s of STEMS) {
        const url = l.stemUrls[s];
        if (!url || l.emptyStems.has(s)) continue;
        try {
          const buf = await ac.decodeAudioData(await (await fetch(url)).arrayBuffer());
          const left = buf.getChannelData(0);
          const right = buf.numberOfChannels > 1 ? buf.getChannelData(1) : left;
          // ~10 buckets a second, Moises' own density (their 4:44 canvas was
          // 2583 columns wide). The clamp keeps short clips from looking
          // coarse and long songs from decoding into six 10k-float arrays.
          const buckets = Math.round(Math.min(3200, Math.max(1600, buf.duration * 10)));
          const env = computeEnvelope(left, right, buckets);
          setLanePeaks((prev) => ({ ...prev, [s]: env }));
        } catch { /* one lane stays flat; not worth failing the mixer over */ }
      }
    } finally {
      try { await ac.close(); } catch { /* ignore */ }
    }
  }

  // ── resume a saved track ────────────────────────────────────────────────
  // The stems live in IndexedDB on this device (they're never uploaded), so
  // reopening is a cache lookup, not a re-separation — no credit is spent.
  // Newest section wins; if none of a track's sections still has its audio
  // cached we say so plainly rather than opening a silent mixer.
  async function reopenTrack(videoId: string, sepKey?: string, arm?: { start: number; end: number; id?: string }) {
    if (resuming) return;
    setResuming(true);
    setErrorMsg(null);
    try {
      // The last session points straight at a cached separation. Without this the
      // resume card could only ever restore tracks that had a SAVED LOOP, and
      // anything else fell through to "capture it again" — for the very track you
      // were just working on.
      const directKey = sepKey || (lastSession?.sourceRef === videoId ? lastSession.key : null);
      if (directKey && await restoreSession(directKey, arm)) return;
      const secs = savedSections
        .filter((s) => s.video_id === videoId)
        .sort((a, b) => ((a.updated_at || a.created_at) < (b.updated_at || b.created_at) ? 1 : -1));

      for (const s of secs) {
        const key = sectionIndex.get(s.id);
        if (!key) continue;
        const rec = await getSeparation(key);
        if (!rec) continue;

        stopPreview();
        const stemUrls = stemUrlsFrom(rec);
        const emptyStems = new Set(rec.emptyStems);
        const l: Loaded = {
          stemUrls,
          emptyStems,
          peaks: rec.peaks,
          duration: rec.duration,
          goal: 'studio',
          primaryStem: (s.stem as StemName) || 'vocals',
          sourceTitle: s.title || cleanTrackName(s.video_id),
          sourceRef: s.video_id,
          clientStemsKey: key,
          solos: [],
        };
        setLoaded(l);
        setLanePeaks({});
        beginHydrate();
        activeSepKeyRef.current = key;
        applyKeyBpm(rec.keyBpm ?? null); // see the session-restore twin above
        if (!rec.keyBpm) void redetectKeyBpm(key, rec.source, rec.sourceRef);
        applyChords(chordsEnabled ? rec.chords ?? null : null); // twin of the restore above
        setChordsBusy(false);
        if (chordsEnabled && rec.chords?.length && rec.keyBpm) upgradeKeyFromChords(key, rec.chords);
        {
          // Grids stored before beat TRACKING existed lack `beats` — treat as
          // absent and recompute from the cached stems.
          const storedGrid = rec.beatGrid?.beats?.length && rec.beatGrid.v === BEAT_GRID_VERSION ? rec.beatGrid : null;
          applyBeatGrid(chordsEnabled ? storedGrid : null);
          void backfillAnalysis(key, () => stemsToStereo(stemUrls), !rec.chords?.length, !storedGrid);
        }
        setRegion({ start: s.start, end: s.end });
        await engine.load(stemUrls);
        engine.setWindow(0, rec.duration);

        // Restore the mix this section was saved with; fall back to full band.
        const saved = s.stem_mix || null;
        const stems = Object.fromEntries(
          STEMS.map((st) => [st, { volume: saved && typeof saved[st] === 'number' ? saved[st] : 1, solo: false, mute: false }]),
        ) as MixState['stems'];
        const nextMix: MixState = { ...DEFAULT_MIX, stems, loop: s.loop ?? true };
        setMix(nextMix);
        engine.applyMix(nextMix);
        for (const st of STEMS) engine.setPan(st, 0);
        setPans(Object.fromEntries(STEMS.map((st) => [st, 0])) as Record<StemName, number>);
        // Arm the loop we were opened for, HERE — after `engine.load` and after
        // `setWindow(0, duration)`, which always seeks to its start. Doing it from
        // an effect on `loaded` raced that await and lost: the range was set and
        // then the playhead was yanked back to 0:00, so play ran from the top.
        // setBounds (not setWindow) because the playhead is placed explicitly.
        const armed = arm ? { a: Math.max(0, Math.min(arm.start, Math.max(0, rec.duration - 0.75))), b: Math.min(rec.duration, Math.max(arm.end, Math.min(arm.start, rec.duration) + 0.75)) } : null;
        if (armed) {
          setArmedId(arm?.id ?? null); // same as the restoreSession twin above
          setAb(armed);
          engine.setBounds(armed.a, armed.b);
          engine.seekAll(armed.a);
        }
        setPos(armed ? armed.a : 0);
        setMode('playing');
        setScreen('mixer');
        void computeLanePeaks(l);
        void persistSession(l, nextMix, Object.fromEntries(STEMS.map((st) => [st, 0])) as Record<StemName, number>, { start: s.start, end: s.end });
        return;
      }

      // Nothing cached. A link source can at least be re-captured; an upload
      // needs the original file again.
      //
      // Classify the same way the rest of the app does — `ytIdOf` accepts a
      // BARE 11-char video id, which is how these refs are actually stored.
      // Testing only for an http(s) prefix sent every YouTube song down the
      // "upload the file again" path, which is impossible advice for a link.
      const ytId = ytIdOf(videoId);
      const isUrl = /^https?:\/\//i.test(videoId);
      const isSc = isSoundCloudRef(videoId);
      if (ytId || isUrl) {
        // A bare id needs rebuilding into a watch URL; the link field and its
        // oEmbed lookup both expect something navigable.
        const href = ytId && !isUrl ? `https://www.youtube.com/watch?v=${ytId}` : videoId;
        setTab(isSc ? 'soundcloud' : 'url');
        setLinkInput(href);
        setScreen('setup');
        // Straight through to the goal chooser once the link resolves. Dropping
        // someone on a pre-filled box they still have to click Continue on —
        // under a red banner — is worse than not opening the track at all.
        autoCapture.current = true;
      } else {
        setErrorMsg('That track’s audio isn’t on this device any more. Upload the file again to pick up where you left off.');
      }
    } catch {
      setErrorMsg("Couldn't reopen that track.");
    } finally {
      setResuming(false);
    }
  }

  // ── mixer ───────────────────────────────────────────────────────────────
  // Soloing the click silences the stems at the ENGINE only — `mix` still
  // holds the balance the user built, so every later patch keeps working and
  // releasing S restores it untouched.
  function applyEngineMix(next: MixState, clickSoloed: boolean) {
    engine.applyMix(
      clickSoloed
        ? { ...next, stems: Object.fromEntries(STEMS.map((s) => [s, { ...next.stems[s], mute: true }])) as MixState['stems'] }
        : next,
    );
  }
  function patchMix(next: MixState) {
    setMix(next);
    applyEngineMix(next, metroSolo);
  }
  function setStem(stem: StemName, patch: Partial<MixState['stems'][StemName]>) {
    patchMix({ ...mix, stems: { ...mix.stems, [stem]: { ...mix.stems[stem], ...patch } } });
  }
  function setPan(stem: StemName, v: number) {
    const clamped = Math.max(-1, Math.min(1, v));
    setPans((p) => ({ ...p, [stem]: clamped }));
    engine.setPan(stem, clamped);
  }
  function applyPreset(id: string) {
    const p = MIX_PRESETS.find((x) => x.id === id);
    if (!p) return;
    const stems = Object.fromEntries(
      STEMS.map((s) => [s, { volume: p.mix[s], solo: false, mute: false }]),
    ) as MixState['stems'];
    patchMix({ ...mix, stems });
  }
  function resetMix() {
    if (!loaded) return;
    const gains = mixForGoal(loaded.goal, { guide, mutes });
    const stems = Object.fromEntries(
      STEMS.map((s) => [s, { volume: gains[s], solo: false, mute: false }]),
    ) as MixState['stems'];
    patchMix({ ...mix, stems });
    const centred = Object.fromEntries(STEMS.map((s) => [s, 0])) as Record<StemName, number>;
    setPans(centred);
    for (const s of STEMS) engine.setPan(s, 0);
  }
  const activePreset = useMemo(() => {
    const cur = STEMS.map((s) => mix.stems[s].volume);
    const hit = MIX_PRESETS.find((p) => STEMS.every((s, i) => Math.abs(p.mix[s] - cur[i]) < 0.02));
    return hit?.id || null;
  }, [mix]);

  async function togglePlay() {
    if (mix.playing) {
      engine.pause();
      metro.stop();
      patchMix({ ...mix, playing: false });
      return;
    }
    // Play starts the song immediately — no count-in. The metronome is a
    // channel in the mix, the way the reference app treats it.
    await engine.play();
    if (metroOn && !(metroFree && pos > FREE_METRONOME_SECONDS)) metro.start();
    patchMix({ ...mix, playing: true });
  }

  function toggleMetroSolo() {
    const next = !metroSolo;
    setMetroSolo(next);
    applyEngineMix(mix, next);
    // Soloing a muted channel would be silence — bring the click up with it.
    if (next && !metroOn) {
      setMetroOn(true);
      if (mix.playing) metro.start();
    }
  }

  function toggleMetro() {
    const next = !metroOn;
    setMetroOn(next);
    if (!next) metro.stop();
    else if (mix.playing) metro.start();
  }

  // Output level for the whole mix — the transport's speaker. Deliberately
  // separate from the per-stem faders: muting here doesn't touch the balance
  // the user built, so unmuting brings it back exactly as it was.
  function setMasterVol(v: number) {
    setMasterVolState(v);
    if (v > 0) lastMasterVol.current = v;
    engine.setMasterVolume(v);
  }

  function toggleMasterMute() {
    setMasterVol(masterVol === 0 ? lastMasterVol.current || 1 : 0);
  }

  useEffect(() => {
    if (!sectionsOpen) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setSectionsOpen(false); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [sectionsOpen]);

  // Space toggles playback, the way every player does. Text fields and selects
  // keep the key; on a focused button we preventDefault so the browser's own
  // space-activates-button doesn't fire a second toggle.
  useEffect(() => {
    if (screen !== 'mixer' || !loaded) return;
    function onKey(e: KeyboardEvent) {
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (t?.isContentEditable || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (tag === 'INPUT' && (t as HTMLInputElement).type !== 'range') return;
      e.preventDefault();
      void togglePlay();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, loaded, mix.playing, metroOn]);

  function seekTo(t: number) {
    if (!loaded) return;
    const clamped = Math.max(0, Math.min(loaded.duration, t));
    engine.seekAll(clamped);
    setPos(clamped);
  }

  // The playhead is painted imperatively, not from React state. `pos` comes off
  // the media element's `timeupdate`, which fires ~4×/s — enough for the clock
  // readout but visibly steppy on a moving needle. While playing we poll the
  // element clock from rAF and write straight to the node, so the needle runs at
  // display rate without re-rendering six lanes of waveform sixty times a second.
  // A→B loop range over the SEPARATED section. Off until asked for: markers
  // that are always on would sit over every lane claiming a range nobody set,
  // and `region` (the pre-separation selection) is not one — it is the whole
  // window that got separated, which is why every saved loop read 0:00.
  const [ab, setAb] = useState<{ a: number; b: number } | null>(null);
  const phRef = useRef<HTMLDivElement>(null);
  const paintPh = useCallback((t: number) => {
    const el = phRef.current;
    if (!el || !loaded) return;
    el.style.left = `${Math.max(0, Math.min(100, (t / Math.max(0.01, loaded.duration)) * 100))}%`;
  }, [loaded]);

  // Current-chord highlight + transport readout, painted imperatively off the
  // same clocks as the playhead (chord changes are ~1/s; re-rendering the rack
  // for them would be waste). Reads refs only — safe to call from rAF.
  const paintChord = useCallback((t: number) => {
    const view = chordViewRef.current;
    if (!view) return;
    let idx = -1;
    for (let i = 0; i < view.segs.length; i++) {
      if (t >= view.segs[i].start && t < view.segs[i].end) { idx = i; break; }
    }
    // Progress through the current chord — smooth, so it rides every frame
    // rather than only chord changes. Painted imperatively for the same
    // reason the playhead is.
    const prog = chordProgRef.current;
    if (prog) {
      const seg = idx >= 0 ? view.segs[idx] : null;
      const frac = seg && seg.end > seg.start ? (t - seg.start) / (seg.end - seg.start) : 0;
      prog.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
    }
    if (idx === chordIdxRef.current) return;
    chordIdxRef.current = idx;
    setCurChordIdx(idx);
    const lane = chordLaneRef.current;
    if (lane) {
      lane.querySelectorAll('.sx-chordblk.cur').forEach((n) => n.classList.remove('cur'));
      if (idx >= 0 && view.labels[idx]) lane.querySelector(`[data-ci="${idx}"]`)?.classList.add('cur');
    }
  }, []);

  // Paused or scrubbing: `pos` is the source of truth. The chord highlight
  // additionally follows the ~4 Hz `pos` clock even while playing: rAF is
  // starved entirely in hidden/occluded tabs, and unlike the needle a chord
  // repaint is idempotent and coarse, so the fallback can't fight the rAF path.
  useEffect(() => {
    if (!mix.playing) paintPh(pos);
    paintChord(pos);
  }, [pos, mix.playing, paintPh, paintChord]);

  useEffect(() => {
    if (!mix.playing) return;
    let raf = 0;
    const tick = () => {
      paintPh(engine.currentTime);
      paintChord(engine.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mix.playing, paintPh, paintChord, engine]);

  // What the ribbon (and painter) actually shows: the raw timeline, edges
  // snapped to the drum-derived beat grid when one exists. Stored chords stay
  // raw — snapping is a view, so a better grid later re-snaps for free.
  const displayChords = useMemo(() => {
    if (!chords || !chords.length) return chords;
    // Snap first so the merge threshold below measures beat-aligned lengths,
    // then fold upper-structure blips into their parent chord.
    const snapped = beatGrid ? snapChordsToGrid(chords, beatGrid) : chords;
    return simplifyChords(snapped, beatGrid?.period, keyBpm ? { tonic: keyBpm.tonic, mode: keyBpm.mode } : null);
  }, [chords, beatGrid, keyBpm]);
  // Display labels: spelling follows the (transposed) key — Gm territory says
  // Eb and Bb, never D# and A# — and every label shifts with the transpose
  // stepper so what you read is what you hear.
  const chordLabels = useMemo(() => {
    if (!displayChords) return null;
    const flats = chordsPreferFlats(keyBpm ? { tonic: keyBpm.tonic, mode: keyBpm.mode } : null, mix.transpose);
    return displayChords.map((c) => formatChord(c.label, mix.transpose, flats));
  }, [displayChords, keyBpm, mix.transpose]);
  // The ribbon can only name what it has room for, so it needs its own pixel
  // width. ⚠️ This MUST be a callback ref, not an effect keyed on render state:
  // the lane mounts late (chords land), unmounts in the shapes view and remounts
  // across the mobile/desktop layout switch. An effect whose deps don't happen
  // to change on a remount leaves the observer watching a DETACHED node, the
  // width sticks at 0, and every chord gets named again — which is the crowded
  // ribbon this pass exists to prevent. A callback ref follows the real node.
  const [chordLaneW, setChordLaneW] = useState(0);
  const [chordRowBottom, setChordRowBottom] = useState(0);
  const chordLaneRoRef = useRef<ResizeObserver | null>(null);
  const setChordLaneEl = useCallback((el: HTMLDivElement | null) => {
    chordLaneRef.current = el; // paintChord looks the blocks up through this
    chordLaneRoRef.current?.disconnect();
    if (!el) { chordLaneRoRef.current = null; return; }
    // Read it NOW rather than waiting for the observer's first delivery.
    // ⚠️ ResizeObserver is driven by the rendering steps, so in a hidden or
    // occluded tab it never fires at all — same starvation as rAF (round 3).
    // Relying on its initial callback leaves the width at 0 and every chord
    // gets named. The observer is only here for later resizes.
    const measure = () => setChordLaneW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    chordLaneRoRef.current = ro;
  }, []);

  const tracksRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const lane = chordLaneRef.current;
    const stack = tracksRef.current;
    if (!lane || !stack) { setChordRowBottom(0); return; }
    // Measure to the TOP OF THE STEM LANES rather than the chord lane's bottom
    // plus a guessed margin — the guess put the line 6px inside the first lane.
    const top = stack.getBoundingClientRect().top;
    const firstLane = stack.querySelector('.sx-stem-row');
    const y = firstLane
      ? firstLane.getBoundingClientRect().top - top
      : lane.getBoundingClientRect().bottom - top;
    setChordRowBottom(y > 0 ? y : 0);
  }, [chordLaneW, chordStage, chords, chordsBusy, screen, mode]);

  // Mono, but the fallback may not be — measure rather than assume an advance.
  const measureChordLabel = useMemo(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    if (ctx) ctx.font = '600 12px "JetBrains Mono", ui-monospace, monospace';
    return (s: string) => (ctx ? ctx.measureText(s).width : s.length * 7.2);
  }, []);

  // The ribbon reads as a CHART, not a scale model of the song. Chords at true
  // timeline positions are spaced by the music — on a long track that is ~18px
  // apart no matter how few we draw, so no amount of thinning makes them
  // comfortable. The only way to a readable ribbon is to stop scaling by time:
  // every chord gets the same comfortable pitch, we show one page of them, and
  // "Show more" walks to the next page. Clicking one still seeks to its real
  // time, and the ruler above still carries the song's true shape.
  const [chordPage, setChordPage] = useState(0);
  const chordStrip = useMemo(() => {
    if (!displayChords || !chordLabels || !loaded || chordLaneW <= 0) return null;
    const PITCH = 62;   // per chord — comfortable, measured against the 12px mono
    const MORE_W = 104; // room the "Show more" control needs at the right end
    // The free ceiling is enforced by what enters the pool, so nothing past it
    // can be paged to.
    const pool = displayChords
      .map((c, i) => ({ c, i, label: chordLabels[i] }))
      .filter((x) => !!x.label && x.c.start < chordSpanSec);
    if (!pool.length) return null;
    // If the song's own spacing is already comfortable, leave the ribbon as a
    // true map — short clips never had this problem and shouldn't lose the
    // timeline to fix one long songs have.
    const dur = Math.max(0.01, loaded.duration);
    // "Roomy" means the NAMES clear each other, not that they hit the chart's
    // pitch — the bar is legibility, and a song that already reads fine should
    // keep its timeline rather than be flattened to fix a problem it lacks.
    // ⚠️ This used to be `pool.every(...)`: ONE quick chord change anywhere threw
    // the whole song into chart mode and lost its alignment with the waveform —
    // even when every other chord had room to spare. Count the tight ones
    // instead, and if they are a small minority keep the true timeline and drop
    // only THOSE labels (their change edge stays, so the map is intact). Losing
    // two names beats losing the time of all of them.
    const tight = new Set<number>();
    pool.forEach((x, k) => {
      if (k === 0) return;
      const prev = pool[k - 1];
      const gapPx = ((x.c.start - prev.c.start) / dur) * chordLaneW;
      if (gapPx < measureChordLabel(prev.label as string) + 12) tight.add(prev.i);
    });
    const tolerated = Math.max(1, Math.round(pool.length * 0.25));
    if (tight.size <= tolerated) {
      return { mode: 'time' as const, items: pool, page: 0, pages: 1, per: pool.length, pitch: PITCH, total: pool.length, paged: false, gated: chordsGated, moreX: 0, hide: tight };
    }
    const fitsAll = pool.length * PITCH <= chordLaneW;
    // Gated songs need the room too: their chords stop early and the reason has
    // to sit at the cut, not at the far end of an empty lane.
    const needsMore = !fitsAll || chordsGated;
    const per = Math.max(3, Math.floor((chordLaneW - (needsMore ? MORE_W : 0)) / PITCH));
    const pages = Math.max(1, Math.ceil(pool.length / per));
    const page = Math.min(chordPage, pages - 1);
    const items = pool.slice(page * per, page * per + per);
    // PITCH is a MINIMUM, not a fixed cell width. A page that doesn't fill the
    // lane — the last page, or a whole gated song whose free chords are few —
    // left the chords bunched at the left with most of the lane empty. Spread
    // them across the space that is actually there, capped so three chords don't
    // become three enormous slabs.
    const avail = Math.max(PITCH, chordLaneW - (needsMore ? MORE_W : 0));
    const pitch = Math.min(PITCH * 2.6, Math.max(PITCH, avail / Math.max(1, items.length)));
    return {
      mode: 'chart' as const, items, page, pages, per, pitch, total: pool.length,
      paged: pages > 1, gated: chordsGated, hide: new Set<number>(),
      // Against the last chord, not pinned right: a page that ends halfway down
      // the lane left the control stranded and the cut unexplained.
      moreX: Math.min(items.length * pitch, Math.max(0, chordLaneW - MORE_W)),
    };
  }, [displayChords, chordLabels, loaded, chordLaneW, chordSpanSec, chordPage, measureChordLabel, chordsGated]);

  // A page that never moves would be showing the intro while you play the
  // bridge, so follow the playhead. ⚠️ Key it on the CHORD CHANGING, not on the
  // page differing: the latter re-fires on every render and stamps the page
  // back, so "Show more" appears to do nothing at all. Paging by hand doesn't
  // move the playhead, so it now sticks until the music actually moves on.
  const chordFollowRef = useRef(-2);
  useEffect(() => {
    if (!chordStrip || !chordStrip.paged || !displayChords || !chordLabels) return;
    const at = displayChords.findIndex((c, i) => !!chordLabels[i] && pos >= c.start && pos < c.end);
    if (at < 0 || at === chordFollowRef.current) return;
    chordFollowRef.current = at;
    const rank = displayChords.filter((c, i) => (
      !!chordLabels[i] && c.start < chordSpanSec && c.start < displayChords[at].start
    )).length;
    setChordPage(Math.floor(rank / chordStrip.per));
  }, [pos, chordStrip, displayChords, chordLabels, chordSpanSec]);

  // A new song starts at the beginning again.
  useEffect(() => { setChordPage(0); }, [displayChords]);

  // Turning the page rebuilds the blocks, which throws away the .cur class the
  // painter set imperatively — and the painter early-returns while the index is
  // unchanged, so without this nudge the chord you are on stops highlighting.
  useEffect(() => {
    if (!chordStrip) return;
    chordIdxRef.current = -1;
    paintChord(mix.playing ? engine.currentTime : pos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chordStrip?.page, chordStrip?.mode, paintChord]);

  // Opening the stage mid-song must show the chord under the playhead — the
  // painter early-returns while the index is unchanged, so nudge it.
  useEffect(() => {
    if (!chordStage) return;
    chordIdxRef.current = -1;
    paintChord(mix.playing ? engine.currentTime : pos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chordStage, paintChord]);

  useEffect(() => {
    chordViewRef.current = displayChords && chordLabels ? { segs: displayChords, labels: chordLabels } : null;
    chordIdxRef.current = -1; // force a repaint — spelling may have changed
    if (!chordViewRef.current) setCurChordIdx(-1);
    paintChord(pos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayChords, chordLabels, paintChord]);

  function scrubTo(clientX: number) {
    const el = rulerRef.current;
    if (!el || !loaded) return;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    seekTo(f * loaded.duration);
  }

  // Same px→time mapping the ruler and every lane use, so a handle lands where
  // the playhead would for that pixel.
  function timeAtX(clientX: number) {
    const el = rulerRef.current;
    if (!el || !loaded) return 0;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return f * loaded.duration;
  }

  const AB_MIN = 0.75; // seconds — below this the handles are impossible to grab apart

  /**
   * Slide the whole A→B window, keeping its length. Grabbed by the strip between
   * the two markers in the RULER row, not the band over the lanes: making the
   * band itself grabbable would cost you the ability to scrub inside your own
   * loop, which is the thing you do most while practising.
   */
  /**
   * Clicking the dimmed area outside the range means "I'm done here" — leave the
   * loop (or discard the draft) and go to where you clicked. Without this, a click
   * out there did nothing useful: the engine's bounds snapped the playhead
   * straight back inside, which reads as the app fighting you.
   */
  function exitLoopAt(e: React.PointerEvent) {
    if (e.button !== 0 || !loaded) return;
    e.preventDefault();
    e.stopPropagation();
    const t = Math.max(0, Math.min(timeAtX(e.clientX), loaded.duration));
    setArmedId(null);
    setAb(null);
    // Bounds are handed back to the whole track by the `ab` effect; seek after it
    // so the playhead lands where the click was rather than being clamped.
    window.setTimeout(() => {
      try { engine.seekAll(t); setPos(t); } catch { /* engine not ready */ }
    }, 0);
  }

  function dragABWindow(e: React.PointerEvent, scrubOnClick = false) {
    if (e.button !== 0 || !loaded || !ab) return;
    e.preventDefault();
    e.stopPropagation(); // the ruler underneath would scrub
    const d = loaded.duration;
    const len = ab.b - ab.a;
    // Anchor on where inside the window it was grabbed, so it doesn't jump.
    const grabOffset = timeAtX(e.clientX) - ab.a;
    const startX = e.clientX;
    let dragging = false;
    const move = (ev: PointerEvent) => {
      // Over the BAND a press is ambiguous: move the loop, or scrub inside it?
      // Resolve by intent — nothing moves until the pointer travels a few pixels,
      // so a plain click still falls through to the seek below.
      if (scrubOnClick && !dragging && Math.abs(ev.clientX - startX) < 4) return;
      dragging = true;
      setArmedId(null); // sliding the window is an edit too
      const a = Math.max(0, Math.min(timeAtX(ev.clientX) - grabOffset, d - len));
      setAb({ a, b: a + len });
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (scrubOnClick && !dragging) {
        // A click, not a drag: seek there, exactly as the lanes did before the
        // band started accepting the pointer.
        const t = Math.max(0, Math.min(timeAtX(ev.clientX), d));
        try { engine.seekAll(t); setPos(t); } catch { /* engine not ready */ }
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  function dragAB(which: 'a' | 'b', e: React.PointerEvent) {
    if (e.button !== 0 || !loaded) return;
    e.preventDefault();
    e.stopPropagation(); // the ruler underneath would scrub
    const d = loaded.duration;
    const move = (ev: PointerEvent) => {
      const t = timeAtX(ev.clientX);
      setArmedId(null); // moving a handle turns a saved loop back into a draft
      setAb((cur) => {
        if (!cur) return cur;
        return which === 'a'
          ? { a: Math.max(0, Math.min(t, cur.b - AB_MIN)), b: cur.b }
          : { a: cur.a, b: Math.min(d, Math.max(t, cur.a + AB_MIN)) };
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  // The engine already loops whatever window it is given — the mixer just never
  // gave it one narrower than the whole section. Clearing hands the section back
  // (setBounds, not setWindow: it must not yank the playhead).
  useEffect(() => {
    if (!loaded) return;
    if (ab) engine.setBounds(ab.a, ab.b);
    else engine.setBounds(0, loaded.duration);
  }, [ab, loaded, engine]);

  // Press anywhere on the ruler (or grab the flag) to scrub. Playback isn't
  // interrupted: seeking while playing keeps playing, exactly like the DAW
  // behaviour we're mirroring.
  function startScrub(e: React.PointerEvent) {
    if (e.button !== 0) return;
    e.preventDefault();
    setScrubbing(true);
    scrubTo(e.clientX);
    const move = (ev: PointerEvent) => scrubTo(ev.clientX);
    const up = () => {
      setScrubbing(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  // ── export (our real gating) ────────────────────────────────────────────
  // Free-plan trim for the current section: undefined for Pro, and for sections
  // that already fit inside the preview window (nothing would be cut).
  function previewTrim() {
    if (isPro || !loaded || loaded.duration <= FREE_EXPORT_PREVIEW_SECONDS) return undefined;
    return { window: { start: 0, end: FREE_EXPORT_PREVIEW_SECONDS }, fadeOutSec: PREVIEW_FADE_SECONDS };
  }
  function exportName(label: string, fmt: ExportFormat) {
    const tag = previewTrim() ? ` (${FREE_EXPORT_PREVIEW_SECONDS}s preview)` : '';
    return `${safeBaseName(loaded?.sourceRef || '')} - ${label}${tag}.${FORMAT_EXT[fmt]}`;
  }
  // Shared params so every export event is sliceable by plan and section length.
  // Shared shape for the separation funnel (start → done → error). Named to
  // match what the extension sends (tier / stem / source_kind / quality) so the
  // two surfaces sit in one comparable funnel; `surface` splits them apart.
  function sepEventBase(info: SrcInfo, stem: StemName, winSec: number) {
    return {
      surface: 'studio',
      tier: !user ? 'anonymous' : isPro ? 'pro' : 'free',
      stem,
      source_kind: info.sourceKind,
      quality: sepEngine === 'roformer' ? 'best' : 'standard',
      clip_seconds: Number.isFinite(winSec) ? Math.round(winSec) : 0,
    };
  }
  function exportEventBase() {
    return {
      plan: isPro ? 'pro' : 'free',
      section_len_sec: loaded ? Math.round(loaded.duration) : undefined,
      source_kind: loadedIsLink ? 'url' : 'upload',
    };
  }
  function requestExport(target: ExportTarget) {
    if (!loaded || !user) return;
    // Same rule as the live Studio: streamed sources can be practised with but
    // never exported, regardless of plan.
    if (loadedIsLink) {
      setErrorMsg('Streamed tracks can be practised with, but not exported.');
      setExportOpen(false);
      return;
    }
    // Free WAV never proceeds — the format chip is locked, but guard anyway.
    if (!isPro && format === 'wav') {
      funnelEvent('rl_export_gate_shown', { ...exportEventBase(), kind: 'wav', target }, 'studio');
      setGate({ kind: 'wav' });
      setExportOpen(false);
      return;
    }
    // Everything the user needs to see before this file exists, resolved in one
    // pass. The trim notice fires on EVERY constrained export (a shortened file
    // should never arrive unannounced); the rights attestation is once per
    // account. When both apply they share a single card.
    const trimmed = !!previewTrim();
    const needsRights = !user.download_acknowledged_at;
    if (trimmed || needsRights) {
      funnelEvent('rl_export_gate_shown', {
        ...exportEventBase(),
        kind: trimmed ? 'preview' : 'rights',
        target,
        format,
        // Last on purpose: the server mirror keeps only the first six scalars,
        // and `rights` is the one worth losing — `kind` already implies it.
        rights: needsRights,
      }, 'studio');
      setGate({ kind: 'export', target, format, trimmed, needsRights });
      setExportOpen(false);
      return;
    }
    // The plan-awareness notice — only ever ALONE, never stacked on another
    // facet: a trimmed export already carries the cap + upgrade CTA (saying it
    // twice), and the rights attestation is a legal moment marketing must not
    // muddy (that card fires once per account; this one simply waits for the
    // next export). Marked seen at SHOW time, so closing the card without
    // exporting doesn't re-arm it.
    if (!isPro && !sessionStorage.getItem(PLAN_NOTICE_KEY)) {
      try { sessionStorage.setItem(PLAN_NOTICE_KEY, '1'); } catch { /* private mode */ }
      funnelEvent('rl_export_gate_shown', { ...exportEventBase(), kind: 'plan', target, format }, 'studio');
      setGate({
        kind: 'export', target, format, trimmed: false, needsRights: false,
        planNotice: true, secLen: loaded ? loaded.duration : 0,
      });
      setExportOpen(false);
      return;
    }
    proceedExport(target, format);
  }
  // Runs the export itself. Reached only once every gate has been passed.
  function proceedExport(target: ExportTarget, fmt: ExportFormat) {
    if (target === 'all') void runExportAll(fmt);
    else void runExport(target, fmt);
  }
  // Render one channel to a Blob. 'metronome' is generated from the tempo grid;
  // everything else is summed from the separated stems.
  async function renderTarget(target: 'mix' | 'metronome' | StemName, fmt: ExportFormat): Promise<Blob> {
    const l = loaded!;
    const trim = previewTrim();
    if (target === 'metronome') {
      const buf = await renderClickTrack({
        durationSec: trim ? Math.min(l.duration, FREE_EXPORT_PREVIEW_SECONDS) : l.duration,
        bpm,
        beatsPerBar,
      });
      return encodeBuffer(buf, fmt);
    }
    let gains: Record<string, number>;
    if (target === 'mix') {
      const anySoloNow = STEMS.some((s) => mix.stems[s].solo);
      gains = {};
      for (const s of STEMS) {
        if (l.emptyStems.has(s)) continue;
        gains[s] = audibleVolume(mix.stems[s], anySoloNow);
      }
    } else {
      gains = { [target]: 1 };
    }
    return renderAndEncode(l.stemUrls, gains, fmt, trim);
  }
  function targetLabel(target: 'mix' | 'metronome' | StemName) {
    if (target === 'mix') return 'Mix';
    if (target === 'metronome') return 'Metronome';
    return STEM_META[target]?.label || target;
  }
  async function runExport(target: 'mix' | 'metronome' | StemName, fmt: ExportFormat) {
    if (!loaded || exporting) return;
    setExporting(true);
    setExportOpen(false);
    const trimmed = !!previewTrim();
    try {
      const blob = await renderTarget(target, fmt);
      saveBlob(blob, exportName(targetLabel(target), fmt));
      funnelEvent(trimmed ? 'rl_export_preview' : 'rl_export_done', {
        ...exportEventBase(), target, format: fmt, files: 1,
      }, 'studio');
    } catch (e) {
      setErrorMsg((e as Error)?.message || 'Export failed.');
      funnelEvent('rl_export_failed', { ...exportEventBase(), target, format: fmt }, 'studio');
    } finally {
      setExporting(false);
    }
  }
  // One file per channel, rendered sequentially so memory stays flat and the
  // browser registers each download separately.
  async function runExportAll(fmt: ExportFormat) {
    if (!loaded || exporting) return;
    setExporting(true);
    setExportOpen(false);
    const trimmed = !!previewTrim();
    const targets: ('metronome' | StemName)[] = STEMS.filter(
      (s) => loaded.stemUrls[s] && !loaded.emptyStems.has(s),
    );
    if (metroOn) targets.push('metronome');
    try {
      for (let i = 0; i < targets.length; i++) {
        setExportProg(`Exporting ${i + 1}/${targets.length}…`);
        const blob = await renderTarget(targets[i], fmt);
        saveBlob(blob, exportName(targetLabel(targets[i]), fmt));
        await new Promise((r) => setTimeout(r, 350));
      }
      funnelEvent(trimmed ? 'rl_export_preview' : 'rl_export_done', {
        ...exportEventBase(), target: 'all', format: fmt, files: targets.length,
      }, 'studio');
    } catch (e) {
      setErrorMsg((e as Error)?.message || 'Export failed.');
      funnelEvent('rl_export_failed', { ...exportEventBase(), target: 'all', format: fmt }, 'studio');
    } finally {
      setExporting(false);
      setExportProg(null);
    }
  }

  // ── save a separated loop (real backend row + local stem cache) ─────────
  async function saveLoop(nameOverride?: string) {
    if (!loaded || !user) return;
    const id = `sec_${Date.now().toString(36)}`;
    const range = ab ? { start: ab.a, end: ab.b } : region;
    const stemMix = Object.fromEntries(STEMS.map((s) => [s, mix.stems[s].volume]));
    const row: Partial<Section> = {
      id,
      source_kind: /^https?:\/\//i.test(loaded.sourceRef) ? 'url' : 'upload',
      video_id: loaded.sourceRef,
      title: loaded.sourceTitle,
      label: nameOverride || goalDef(loaded.goal).title,
      stem: loaded.primaryStem,
      // The A→B range if one was set, otherwise the whole separated window.
      start: range.start,
      end: range.end,
      stem_mix: stemMix,
      loop: mix.loop,
    };
    try {
      // 200 does NOT mean saved: the free plan caps brand-new loops per song and the
      // server reports that as `blocked`, not an error. Treating the response as
      // success flashed "Saved", cleared the range and left nothing behind — the
      // loop simply vanished, with no hint that a limit had been reached.
      const res = await sectionsApi.sync([row]);
      // (naming happens on the range before saving — see .sx-abname)
      if (res?.blocked) {
        // A ceiling, not a fault: the dialog explains it and offers the upgrade,
        // the way every other limit in here does. A→B stays exactly where it is,
        // so the placement isn't lost while they decide.
        setLoopCap(res.cap ?? 3);
        setUpgradeAnon(!user);
        setUpgradeReason('loopcap');
        setUpgradeOpen(true);
        return;
      }
      // Cache the audio locally so the row can be reopened and actually play.
      const blobs: Partial<Record<StemName, Blob>> = {};
      await Promise.all(
        STEMS.filter((s) => loaded.stemUrls[s]).map(async (s) => {
          blobs[s] = await fetch(loaded.stemUrls[s]).then((r) => r.blob());
        }),
      );
      await cacheSeparation(loaded.clientStemsKey, {
        stems: blobs,
        emptyStems: [...loaded.emptyStems],
        peaks: loaded.peaks,
        duration: loaded.duration,
        sourceRef: loaded.sourceRef,
        sourceTitle: loaded.sourceTitle,
        keyBpm: keyBpmRef.current ?? undefined,
      });
      await linkSection(loaded.clientStemsKey, id);
      sectionsApi.list().then(setSavedSections).catch(() => {});
      flashSaved();
      setDraftName('');
      // The range has done its job — put the lanes back and offer the next loop.
      // Held for the flash so "✓ Saved" is actually seen; clearing on the same
      // tick unmounts the pill before it can confirm anything. Only on SUCCESS:
      // a failed save must leave the markers exactly where they were, or the
      // work of placing them is lost along with the save.
      window.setTimeout(() => setAb(null), 1600);
    } catch {
      setErrorMsg("Couldn't save that loop. Check your connection and try again.");
    }
  }

  /** Loop pending delete confirmation, and the loop being renamed. */
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  /** Which chip we've already focused, so re-renders don't re-select mid-typing. */
  const renameFocused = useRef<string | null>(null);
  const confirmTimer = useRef<number | null>(null);

  /** Arm the ×. Auto-disarms, so a stray click can't leave a chip primed to delete. */
  function armDelete(id: string) {
    setRenaming(null);
    setConfirmDel(id);
    if (confirmTimer.current) window.clearTimeout(confirmTimer.current);
    confirmTimer.current = window.setTimeout(() => setConfirmDel(null), 4000);
  }
  function disarmDelete() {
    if (confirmTimer.current) window.clearTimeout(confirmTimer.current);
    setConfirmDel(null);
  }

  /** Strip the start time older rows baked into the label. */
  const loopName = (s: Section) => s.label.replace(/\s+\d+:\d{2}$/, '');

  /**
   * Rename a saved loop. There is no rename endpoint, but /api/sections/sync is an
   * upsert (`ON CONFLICT(id) DO UPDATE SET label=excluded.label, …`), so syncing the
   * WHOLE row back with a new label renames it. Sending a partial row here would
   * blank the other columns, since the update reads every `excluded.*` value.
   */
  async function commitRename(sec: Section, raw: string) {
    setRenaming(null);
    renameFocused.current = null;
    const value = raw.trim().slice(0, 80);
    if (!value || value === loopName(sec)) return;
    const next = { ...sec, label: value };
    setSavedSections((rows) => rows.map((r) => (r.id === sec.id ? next : r)));
    try {
      await sectionsApi.sync([next]);
    } catch {
      setSavedSections((rows) => rows.map((r) => (r.id === sec.id ? sec : r))); // put it back
      setErrorMsg("Couldn't rename that loop.");
    }
  }

  async function removeSection(id: string) {
    try {
      await sectionsApi.remove(id);
      setSavedSections((rows) => rows.filter((r) => r.id !== id));
    } catch {
      setErrorMsg("Couldn't delete that loop.");
    }
  }

  function removeRawLoop(id: string) {
    const next = rawLoops.filter((r) => r.id !== id);
    setRawLoops(next);
    try { localStorage.setItem(RAW_LOOPS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }

  // ── derived ─────────────────────────────────────────────────────────────
  const remaining = usage?.daily_quota == null ? null : Math.max(0, (usage.daily_quota ?? 0) - (usage.today ?? 0));
  /**
   * WHICH saved loop is armed — tracked explicitly, never inferred from the
   * coordinates. Comparing A/B against saved ranges looked equivalent and wasn't:
   * "+ New loop" starts at the playhead and runs 8s, so parking on a saved loop's
   * start and adding a new one produced an IDENTICAL range, and the fresh draft
   * was mistaken for the saved loop — green/Save never appeared. Intent is not
   * recoverable from geometry, so it is stored.
   */
  const [armedId, setArmedId] = useState<string | null>(null);
  const loopIsActive = (id: string) => armedId === id;

  /**
   * Click a saved loop to arm it. The engine already loops whatever bounds it is
   * given, so this only has to set the A→B range and drop the playhead at A —
   * the existing `ab` effect does the rest. Deliberately does NOT restore the
   * loop's saved stem mix: that would silently throw away the faders the user is
   * working with, and they asked for the range, not a session restore.
   */
  function activateLoop(st: number, en: number, id?: string) {
    if (!loaded) return;
    setArmedId(id ?? null);
    const a = Math.max(0, Math.min(st, Math.max(0, loaded.duration - 0.75)));
    const b = Math.min(loaded.duration, Math.max(en, a + 0.75));
    setAb({ a, b });
    // The range alone does NOT loop: the engine wraps at B only when its `loop`
    // flag is on (it is a separate transport toggle), so arming a saved loop with
    // looping off played straight past B. "+ New loop" already does this — the
    // same has to happen here.
    if (!mix.loop) patchMix({ ...mix, loop: true });
    // Clicking a chip in the mixer races nothing — the stems are already loaded.
    try { engine.seekAll(a); setPos(a); } catch { /* engine not ready */ }
    // …and play it. Selecting a loop and then having to find the transport is a
    // step nobody wants; the point of picking a loop is to hear it.
    if (!mix.playing) {
      void (async () => {
        try {
          await engine.play();
          if (metroOn && !(metroFree && a > FREE_METRONOME_SECONDS)) metro.start();
          patchMix({ ...mix, playing: true, loop: true });
        } catch { /* autoplay blocked — the transport still works */ }
      })();
    }
  }

  /** The saved loop the current A→B *is*, if it is one. Drives the armed look. */
  const armedSaved = useMemo(
    () => (ab && armedId ? savedSections.find((s) => s.id === armedId) || null : null),
    [ab, armedId, savedSections],
  );

  // Escape leaves the loop: the keyboard counterpart to clicking outside the range,
  // or to the ✕ on the badge. Ignored while typing, and while a delete is armed or a
  // rename is open, where Escape already means "cancel that".
  useEffect(() => {
    if (screen !== 'mixer' || !ab) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (renaming || confirmDel) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (t?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      setArmedId(null);
      setAb(null);
      setDraftName('');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [screen, ab, renaming, confirmDel]);


  const thisSongLoops = useMemo(
    () => (loaded ? savedSections.filter((s) => s.video_id === loaded.sourceRef) : []),
    [savedSections, loaded],
  );
  /**
   * The list belongs to whichever track is on screen — the loaded stems in the
   * mixer, whatever's queued on the separator. Keying it off `file` was wrong
   * twice over: after a reload `file` is null, so it fell through to every loop
   * from every song (those unnamed rows from tracks you can't identify), and on
   * the YouTube path it never matched at all, because a capture becomes
   * "<title>.webm" while the loop you saved beforehand stored the URL.
   * `sourceRef` is the one key both kinds are written with.
   */
  const activeRef = screen === 'mixer'
    ? loaded?.sourceRef ?? null
    : (usingLink ? linkMeta!.url : file?.name) ?? null;
  const railRows = useMemo(() => {
    if (!activeRef) {
      // Nothing picked yet: the most recent across every song, and the rows
      // name their song so they aren't bare timestamps.
      return { raw: rawLoops.slice(-3).reverse(), cut: savedSections.slice(0, 6), scoped: false };
    }
    return {
      raw: rawLoops.filter((r) => r.sourceRef === activeRef),
      cut: savedSections.filter((s) => s.video_id === activeRef),
      scoped: true,
    };
  }, [rawLoops, savedSections, activeRef]);
  const sectionCount = railRows.raw.length + railRows.cut.length;

  // "Where you left off" lives in the SESSION record, not in saved loops — the
  // library below is built from loops, so a track you separated and mixed but
  // never saved a loop for was invisible here. That is most tracks.
  const [lastSession, setLastSession] = useState<MixerSession | null>(null);
  const [seps, setSeps] = useState<{ key: string; sourceRef: string; sourceTitle?: string; createdAt: number; duration: number }[]>([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      setLastSession(raw ? (JSON.parse(raw) as MixerSession) : null);
    } catch { setLastSession(null); }
    // The separations cache outlives the session record (which holds only the
    // most recent one) and every one of them is a track the user split to
    // stems, saved loop or not.
    listSeparations().then(setSeps).catch(() => setSeps([]));
  }, [screen, file]);

  // Saved-loop rows carry server timestamps, raw loops carry ISO strings and the
  // session carries epoch ms. Comparing those as strings sorted a MySQL-style
  // "2026-08-18 14:22" BEFORE an ISO "2026-08-18T14:22" every time, because a
  // space sorts under "T" — so normalise to a number and compare that.
  const tsOf = (v: string) => {
    const n = Date.parse(/^\d{4}-\d\d-\d\d \d/.test(v) ? v.replace(' ', 'T') : v);
    return Number.isFinite(n) ? n : 0;
  };

  const library = useMemo(() => {
    const by = new Map<string, { title: string; kind: string; loops: number; last: string; sepKey?: string }>();
    for (const s of savedSections) {
      const k = s.video_id;
      const cur = by.get(k);
      const when = s.updated_at || s.created_at;
      if (cur) {
        cur.loops += 1;
        if (when > cur.last) cur.last = when;
      } else {
        by.set(k, { title: s.title || cleanTrackName(s.video_id), kind: s.source_kind === 'url' ? 'YouTube' : 'Upload', loops: 1, last: when });
      }
    }
    for (const r of rawLoops) {
      const cur = by.get(r.sourceRef);
      if (cur) { cur.loops += 1; continue; }
      // Classify by the ref itself: a raw loop saved against a link source is
      // still a link source, and showing a bare URL as the title reads as junk.
      const isUrl = /^https?:\/\//i.test(r.sourceRef);
      const isSc = isSoundCloudRef(r.sourceRef);
      by.set(r.sourceRef, {
        title: isUrl ? (ytIdOf(r.sourceRef) ? 'YouTube clip' : 'Streamed track') : cleanTrackName(r.sourceRef),
        kind: isUrl ? (isSc ? 'SoundCloud' : 'YouTube') : 'Upload',
        loops: 1,
        last: new Date(r.createdAt).toISOString(),
      });
    }
    // Newest first, so the first record seen for a ref is the one to reopen.
    for (const sp of seps) {
      if (!sp.sourceRef) continue;
      const when = new Date(sp.createdAt).toISOString();
      const cur = by.get(sp.sourceRef);
      if (cur) {
        if (!cur.sepKey) cur.sepKey = sp.key;
        if (tsOf(when) > tsOf(cur.last)) cur.last = when;
        if (!cur.title && sp.sourceTitle) cur.title = sp.sourceTitle;
      } else {
        const isUrl = /^https?:\/\//i.test(sp.sourceRef) || !!ytIdOf(sp.sourceRef);
        by.set(sp.sourceRef, {
          title: sp.sourceTitle || (isUrl ? (ytIdOf(sp.sourceRef) ? 'YouTube clip' : 'Streamed track') : cleanTrackName(sp.sourceRef)),
          kind: isUrl ? (isSoundCloudRef(sp.sourceRef) ? 'SoundCloud' : 'YouTube') : 'Upload',
          loops: 0,
          last: when,
          sepKey: sp.key,
        });
      }
    }
    if (lastSession?.sourceRef) {
      const when = new Date(lastSession.savedAt).toISOString();
      const cur = by.get(lastSession.sourceRef);
      if (cur) {
        if (tsOf(when) > tsOf(cur.last)) cur.last = when;
        if (lastSession.key) cur.sepKey = lastSession.key;
        if (lastSession.sourceTitle) cur.title = lastSession.sourceTitle;
      } else {
        const isUrl = /^https?:\/\//i.test(lastSession.sourceRef) || !!ytIdOf(lastSession.sourceRef);
        by.set(lastSession.sourceRef, {
          title: lastSession.sourceTitle || cleanTrackName(lastSession.sourceRef),
          kind: isUrl ? (isSoundCloudRef(lastSession.sourceRef) ? 'SoundCloud' : 'YouTube') : 'Upload',
          loops: 0, // separated and mixed, nothing saved from it yet
          last: when,
          sepKey: lastSession.key,
        });
      }
    }
    // Of the four sources above only `seps` proves the stems are still in
    // IndexedDB: savedSections come from the SERVER (so they exist on a device
    // that never held the audio), and rawLoops and the session record live
    // outside that store, so all three outlive an evicted cache. An upload whose
    // stems are gone cannot be reopened at all — the original file isn't
    // recoverable — so offering it is a button that can only report failure.
    // Link sources stay either way: with no cache `reopenTrack` re-opens capture,
    // which works and spends no credit.
    const cachedRefs = new Set(seps.map((s) => s.sourceRef).filter(Boolean));
    const cachedKeys = new Set(seps.map((s) => s.key));
    const resumable = (e: { key: string; kind: string; sepKey?: string }) =>
      e.kind !== 'Upload' || cachedRefs.has(e.key) || (!!e.sepKey && cachedKeys.has(e.sepKey));
    return [...by.entries()]
      .map(([k, v]) => ({ key: k, ...v }))
      .filter(resumable)
      .sort((a, b) => tsOf(b.last) - tsOf(a.last));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSections, rawLoops, lastSession, seps]);

  const anySolo = STEMS.some((s) => mix.stems[s].solo);
  const savedTotal = savedSections.length + rawLoops.length;
  const loadedIsLink = !!loaded && /^https?:\/\//i.test(loaded.sourceRef);


  // No PCM exists before a capture, so the picker's waveform is decorative —
  // seeded off the track ref so it's at least stable for a given song.
  const abBars = useMemo(
    () => seededBars(90, hashSeed(linkMeta?.ref || 'riffloop')),
    [linkMeta],
  );

  // The goal chooser is identical for uploads and link captures — same copy,
  // same behaviour — so it is built once and rendered into both panels.
  const goalChooser = (
    <>
  {/* GOAL CHOOSER */}
  <div className="sx-goal-head">
    <h2 className="sx-goal-h2">What do you want to do with this song?</h2>
  </div>

  {collapsed ? (
    <div>
      <div className="sx-chips">
        {GOALS.map((g) => (
          <button key={g.key} className={`sx-chip${g.key === goal ? ' on' : ''}`} onClick={() => setGoal(g.key)}>
            <Ico d={g.icon} size={15} sw={2} />
            {g.title}
          </button>
        ))}
      </div>
      <div className="sx-chips-desc">{gd.desc}</div>
    </div>
  ) : (
    <div className="sx-goals">
      {GOALS.map((g) => (
        <button
          key={g.key}
          className={`sx-goal${g.key === goal ? ' on' : ''}`}
          onClick={() => setGoal(g.key)}
          aria-pressed={g.key === goal}
        >
          {/* Icon beside the title rather than stacked above it: the same
              card reads in one line instead of three, which is what buys the
              section picker its room on screen. */}
          <span className="sx-goal-top">
            <span className="sx-goal-ico"><Ico d={g.icon} size={17} /></span>
            <span className="sx-goal-t">{g.title}</span>
            <span className="sx-goal-mark" aria-hidden>{Icons.check({ size: 13 })}</span>
          </span>
          {/* The card's promise has to match what this device returns. Only
              the two goals whose wording implies more than two parts are
              rewritten; Loop and Remove vocals are true on both. */}
          <span className="sx-goal-d">
            {onPhone && g.key === 'studio' ? 'Vocals and instrumental, mixed by you.'
              : onPhone && g.key === 'backing' ? 'Drop the vocal and play over the band.'
              : g.desc}
          </span>
        </button>
      ))}
    </div>
  )}

  {/* per-goal detail */}
  {/* A segmented pick, not a switch on a full-width bar. Two reasons: this
      screen already answers "one of N" with `.sx-seg` (Quality, the source
      tabs), so a lone toggle was a second vocabulary for the same job; and
      "Guide vocals — Off" was genuinely ambiguous — Off could read as "no
      guide vocal" (i.e. removed) or "the feature is off". Naming both states
      removes the inference entirely. */}
  {goal === 'karaoke' && (
    <div className="sx-guide">
      <span className="sx-lbl">Vocals</span>
      <div className="sx-seg sx-seg-sm">
        <button className={!guide ? 'on' : ''} onClick={() => setGuide(false)}>Fully removed</button>
        <button className={guide ? 'on' : ''} onClick={() => setGuide(true)}>Quiet guide</button>
      </div>
    </div>
  )}

  {goal === 'backing' && (
    <div className="sx-mutes">
      <div className="sx-steph" style={{ marginBottom: 10 }}>
        {onPhone ? 'What should we mute?' : 'Which instruments should we mute?'}
      </div>
      {/* Chips, not tiles: six full-width rows cost more vertical room than the
          whole section picker. The per-stem marks are the same ones the mixer
          uses, so an instrument looks the same everywhere — the old emoji set
          gave bass and guitar the identical 🎸. */}
      {/* ⚠️ A PHONE RUNS A 2-STEM ENGINE (vendor/mobile-tier.js): it returns
          vocals and everything else, and nothing more. Offering Drums / Bass /
          Piano / Guitar there is not a cosmetic problem — the pick SPENDS a
          separation and comes back silent, which is the failure this picker
          has quietly shipped since the v2 cutover. So the chips are the ones
          the engine can honour, and the reason is stated rather than left to
          be discovered. Desktop is untouched: `onPhone` is false there and the
          full six render exactly as before. */}
      <div className="sx-mchips">
        {(onPhone ? STEM_UI.filter((s) => s.id === 'vocals') : STEM_UI).map((s) => {
          const on = mutes.includes(s.id);
          return (
            <button
              key={s.id}
              className={`sx-mchip${on ? ' on' : ''}`}
              style={{ ['--tc' as string]: s.color }}
              aria-pressed={on}
              title={on ? `${s.name} will be muted — click to keep it` : `Mute ${s.name.toLowerCase()}`}
              onClick={() => {
                // Never empty: deselecting the last one is a no-op,
                // and the order is canonical so the CTA label is stable.
                const next = new Set(mutes);
                if (next.has(s.id)) { if (next.size > 1) next.delete(s.id); }
                else next.add(s.id);
                setMutes(STEMS.filter((x) => next.has(x)));
              }}
            >
              <span className="sx-mchip-i">{stemMark[s.id](s.color)}</span>
              {s.name}
            </button>
          );
        })}
      </div>
      {onPhone && (
        <ContinueOnDesktop
          variant="inline"
          handoff={{ kind: 'file' }}
          title="Mute any instrument on a computer"
          body="This phone separates a song into vocals and instrumental, so vocals is the part it can mute. Drums, bass, piano and guitar are separated on a computer."
          shareText="Open this on your computer to mute any instrument on Riffloop."
          onShown={() => funnelEvent('rl_desktop_bridge_shown', { where: 'mute', kind: 'file', tier: isPro ? 'pro' : 'free' }, 'studio')}
          onSent={(how) => funnelEvent('rl_desktop_bridge_send', { where: 'mute', how, kind: 'file', tier: isPro ? 'pro' : 'free' }, 'studio')}
        />
      )}
    </div>
  )}
    </>
  );

  // ── working-card progress ───────────────────────────────────────────────
  // The engine reports ONE 0-100 scale across two very different jobs: 0-70 is
  // downloading/building the engine, 70-99 is the actual separation. Once the
  // engine is cached — which it is on every run after the first — the warm-up
  // finishes instantly and the bar appeared to *start* at 70%. Each phase now
  // gets its own 0-100, so "Separating audio" begins where you'd expect.
  const workPhase = (() => {
    if (progress < 70) {
      return { label: 'Setting up — first run takes a bit…', pct: Math.round((progress / 70) * 100) };
    }
    const sep = Math.min(100, Math.round(((progress - 70) / 29) * 100));
    return { label: sep >= 99 ? 'Finishing up…' : 'Separating audio…', pct: sep };
  })();

  // ── the setup dock ──────────────────────────────────────────────────────
  // Once a source is loaded, the setup screen focuses down to the choice
  // itself and this bar takes over the bottom: file status on the left,
  // quality where it matters, and THE action on the right — always in view.
  const focusMode =
    screen === 'setup' && mode !== 'working' &&
    ((tab === 'upload' && !!file) || (tab !== 'upload' && captureOpen));
  const showDock =
    focusMode && !capturing &&
    (tab === 'upload' || (!!linkMeta && captureOk && link.duration > 0));

  // On a phone the dock is a FIXED bar rather than a sticky one (sticky floated
  // it over the goal cards the user was being asked to choose from), so the
  // scrolling column has to reserve its height or the last card would sit
  // under it forever. It wraps to two or three rows depending on which copy is
  // showing, so the height is measured rather than guessed, and published as
  // --sx-dock-h. Desktop keeps the sticky dock and never reads the variable.
  // ⚠️ 860px is the studio-next.css breakpoint. The variable is read by exactly
  // one rule, inside `@media (max-width: 860px)`, so above the breakpoint this
  // whole effect is dead weight — gated rather than merely inert, so nothing
  // new runs on a desktop at all. If that breakpoint moves, move this with it.
  const dockRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const clear = () => root.style.removeProperty('--sx-dock-h');
    const el = dockRef.current;
    if (!el) { clear(); return; }

    const mq = window.matchMedia('(max-width: 860px)');
    const publish = () => root.style.setProperty('--sx-dock-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    let ro: ResizeObserver | null = null;
    // Not a one-shot read: a desktop window dragged across the breakpoint has
    // to pick the dock up (and drop it again on the way back), or the reserved
    // space would be stale in whichever direction it was resized.
    const sync = () => {
      if (mq.matches && !ro) {
        publish();
        ro = new ResizeObserver(publish);
        ro.observe(el);
      } else if (!mq.matches && ro) {
        ro.disconnect();
        ro = null;
        clear();
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
      ro?.disconnect();
      clear();
    };
  }, [showDock]);

  // The source title already heads the step a few hundred pixels up — on the
  // link tabs, and now on Upload too — so repeating it here spends the dock's
  // best slot on a duplicate. Report the thing that actually changes as you
  // work: the selection you are about to send off.
  const dockTitle = `Section ${fmtTime(region.start)} – ${fmtTime(region.end)}`;
  // One source of truth for the loaded file's stats — the upload step's header
  // and its dock both show them, and they must never drift.
  const fileMeta = file
    ? `${fmtTime(duration)} · ${fmtBytes(file.size)}${
      analysing ? ' · Analyzing key & tempo…'
        : keyBpm ? ` · ${keyBpm.keyConfidence < 0.5 ? '~' : ''}${keyBpm.keyShort} · ${keyBpm.bpmConfidence < 0.5 ? '~' : ''}${keyBpm.bpm} BPM` : ''
    }`
    : '';
  const dockTotal = tab !== 'upload'
    ? (link.duration > 0 ? fmtTime(link.duration) : '—')
    : (duration > 0 ? fmtTime(duration) : '—');
  const dockMeta = `${fmtTime(regionLen)} of ${dockTotal}`;

  const setupDock = showDock ? (
    <div className="sx-setupdock" ref={dockRef}>
      <div className="sx-dock-file">
        <span className="sx-dock-check" aria-hidden>✓</span>
        <div className="sx-dock-meta">
          <b>{dockTitle}</b>
          <span className="sx-mono">{dockMeta}</span>
        </div>
      </div>
      <span className="sx-spacer" />
      <span className="sx-lbl">Quality</span>
      <div className="sx-seg sx-seg-sm">
        <button className={sepEngine === 'demucs' ? 'on' : ''} onClick={() => setSepEngine('demucs')}>Standard</button>
        <button
          className={sepEngine === 'roformer' ? 'on' : ''}
          onClick={() => {
            if (isPro) { setSepEngine('roformer'); return; }
            setUpgradeReason('quality');
            setUpgradeOpen(true);
          }}
        >
          Best {!isPro && '(Pro)'}
        </button>
      </div>
      {/* Only once Best is actually chosen, and only while its engine is still
          missing — otherwise this is noise for everyone who already has it. */}
      {sepEngine === 'roformer' && isPro && !bestEngineReady && (
        <span className="sx-qual-note">
          Best needs a larger one-time setup — a few minutes on first use, then it is instant.
        </span>
      )}
      {/* Came back by mistake? The stems from this session are still loaded, and
          the CTA beside this one SPENDS A SEPARATION — so the way back has to be
          visible right here, not only in the rail. Only shown when there is
          actually something to return to. */}
      {loaded && (
        <button
          className="sx-dock-back"
          onClick={() => setScreen('mixer')}
          title="Return to the stems you already separated — this costs nothing"
        >
          ← Go back to stems
        </button>
      )}
      <button
        className="sx-cta"
        onClick={onCta}
        disabled={tab !== 'upload' ? regionLen < MIN_REGION : regionLen < 1}
        title={goal === 'loop' ? HINTS.loop : HINTS[goal]}
      >
        {savedFlash && goal === 'loop' ? '✓ Loop saved' : ctaLabel} <span style={{ opacity: 0.7 }}>→</span>
      </button>
    </div>
  ) : null;

  // ── nav ─────────────────────────────────────────────────────────────────
  const navItems: { id: Screen; icon: string; label: string; badge?: string; disabled?: boolean }[] = [
    { id: 'setup', icon: 'M4 12h10M10 7l5 5-5 5M17 5v14', label: 'Stem Separator' },
    { id: 'mixer', icon: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6', label: 'Mixer', badge: loaded ? '6' : undefined, disabled: !loaded },
    { id: 'library', icon: 'M9 18V6l10-2v12M6 20.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM16 18.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z', label: 'Your songs', badge: library.length ? String(library.length) : undefined },
  ];

  // The player bar spans the whole main column and sticks to the bottom of
  // the viewport, so it reads as part of the app shell rather than a card
  // floating over the rack. Built here and rendered as the last child of
  // <main>, outside .sxm-body, so nothing inside the rack can clip it.
  // One definition, mounted in BOTH the lanes rack and the chords column —
  // the click is part of the mix wherever you're looking at it.
  const metroStrip = (
                          <div className={`sx-strip metro${metroOn ? '' : ' dim'}`}>
                            <div className="sx-strip-top">
                              <button className={`sx-ms${metroOn ? '' : ' m-on'}`} onClick={toggleMetro} title={metroOn ? 'Mute the click' : 'Unmute the click'}>M</button>
                              <button className={`sx-ms${metroSolo ? ' s-on' : ''}`} onClick={toggleMetroSolo} title="Solo the click">S</button>
                              <span className="sx-strip-n">Metronome{metroOn && metroPastFree && <i className="sx-silent"> · first minute only</i>}</span>
                              <span className="sx-pct sx-mono">{bpm}</span>
                            </div>
                            <input
                              className="sx-vol"
                              type="range" min={0} max={100} value={Math.round(metroVol * 100)}
                              onChange={(e) => {
                                const v = Number(e.target.value) / 100;
                                setMetroVol(v);
                                try { localStorage.setItem('rl_metro_vol_v1', String(v)); } catch { /* private mode */ }
                              }}
                              aria-label="Metronome volume"
                              title={`Click volume · ${Math.round(metroVol * 100)}%`}
                            />
                          </div>
  );

  const transportBar = loaded && screen === 'mixer' ? (
          <div className="sx-transport">
            <div className="sx-tp-row">
              <div className="sx-volwrap">
                <button
                  className={`sx-tbtn${masterVol === 0 ? ' off' : ''}`}
                  onClick={toggleMasterMute}
                  title={masterVol === 0 ? 'Unmute' : 'Mute everything'}
                  aria-label={masterVol === 0 ? 'Unmute' : 'Mute everything'}
                >
                  {Tx.volume(masterVol === 0)}
                </button>
                <input
                  className="sx-volpop"
                  type="range" min={0} max={100} value={Math.round(masterVol * 100)}
                  onChange={(e) => setMasterVol(Number(e.target.value) / 100)}
                  aria-label="Output volume"
                />
              </div>
              <button className="sx-tbtn" onClick={() => seekTo(pos - 10)} title="Back 10s" aria-label="Back 10 seconds">{Tx.back()}</button>
              <button className="sx-tbtn play" onClick={togglePlay} title="Play / pause (space)" aria-label={mix.playing ? 'Pause' : 'Play'}>
                {mix.playing ? Tx.pause() : Tx.play()}
              </button>
              <button className="sx-tbtn" onClick={() => seekTo(pos + 10)} title="Forward 10s" aria-label="Forward 10 seconds">{Tx.fwd()}</button>
              <button
                className={`sx-tbtn${mix.loop ? ' on' : ''}`}
                onClick={() => patchMix({ ...mix, loop: !mix.loop })}
                title={mix.loop ? 'Looping the section' : 'Play straight through'}
                aria-label="Loop"
                aria-pressed={mix.loop}
              >
                {Tx.loop()}
              </button>
              {/* Bottom-right of the player, beside the centred
                  transport — where the reference keeps its panel
                  toggles. */}
              {chordsEnabled && chords && chords.length > 0 && (
                <button
                  className={`sx-tp-chords${chordStage ? ' on' : ''}`}
                  onClick={() => setChordStage((v) => !v)}
                  aria-expanded={chordStage}
                  title={chordStage ? 'Back to the stem lanes' : 'Show chord shapes'}
                >
                  {Tx.chords()}
                  {/* Wrapped so the phone can drop the word and keep the icon —
                      the two labels are what forced this row to wrap into three. */}
                  <span className="sx-tp-lbl">Chords</span>
                </button>
              )}
              <button
                className={`sx-tp-panel${sectionsOpen ? ' on' : ''}`}
                onClick={() => setSectionsOpen((v) => !v)}
                aria-expanded={sectionsOpen}
              >
                {Tx.loops()}
                <span className="sx-tp-lbl">Loops</span>
                {sectionCount > 0 && <i className="sx-tp-count sx-mono">{sectionCount}</i>}
              </button>
            </div>
            <div className="sx-scrub">
              <span className="sx-mono">{fmtTime(pos)}</span>
              <input
                type="range" min={0} max={Math.max(0.01, loaded.duration)} step={0.01} value={pos}
                onChange={(e) => seekTo(Number(e.target.value))}
                aria-label="Seek"
              />
              <span className="sx-mono dim">{fmtTime(loaded.duration)}</span>
            </div>
          </div>
  ) : null;

  return (
    <AppShell
      rootClass={sectionsOpen ? 'drawer-open' : ''}
      quota={{ remaining: remaining ?? null, dailyQuota: usage?.daily_quota ?? 5 }}
      groups={[
        // Mirrors RailPage's headingless first item so the rail keeps the same
        // shape whether you're in the Studio or anywhere else. `to` rather than
        // onClick: this one leaves the Studio.
        {
          label: '',
          items: [
            {
              id: 'dashboard',
              icon: 'M4 5.5A1.5 1.5 0 015.5 4h13A1.5 1.5 0 0120 5.5v13a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 18.5zM4 10h16M10 10v10',
              label: 'Dashboard',
              to: '/dashboard',
            },
          ],
        },
        {
          label: 'Products',
          items: navItems.slice(0, 2).map((n) => ({
            id: n.id,
            icon: n.icon,
            label: n.label,
            badge: n.badge,
            active: screen === n.id,
            disabled: n.disabled,
            title: n.disabled ? 'Separate a section first' : n.label,
            onClick: () => setScreen(n.id),
          })),
        },
        {
          label: 'Library',
          items: [
            {
              id: 'songs', icon: navItems[2].icon, label: 'Your songs', badge: navItems[2].badge,
              active: screen === 'library', onClick: () => setScreen('library'),
            },
            {
              // Same destination as every other rail on the site. It used to
              // land on the songs table here and the loops grid everywhere
              // else — one label, two different pages.
              id: 'loops', icon: GOALS[0].icon, label: 'Saved loops',
              badge: savedTotal > 0 ? String(savedTotal) : undefined,
              to: '/sections',
            },
          ],
        },
      ]}
    >
        {/* On the mixer the column is height-constrained and only the lanes
            scroll, so the rack's own header and footer rows stay on screen.
            Every other screen keeps a normally scrolling column. */}
        <div className={`sxm-body${screen === 'mixer' ? ' pinned' : ''}`}>
          {errorMsg && (
            <div className="sx-err">
              <span>{errorMsg}</span>
              <button onClick={() => setErrorMsg(null)} aria-label="Dismiss">×</button>
            </div>
          )}

          {screen === 'library' ? (
            <LibraryView rows={library} onOpen={(k) => void reopenTrack(k)} busy={resuming} />
          ) : screen === 'mixer' ? (
            !loaded ? (
              <div className="sx-panel">
                <div className="sx-work">
                  <div className="sx-work-t">Nothing in the mixer yet</div>
                  <p className="sx-work-s" style={{ maxWidth: 420 }}>
                    Pick a section and run one of the separation goals — the six-track mixer opens here.
                  </p>
                  <button className="sx-btn-primary" onClick={() => setScreen('setup')}>Go to the Stem Separator →</button>
                </div>
              </div>
            ) : (
              <>
                {/* ── RESULT HEADER ─────────────────────────────────── */}
                <div className="sx-rhead">
                  {/* Goes back to the separator with THIS SONG still loaded —
                      openNewSection restores the decoded source (or re-opens
                      the link capture). A bare setScreen('setup') dropped you
                      on an empty uploader with the track you were working on
                      nowhere in sight. */}
                  <button
                    className="sx-icobtn"
                    onClick={() => { void openNewSection(() => setScreen('library')); }}
                    title="Back to the separator — keeps this song loaded"
                  >
                    ←
                  </button>
                  <div className="sx-thumb sm">{Icons.music({ size: 18 })}</div>
                  <ClampedTitle className="sx-rtitle" text={loaded.sourceTitle} />
                  <span className="sx-spacer" />

                  {/* PHONE ONLY (display:none until the 860 block). Everything
                      after this collapses into a "Song options" sheet, so the
                      header is back + title + ⋮ and the faders get the screen —
                      the header was 180px of a 812px phone. `.sx-rtools` is
                      `display: contents` on desktop, so the bar there keeps the
                      exact same flex children it has always had. */}
                  <button
                    className="sx-rtools-btn"
                    onClick={() => setToolsOpen((v) => !v)}
                    aria-label="Song options"
                    aria-expanded={toolsOpen}
                  >
                    <Ico d={I_DOTS} size={18} sw={2} />
                  </button>
                  {toolsOpen && <div className="sx-rtools-back" onClick={() => setToolsOpen(false)} />}

                  <div className={`sx-rtools${toolsOpen ? ' open' : ''}`}>
                    <div className="sx-rtools-t">Song options</div>

                  <button
                    className="sx-tool"
                    onClick={() => { void openNewSection(); }}
                    title={isPro ? undefined : `${remaining ?? 0} of ${usage?.daily_quota ?? 5} free separations left today`}
                  >
                    <Ico d={navItems[0].icon} size={15} sw={1.8} /> Separate tracks
                    {/* The quota rides the button that spends it. As bare header
                        text it read as stray words between the chips (owner's
                        call, 2026-08-15); here "3 left" answers "can I run this
                        again?" exactly where that question is asked. */}
                    {!isPro && (
                      <span className={`sx-sep-count${(remaining ?? 0) <= 0 ? ' out' : ''}`}>
                        {remaining ?? '—'} left
                      </span>
                    )}
                  </button>

                  <label className="sx-tool sx-mono" title="Tempo — drives the metronome">
                    <MetaIcon kind="tempo" />
                    <input
                      className="sx-bpm"
                      type="number" min={40} max={240} value={bpm}
                      onChange={(e) => setBpm(Math.max(40, Math.min(240, Number(e.target.value) || 0)))}
                      aria-label="Tempo in BPM"
                    />
                    <span className="sx-tool-u">BPM</span>
                  </label>

                  {keyBpm && (
                    <span className="sx-tool sx-mono" title="Detected key">
                      <MetaIcon kind="key" />
                      {keyBpm.keyConfidence < 0.5 ? '~' : ''}{keyBpm.keyShort}
                    </span>
                  )}

                  {/* The chip frame is the label; the select goes bare inside it.
                      A <select> can't contain an SVG, so the icon has to live
                      beside it rather than within. */}
                  <label className="sx-tool sx-mono" title="Time signature — sets where the metronome accents">
                  <MetaIcon kind="meter" />
                  <select
                    className="sx-selbare sx-mono"
                    value={beatsPerBar}
                    onChange={(e) => setBeatsPerBar(Number(e.target.value))}
                    aria-label="Time signature"
                  >
                    <option value={4}>4/4</option>
                    <option value={3}>3/4</option>
                    <option value={6}>6/8</option>
                    <option value={5}>5/4</option>
                  </select>
                  </label>


                  <div style={{ position: 'relative' }} ref={exportWrapRef}>
                    <button className="sx-btn-primary" onClick={() => setExportOpen((v) => !v)} disabled={exporting}>
                      {exporting ? (exportProg ?? 'Exporting…') : '⤓ Export'}
                    </button>
                    {exportOpen && (
                      <div className="sx-menu">
                        {/* Moises-shaped panel: format → channels → all → mix.
                            Uploads only + free trimmed to a preview stay OUR
                            rules; the plan gate fires on action, not as a dead
                            menu (requestExport). */}
                        <div className="sx-export-t">Export</div>
                        <div className="sx-menu-lbl" style={{ marginTop: 0 }}>File format</div>
                        <div className="sx-seg sx-seg-sm" style={{ marginBottom: 4, width: '100%' }}>
                          {(['mp3', 'm4a', 'wav'] as ExportFormat[]).map((f) => {
                            const locked = f === 'wav' && !isPro;
                            return (
                              <button
                                key={f}
                                className={`${format === f ? 'on' : ''}${locked ? ' locked' : ''}`}
                                style={{ flex: 1, justifyContent: 'center' }}
                                disabled={f === 'm4a' && !m4aOk}
                                title={locked ? 'WAV export is a Pro feature' : undefined}
                                onClick={() => {
                                  if (!locked) { setFormat(f); return; }
                                  funnelEvent('rl_export_gate_shown', { ...exportEventBase(), kind: 'wav', target: 'format_chip' }, 'studio');
                                  setGate({ kind: 'wav' });
                                  setExportOpen(false);
                                }}
                              >
                                {locked && <Ico d={I_LOCK} size={11} sw={2.2} />}
                                {f.toUpperCase()}
                              </button>
                            );
                          })}
                        </div>
                        <div className="sx-menu-lbl">Channels</div>
                        {STEM_UI.filter((s) => !loaded.emptyStems.has(s.id)).map((s) => (
                          <button key={s.id} className="sx-menu-row" onClick={() => requestExport(s.id)}>
                            <span className="sx-chico" style={{ color: s.color }}>{stemMark[s.id](s.color)}</span>
                            {s.name}
                          </button>
                        ))}
                        {/* The click track is generated, not separated — but it
                            lines up with the stems, so it exports like a channel. */}
                        <button
                          className="sx-menu-row"
                          title={`Click track at ${bpm} BPM, ${beatsPerBar}/4`}
                          onClick={() => requestExport('metronome')}
                        >
                          <span className="sx-chico" style={{ color: 'var(--sx-text-3)' }}>
                            <Ico d={I_METRO} size={19} sw={1.7} />
                          </span>
                          Metronome
                          <span className="sx-spacer" />
                          <span className="sx-mono" style={{ fontSize: 11.5, color: 'var(--sx-text-3)' }}>{bpm}</span>
                        </button>
                        <button className="sx-menu-row primary" style={{ marginTop: 6 }} onClick={() => requestExport('all')}>
                          <Ico d={I_DL} size={14} sw={2} />
                          Export all channels
                        </button>
                        <div className="sx-menu-lbl">Audio mix</div>
                        <button
                          className="sx-menu-row"
                          title="Exports the current mixer balance — mutes, solos and levels"
                          onClick={() => requestExport('mix')}
                        >
                          <span className="sx-chico" style={{ color: 'var(--sx-accent)' }}>
                            <Ico d={I_MIX} size={19} sw={1.7} />
                          </span>
                          Export mix
                          <span className="sx-spacer" />
                          <span className="sx-mono" style={{ fontSize: 11.5, color: 'var(--sx-text-3)' }}>{format.toUpperCase()}</span>
                        </button>
                        {!isPro && (
                          <p className="sx-export-note">
                            Free exports include the first {FREE_EXPORT_PREVIEW_SECONDS} seconds.{' '}
                            <Link
                              to="/pricing"
                              onClick={() => funnelEvent('rl_export_upgrade_click', { ...exportEventBase(), kind: 'panel_note' }, 'studio')}
                            >
                              Upgrade →
                            </Link>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  </div>{/* /.sx-rtools */}
                </div>

                <div className="sx-cols">
                  {/* ── CHANNEL RACK ──────────────────────────────── */}
                  <div className={`sx-rack${hydrating ? ' hydrating' : ''}${chordStage ? ' chords' : ''}`} aria-busy={hydrating}>
                    <div className="sx-rack-head">
                      <span className="sx-lbl">Presets</span>
                      {MIX_PRESETS.map((p) => (
                        <button key={p.id} className={`sx-pill${activePreset === p.id ? ' on' : ''}`} onClick={() => applyPreset(p.id)}>
                          {p.label}
                        </button>
                      ))}
                      <span className="sx-spacer" />
                      {loaded.solos.map((s, i) => (
                        <button
                          key={i}
                          className="sx-tag-solo"
                          style={{ ['--sc' as string]: STEM_COLOR[soloStem(s.label)] || '#fbbf24' }}
                          onClick={() => saveLoop(s.label)}
                          title="Save this as a named loop"
                        >
                          {s.label} · detected
                        </button>
                      ))}
                    </div>

                    {/* Ruler + lanes share one positioning context so a single playhead
                        can span the whole stack, the way the reference scrubber does.
                        The chords view replaces this entirely — the reference shows
                        only the shapes there, no timeline. */}
                    {!chordStage && (
                    <div className="sx-tracks" ref={tracksRef}>
                      {/* ruler */}
                      <div className="sx-ch sx-ruler-row">
                        <div /><div />
                        <div
                          className="sx-ruler"
                          ref={rulerRef}
                          onPointerDown={startScrub}
                          title="Click or drag to move the playhead"
                        />
                      </div>

                      {/* chords lane — beta, account-gated */}
                      {chordsEnabled && (chords !== null || chordsBusy) && (
                        <div className="sx-ch sx-chord-row">
                          <div className="sx-chordstrip">
                            <span className="sx-strip-n">Chords</span>
                            {/* The chart is not time-scaled, so a positional padlock
                                had nowhere honest to sit — it ended up at the end of
                                the strip, no longer level with the click row's lock,
                                and only once you had paged that far. The limit is a
                                fact about the plan, not a place in the song, so it
                                is stated here and visible from the start. */}
                            {chordsGated && (
                              <button
                                className="sx-chordfree"
                                onClick={() => { setUpgradeAnon(!user); setUpgradeReason('chords'); setUpgradeOpen(true); }}
                                title={`Chords cover the first ${FREE_CHORDS_SECONDS} seconds on the free plan — Pro reads the whole song`}
                              >
                                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" />
                                  <path d="M8 10.5V7a4 4 0 018 0v3.5" />
                                </svg>
                                First {FREE_CHORDS_SECONDS}s
                              </button>
                            )}
                            {/* The row that shows chords is where someone
                                wonders what else chords can do, so the way
                                into the shapes view lives here rather than
                                only in the transport. A static "beta" badge
                                was occupying the one spot with intent. */}
                            <button
                              className="sx-chordadv"
                              onClick={() => setChordStage(true)}
                              title="Chord shapes — diagrams for every chord in the song"
                            >
                              Advanced
                              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M9 6l6 6-6 6" />
                              </svg>
                            </button>
                          </div>
                          <div />
                          <div className="sx-chordlane" ref={setChordLaneEl}>
                            {chordsBusy && !chords && <span className="sx-chord-wait">Listening for chords…</span>}
                            {!chordsBusy && chords && chords.length === 0 && <span className="sx-chord-wait">No chords heard in this section</span>}
                            {/* Roomy songs keep the true map: block width IS the
                                chord's duration, and the INDEX stays raw so
                                paintChord's data-ci lookup still matches. */}
                            {chordStrip?.mode === 'time' && chordStrip.items.map((x) => {
                              const dur = Math.max(0.01, loaded.duration);
                              const end = Math.min(x.c.end, chordSpanSec);
                              const w = ((end - x.c.start) / dur) * 100;
                              if (w < 0.4) return null;
                              return (
                                <button
                                  key={x.i}
                                  data-ci={x.i}
                                  className="sx-chordblk sx-mono"
                                  style={{ left: `${(x.c.start / dur) * 100}%`, width: `${w}%` }}
                                  onClick={() => seekTo(x.c.start + 0.01)}
                                  title={`Jump to ${x.label}`}
                                >
                                  {!chordStrip.hide.has(x.i) && <span>{x.label}</span>}
                                </button>
                              );
                            })}
                            {/* One page of the chart: even pitch, so every name is
                                legible no matter how tight the song's own chord
                                rhythm is. The time lives in the tooltip and in where
                                clicking takes you. */}
                            {chordStrip?.mode === 'chart' && chordStrip.items.map((x, j) => (
                              <button
                                key={x.i}
                                data-ci={x.i}
                                className="sx-chordblk sx-chordblk-fix sx-mono"
                                style={{ left: `${j * chordStrip.pitch}px`, width: `${chordStrip.pitch}px` }}
                                onClick={() => seekTo(x.c.start + 0.01)}
                                title={`Jump to ${x.label} at ${fmtTime(x.c.start)}`}
                              >
                                <span>{x.label}</span>
                              </button>
                            ))}
                            {chordStrip?.mode === 'chart' && (chordStrip.paged || chordStrip.gated) && (
                              <div className="sx-chordpager" style={{ left: `${chordStrip.moreX}px` }}>
                                {/* Sits where the chords stop, so the cut explains
                                    itself. Gated = the plan ended them, and the CTA
                                    belongs on that; otherwise "more" means the full
                                    chord view (paging by hand was a worse answer than
                                    the view that shows every chord with its shape). */}
                                {chordStrip.gated ? (
                                  <button
                                    className="sx-chordpage-m locked"
                                    onClick={() => { setUpgradeAnon(!user); setUpgradeReason('chords'); setUpgradeOpen(true); }}
                                    title={`Chords stop at ${FREE_CHORDS_SECONDS} seconds on the free plan — Pro reads the whole song`}
                                  >
                                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                      <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" />
                                      <path d="M8 10.5V7a4 4 0 018 0v3.5" />
                                    </svg>
                                    See more
                                  </button>
                                ) : (
                                  <button
                                    className="sx-chordpage-m"
                                    onClick={() => setChordStage(true)}
                                    title="Every chord in the song, with shapes"
                                  >
                                    Show more &#8250;
                                  </button>
                                )}
                              </div>
                            )}
                            {/* Time mode only: the padlock marks a real point on the
                                timeline, which a chart page has no equivalent of. The
                                gate itself holds either way — chords past the ceiling
                                never enter the pool. */}
                            {chordsGated && chordStrip?.mode === 'time' && (
                              <button
                                className="sx-lockedtail"
                                style={{ left: `${(chordSpanSec / Math.max(0.01, loaded.duration)) * 100}%` }}
                                onClick={() => { setUpgradeAnon(!user); setUpgradeReason('chords'); setUpgradeOpen(true); }}
                                aria-label={`Chords stop after ${FREE_CHORDS_SECONDS} seconds — Pro reads the whole song`}
                                title="Pro — chords for the whole song"
                              >
                                {LockGlyph}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* channels */}
                      {STEM_UI.map((s, si) => {
                        const st = mix.stems[s.id];
                        const empty = loaded.emptyStems.has(s.id);
                        const dim = metroSolo || st.mute || (anySolo && !st.solo) || empty;
                        const env = lanePeaks[s.id];
                        // One hue, one ramp: flat tone per lane, 12% lighter each
                        // step, so the six of them read as a single gradient down
                        // the rack. 12 rather than the 9 it started at — the
                        // earlier spread was real on paper and invisible on
                        // screen. Still just the accent; no second colour.
                        const laneTone = `${si * 12}%`;
                        return (
                          // `empty` marks a lane with nothing to hear: either the model
                          // never produced it (a 2-stem run gives vocals + other and
                          // no pair for the rest) or it came back silent. Desktop keeps
                          // showing it greyed with "· silent"; the phone hides it, so a
                          // 2-stem separation reads as two faders instead of two plus
                          // four dead ones. The Export menu has always filtered the
                          // same set.
                          <div key={s.id} className={`sx-ch sx-stem-row${empty ? ' empty' : ''}${panOpen === s.id ? ' panopen' : ''}`}>
                            <div className={`sx-strip${dim ? ' dim' : ''}`} style={{ ['--mc' as string]: s.color }}>
                              <div className="sx-strip-top">
                                <button className={`sx-ms${st.mute ? ' m-on' : ''}`} onClick={() => setStem(s.id, { mute: !st.mute })} title="Mute">M</button>
                                <button className={`sx-ms${st.solo ? ' s-on' : ''}`} onClick={() => setStem(s.id, { solo: !st.solo })} title="Solo">S</button>
                                <span className="sx-strip-n">{stemLabel(s.id, s.name)}{empty && <i className="sx-silent"> · silent</i>}</span>
                                <span className="sx-pct sx-mono">{Math.round(st.volume * 100)}%</span>
                                {/* PHONE ONLY (display:none until the 860 block). The pan
                                    knob + "L R" is a whole 62px row per stem — six of them
                                    pushed four faders off a phone screen — and it is the
                                    one control you rarely reach for. Tucked behind this,
                                    the way Moises tucks "L & R" behind its row menu. */}
                                <button
                                  className="sx-panbtn"
                                  onClick={() => setPanOpen((v) => (v === s.id ? null : s.id))}
                                  aria-label={`${s.name} left/right balance`}
                                  aria-expanded={panOpen === s.id}
                                >
                                  <Ico d={I_DOTS} size={16} sw={2} />
                                </button>
                              </div>
                              <input
                                className="sx-vol"
                                type="range" min={0} max={100} value={Math.round(st.volume * 100)}
                                onChange={(e) => setStem(s.id, { volume: Number(e.target.value) / 100 })}
                                aria-label={`${s.name} volume`}
                              />
                            </div>

                            <div className="sx-pan">
                              <input
                                className="sx-knob"
                                type="range" min={-100} max={100} value={Math.round(pans[s.id] * 100)}
                                onChange={(e) => setPan(s.id, Number(e.target.value) / 100)}
                                aria-label={`${s.name} pan`}
                                title={pans[s.id] === 0 ? 'Centre' : `${pans[s.id] < 0 ? 'L' : 'R'}${Math.abs(Math.round(pans[s.id] * 100))}`}
                                style={{ ['--rot' as string]: `${pans[s.id] * 135}deg` }}
                              />
                              <div className="sx-pan-lr sx-mono"><span>L</span><span>R</span></div>
                            </div>

                            <div
                              className={`sx-lane${dim ? ' dim' : ''}`}
                              style={{ ['--laneTone' as string]: laneTone }}
                            >
                              {/* Midline only while the envelope hasn't landed:
                                  it's the loading skeleton. With data, silence
                                  is clean pill — the trace itself carries the
                                  lane, same as Moises. */}
                              {env ? <LaneWave env={env} dim={dim} /> : <span className="sx-lane-mid" />}
                              <span className="sx-lane-ph" style={{ left: `${(pos / Math.max(0.01, loaded.duration)) * 100}%` }} />
                            </div>
                          </div>
                        );
                      })}

                      {/* metronome lane */}
                      <div className="sx-ch">
                        {metroStrip}
                        <div />
                        <div className={`sx-clicklane${metroOn ? '' : ' dim'}`}>
                          {/* Zero line first, marks over it — the reference
                              runs a continuous line through the click track so
                              it reads as a track rather than loose ticks. */}
                          <span className="sx-lane-mid" />
                          <ClickLane beats={clickMarks.beats} accents={clickMarks.accents} clicks={clickTimes} duration={loaded.duration} maxTime={laneSpanSec} dim={!metroOn} />
                          <div className="sx-clickchips">
                            {[0.5, 1, 2].map((v) => (
                              <button
                                key={v}
                                className={`sx-subdiv sx-mono${clickSubdiv === v ? ' on' : ''}`}
                                onClick={() => setClickSubdiv(v)}
                                title={v === 0.5 ? 'Click every other beat' : v === 2 ? 'Click twice per beat' : 'Click on every beat'}
                                aria-pressed={clickSubdiv === v}
                              >
                                {v}×
                              </button>
                            ))}
                          </div>
                          {/* Not while the row is gated: the tail padlock is the
                              lock that matters there, and two padlocks on one
                              lane read as a bug. */}
                          {isPro && !metroFree && (
                          <button
                            className={`sx-clicklock${gridLocked ? ' on' : ''}`}
                            onClick={() => {
                              // Following the song's own beats is the Pro half
                              // of the metronome, and the lock is where that
                              // gets discovered — so it explains itself rather
                              // than silently doing nothing.
                              if (!isPro) {
                                setUpgradeAnon(!user);
                                setUpgradeReason('metronome');
                                setUpgradeOpen(true);
                                return;
                              }
                              setGridLock((v) => !v);
                            }}
                            disabled={isPro && !gridReady}
                            aria-pressed={gridLocked}
                            aria-label={isPro ? "Lock the click to the song's beats" : "Locking the click to the song's beats is a Pro feature"}
                            title={!isPro ? "Pro — lock the click to the song's own beats" : !gridReady ? 'No tracked beats for this song yet' : gridLocked ? "Locked to the song's own beats" : 'Free-running at the BPM chip'}
                          >
                            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <rect x="4" y="10.5" width="16" height="10.5" rx="2.2" />
                              <path d={gridLocked ? 'M8 10.5V7a4 4 0 018 0v3.5' : 'M8 10.5V7a4 4 0 017.6-1.7'} />
                            </svg>
                          </button>
                          )}
                        {/* Same treatment as the chord row, at the same second,
                            so the two padlocks line up down the rack. */}
                        {metroFree && loaded.duration > laneSpanSec + 0.5 && (
                          <button
                            className="sx-lockedtail"
                            style={{ left: `${(laneSpanSec / Math.max(0.01, loaded.duration)) * 100}%` }}
                            onClick={() => { setUpgradeAnon(!user); setUpgradeReason('metronome'); setUpgradeOpen(true); }}
                            aria-label={`The click stops after ${FREE_METRONOME_SECONDS} seconds — Pro plays it through the whole song`}
                            title="Pro — keep the click going for the whole song"
                          >
                            {LockGlyph}
                          </button>
                        )}
                        </div>
                      </div>

                      {/* One playhead for the whole rack: a flag handle on the ruler and a
                          hairline dropping through every lane. Laid out as a ghost copy of
                          the .sx-ch grid so its third column lands exactly on the lanes
                          without measuring anything. */}
                      {/* Same three-column grid as .sx-phwrap, so the band lands on
                          the lanes without measuring anything. Under the playhead. */}
                      {/* Two different states wearing one costume until now: marking a
                          NEW range (green, offers Save) vs playing back a loop you have
                          already saved (accent, nothing to save). The comment lives out
                          here: inside `{ab && ( … )}` it would be an illegal second
                          sibling. */}
                      {ab && (
                        <div className={`sx-abwrap${armedSaved ? ' armed' : ''}`}>
                          <div /><div />
                          <div className="sx-phcol">
                            {/* Dim everything OUTSIDE the loop, so the part being
                                repeated is the only lit region — the usual DAW
                                reading. Behind the band and handles in DOM order so
                                they stay crisp, and pointer-events:none so scrubbing
                                the lanes still works through them. */}
                            <div
                              className="sx-abmask"
                              style={{ left: 0, width: `${(ab.a / Math.max(0.01, loaded.duration)) * 100}%` }}
                              onPointerDown={exitLoopAt}
                              title="Leave the loop"
                              aria-hidden
                            />
                            <div
                              className="sx-abmask"
                              style={{ left: `${(ab.b / Math.max(0.01, loaded.duration)) * 100}%`, right: 0 }}
                              onPointerDown={exitLoopAt}
                              title="Leave the loop"
                              aria-hidden
                            />
                            <div
                              className="sx-abgrip"
                              style={{
                                left: `${(ab.a / Math.max(0.01, loaded.duration)) * 100}%`,
                                width: `${((ab.b - ab.a) / Math.max(0.01, loaded.duration)) * 100}%`,
                              }}
                              onPointerDown={dragABWindow}
                              title="Drag to move the whole loop"
                            />
                            {!armedSaved && (
                              <input
                                className="sx-abname"
                                style={{ left: `${(ab.a / Math.max(0.01, loaded.duration)) * 100}%` }}
                                value={draftName}
                                maxLength={80}
                                /* The default label as the placeholder read like a
                                   VALUE already typed in — so it looked named, and
                                   nobody knew the field was theirs. Instruct instead,
                                   and name the fallback in the tooltip. */
                                placeholder="Name this loop (optional)"
                                title={`Give this loop a name — leave it blank to use "${goalDef(loaded.goal).title}"`}
                                onChange={(e) => setDraftName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); void saveLoop(draftName.trim() || undefined); }
                                  else if (e.key === 'Escape') { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); }
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                aria-label="Name this loop"
                              />
                            )}
                            <div
                              className="sx-abband"
                              onPointerDown={(e) => dragABWindow(e, true)}
                              title="Drag anywhere in the loop to move it · click to move the playhead"
                              style={{
                                left: `${(ab.a / Math.max(0.01, loaded.duration)) * 100}%`,
                                width: `${((ab.b - ab.a) / Math.max(0.01, loaded.duration)) * 100}%`,
                                ...(chordStrip?.mode === 'chart' && chordRowBottom
                                  ? { top: `${chordRowBottom}px` } : null),
                              }}
                            />
                            <button
                              className="sx-abh a"
                              style={{ left: `${(ab.a / Math.max(0.01, loaded.duration)) * 100}%` }}
                              onPointerDown={(e) => dragAB('a', e)}
                              title={`Loop start — ${fmtTime(ab.a)}`}
                              aria-label="Loop start"
                            >
                              A
                            </button>
                            <button
                              className="sx-abh b"
                              style={{ left: `${(ab.b / Math.max(0.01, loaded.duration)) * 100}%` }}
                              onPointerDown={(e) => dragAB('b', e)}
                              title={`Loop end — ${fmtTime(ab.b)}`}
                              aria-label="Loop end"
                            >
                              B
                            </button>
                            {/* Save rides the B marker: the action belongs on the
                                thing you just marked, not in a row across the page.
                                Flips to the inside of B near the end of the track so
                                it can't run off the lane. */}
                            {armedSaved ? (
                              // Sits on the A side: the exit belongs at the start of
                              // the loop, not trailing off the end. Flips to the inside
                              // of A when there is no room to its left.
                              <button
                                className={`sx-absave is-armed${(ab.a / Math.max(0.01, loaded.duration)) * 100 >= 13 ? ' left' : ''}`}
                                style={{ left: `${(ab.a / Math.max(0.01, loaded.duration)) * 100}%` }}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => { setArmedId(null); setAb(null); }}
                                title={`Looping "${armedSaved.label.replace(/\s+\d+:\d{2}$/, '')}" (${fmtTime(ab.a)}–${fmtTime(ab.b)}) — click to exit and play the whole track`}
                              >
                                {/* Name the loop that is playing — "Looping" alone
                                    didn't say WHICH, and the highlighted chip is all
                                    the way down in the row below. */}
                                <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <path d="M17 2l4 4-4 4" />
                                  <path d="M3 11V9a4 4 0 014-4h14" />
                                  <path d="M7 22l-4-4 4-4" />
                                  <path d="M21 13v2a4 4 0 01-4 4H3" />
                                </svg>
                                <span className="sx-absave-name">{armedSaved.label.replace(/\s+\d+:\d{2}$/, '')}</span>
                                <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" aria-hidden>
                                  <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                              </button>
                            ) : (
                              <>
                              {/* Cancel, mirroring Save across the range: Save rides B,
                                  this rides A. Drafting had no visible way out — the
                                  only exit was the pill in the row below. */}
                              <button
                                className={`sx-abcancel${(ab.a / Math.max(0.01, loaded.duration)) * 100 >= 13 ? ' left' : ''}`}
                                style={{ left: `${(ab.a / Math.max(0.01, loaded.duration)) * 100}%` }}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => { setArmedId(null); setAb(null); setDraftName(''); }}
                                title="Discard this range"
                              >
                                Cancel
                              </button>
                              <button
                                className={`sx-absave${(ab.b / Math.max(0.01, loaded.duration)) > 0.82 ? ' flip' : ''}`}
                                style={{ left: `${(ab.b / Math.max(0.01, loaded.duration)) * 100}%` }}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => saveLoop(draftName.trim() || undefined)}
                                title={`Save ${fmtTime(ab.a)}–${fmtTime(ab.b)} as a loop — you can name it right after`}
                              >
                                {savedFlash ? '\u2713 Saved' : 'Save'}
                              </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="sx-phwrap" aria-hidden="true">
                        <div /><div />
                        <div className="sx-phcol">
                          <div
                            className={`sx-ph${scrubbing ? ' grabbing' : ''}`}
                            ref={phRef}
                            style={chordStrip?.mode === 'chart' && chordRowBottom
                              ? ({ ['--ph-top' as string]: `${chordRowBottom}px` })
                              : undefined}
                          >
                            <span className="sx-ph-flag" onPointerDown={startScrub} />
                          </div>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* ── chord stage ──────────────────────────────────────
                        Time-driven but NOT time-scaled: at 30 s in ~600 px a
                        chord gets ~40 px, and a readable diagram needs ~90 —
                        so the carousel steps chord-by-chord while the ribbon
                        above keeps the time axis. */}
                    {chordStage && (
                      <div className="sx-chwrap">
                      {/* Left rail of channel strips, centre stage of shapes —
                          the reference's arrangement. The strips are the SAME
                          markup as the rack's, so mute/solo/level behave
                          identically in both views. */}
                      <div className="sx-chmixcol">
                        {STEM_UI.map((st) => {
                          const m = mix.stems[st.id];
                          const empty = loaded.emptyStems.has(st.id);
                          const dim = metroSolo || m.mute || (anySolo && !m.solo) || empty;
                          return (
                            <div key={st.id} className={`sx-strip${dim ? ' dim' : ''}`} style={{ ['--mc' as string]: st.color }}>
                              <div className="sx-strip-top">
                                <button className={`sx-ms${m.mute ? ' m-on' : ''}`} onClick={() => setStem(st.id, { mute: !m.mute })} title="Mute">M</button>
                                <button className={`sx-ms${m.solo ? ' s-on' : ''}`} onClick={() => setStem(st.id, { solo: !m.solo })} title="Solo">S</button>
                                <span className="sx-strip-n">{stemLabel(st.id, st.name)}{empty && <i className="sx-silent"> · silent</i>}</span>
                                <span className="sx-pct sx-mono">{Math.round(m.volume * 100)}%</span>
                              </div>
                              <input
                                className="sx-vol" type="range" min={0} max={100}
                                value={Math.round(m.volume * 100)}
                                onChange={(e) => setStem(st.id, { volume: Number(e.target.value) / 100 })}
                                aria-label={`${st.name} volume`}
                              />
                            </div>
                          );
                        })}
                        {metroStrip}
                      </div>

                      <div className="sx-chmain">
                        <div className="sx-chhead">
                          {/* The transport's "Chords" toggle is also the way back,
                              but it is at the far bottom of the page — the way out
                              belongs where the view you are in begins. Named, not a
                              bare chevron: it says where it goes. */}
                          <span className="sx-chhead-l">
                            <button
                              className="sx-chhead-back"
                              onClick={() => setChordStage(false)}
                              title="Back to the mixer"
                              aria-label="Back to the mixer"
                            >
                              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M19 12H5M11 6l-6 6 6 6" />
                              </svg>
                              <i className="sx-chhead-back-t">Back to Mixer</i>
                            </button>
                          </span>
                          <span className="sx-chhead-t">Chords</span>
                          <span className="sx-chhead-r">
                            <select
                              className="sx-chstyle"
                              value={shapeStyle}
                              onChange={(e) => setShapeStyle(e.target.value as ShapeStyle)}
                              title="Voicing difficulty"
                              aria-label="Voicing difficulty"
                            >
                              <option value="easy">Easy</option>
                              <option value="barre">Barre</option>
                            </select>
                          </span>
                        </div>
                      <div className="sx-chstage">
                        {(() => {
                          const segs = displayChords || [];
                          const labels = chordLabels || [];
                          // Past the free ceiling the shapes view would be a way
                          // around the gate, so it closes with the same message.
                          if (chordsGated && pos > chordSpanSec) {
                            return (
                              <div className="sx-chlocked">
                                <div className="sx-chlocked-art" aria-hidden>
                                  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="4" y="10.5" width="16" height="10.5" rx="2.4" />
                                    <path d="M8 10.5V7a4 4 0 018 0v3.5" />
                                  </svg>
                                </div>
                                <h4>Chords stop at {FREE_CHORDS_SECONDS} seconds</h4>
                                <p>
                                  The free plan reads the opening of a song. Pro maps the whole
                                  track — every chord, with the shapes to play it.
                                </p>
                                <button
                                  className="sx-chstage-cta"
                                  onClick={() => { setUpgradeAnon(!user); setUpgradeReason('chords'); setUpgradeOpen(true); }}
                                >
                                  See the whole song with Pro
                                </button>
                                <button className="sx-chlocked-back" onClick={() => seekTo(0)}>
                                  Back to the first {FREE_CHORDS_SECONDS} seconds
                                </button>
                              </div>
                            );
                          }
                          // Playable chords only: the model's silence gaps are
                          // not something you can fret.
                          const idxs = segs.map((_, i) => i).filter((i) => !!labels[i] && (!chordsGated || segs[i].start < chordSpanSec));
                          if (!idxs.length) {
                            return <div className="sx-chstage-empty">No chords detected in this section.</div>;
                          }
                          // Before playback starts (or inside a gap) show the
                          // first chord rather than an empty stage.
                          const at = idxs.includes(curChordIdx)
                            ? idxs.indexOf(curChordIdx)
                            : Math.max(0, idxs.filter((i) => i <= curChordIdx).length - 1);
                          const curI = idxs[at];
                          // Symmetric context, like the reference: the same
                          // count either side so the current shape sits dead
                          // centre and your eye never has to re-find it.
                          const CONTEXT = 2;
                          const prevIs = idxs.slice(Math.max(0, at - CONTEXT), at);
                          const nextIs = idxs.slice(at + 1, at + 1 + CONTEXT);
                          const curShape = shapeForChord(segs[curI].label, mix.transpose, shapeStyle);
                          // In a silence gap (intro, breakdown) the card shows
                          // what's COMING, and the bar fills toward its entry —
                          // say so, or the card reads as "playing now".
                          const upcoming = curChordIdx !== curI;
                          return (
                            <>
                              <div className="sx-chstage-past">
                                {prevIs.map((i) => {
                                  const sh = shapeForChord(segs[i].label, mix.transpose, shapeStyle);
                                  return (
                                    <button
                                      key={i}
                                      className="sx-chcard past"
                                      onClick={() => seekTo(segs[i].start + 0.01)}
                                      title={`Back to ${labels[i]}`}
                                    >
                                      {sh ? <ChordDiagram shape={sh} w={104} /> : null}
                                      <span className="sx-chcard-n">{labels[i]}</span>
                                    </button>
                                  );
                                })}
                              </div>

                              <div className={`sx-chcard cur${upcoming ? ' upcoming' : ''}`}>
                                {upcoming && <span className="sx-chcard-up">next up</span>}
                                {curShape ? <ChordDiagram shape={curShape} w={168} /> : <div className="sx-chstage-empty">—</div>}
                                <span className="sx-chcard-n big">{labels[curI]}</span>
                                {/* fills as the chord plays out — your cue to move */}
                                <span className="sx-chprog"><i ref={chordProgRef} /></span>
                              </div>

                              <div className="sx-chstage-next">
                                {nextIs.map((i) => {
                                  const sh = shapeForChord(segs[i].label, mix.transpose, shapeStyle);
                                  return (
                                    <button
                                      key={i}
                                      className="sx-chcard next"
                                      onClick={() => seekTo(segs[i].start + 0.01)}
                                      title={`Jump to ${labels[i]}`}
                                    >
                                      {sh ? <ChordDiagram shape={sh} w={104} /> : null}
                                      <span className="sx-chcard-n">{labels[i]}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      </div>
                      </div>
                    )}

                    {/* rack footer */}
                    <div className="sx-rack-foot">
                      <button className="sx-reset" onClick={resetMix}>Reset</button>
                      <span className="sx-legend"><b>M</b> mute · <b>S</b> solo · knob pans</span>
                      <span className="sx-spacer" />
                      <div className="sx-speedwrap">
                        <span className="sx-step-l">Speed</span>
                        {[0.5, 0.75, 1, 1.25, 1.5].map((v) => (
                          <button
                            key={v}
                            className={`sx-speed sx-mono${mix.speed === v ? ' on' : ''}`}
                            onClick={() => patchMix({ ...mix, speed: v })}
                          >
                            {v}×
                          </button>
                        ))}
                      </div>
                      <div className="sx-step">
                        <span className="sx-step-l">Transpose</span>
                        <button className="sx-step-b" onClick={() => patchMix({ ...mix, transpose: Math.max(-12, mix.transpose - 1) })}>−</button>
                        <span className="sx-step-v sx-mono">{mix.transpose > 0 ? '+' : ''}{mix.transpose} st</span>
                        <button className="sx-step-b" onClick={() => patchMix({ ...mix, transpose: Math.min(12, mix.transpose + 1) })}>+</button>
                        <span className="sx-step-r sx-mono">−12…+12</span>
                      </div>
                    </div>

                    <div className="sx-loops">
                      <span className="sx-lbl">Loops in this song</span>
                      {thisSongLoops.map((s) => {
                        const name = loopName(s);
                        const asking = confirmDel === s.id;
                        const editing = renaming?.id === s.id;
                        return (
                          /* Body and × are SIBLINGS, never nested — a button inside a
                             button is invalid and swallows the delete click. */
                          <span key={s.id} className={`sx-pill${loopIsActive(s.id) ? ' on' : ''}${asking ? ' asking' : ''}`}>
                            {editing ? (
                              <input
                                className="sx-pill-input"
                                ref={(el) => {
                                  // focus() then select() in the SAME tick. Selecting
                                  // from inside an onFocus handler does not survive —
                                  // the browser places its own caret afterwards.
                                  if (el && renameFocused.current !== s.id) {
                                    renameFocused.current = s.id;
                                    el.focus();
                                    el.select();
                                  }
                                }}
                                value={renaming.value}
                                maxLength={80}
                                onChange={(e) => setRenaming({ id: s.id, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); void commitRename(s, renaming.value); }
                                  else if (e.key === 'Escape') { e.preventDefault(); renameFocused.current = null; setRenaming(null); }
                                }}
                                onBlur={() => void commitRename(s, renaming.value)}
                                onPointerDown={(e) => e.stopPropagation()}
                                aria-label={`Rename loop ${name}`}
                              />
                            ) : (
                              <button
                                type="button"
                                className="sx-pill-open"
                                // Single click does nothing on purpose: these chips sit
                                // under the transport and a stray click used to yank the
                                // loop out from under whatever was playing. Playing is a
                                // deliberate double-click; renaming has its own affordances.
                                onClick={(e) => {
                                  if (asking) { disarmDelete(); return; }
                                  activateLoop(s.start, s.end, s.id);
                                  // Drop focus: a focused button swallows the next
                                  // Space/Enter (the browser re-clicks it) instead of
                                  // letting them reach play/pause.
                                  e.currentTarget.blur();
                                }}
                                onContextMenu={(e) => {
                                  if (asking) return;
                                  e.preventDefault(); // our rename, not the browser menu
                                  setRenaming({ id: s.id, value: name });
                                }}
                                title={asking ? 'Click to cancel' : `Play ${fmtTime(s.start)}–${fmtTime(s.end)} · right-click or ✎ to rename`}
                              >
                                {asking ? (
                                  <>Delete “{name}”?</>
                                ) : (
                                  <>
                                    {name}{' '}
                                    <span className="sx-mono" style={{ opacity: 0.65 }}>
                                      {fmtTime(s.start)}–{fmtTime(s.end)}
                                    </span>
                                  </>
                                )}
                              </button>
                            )}
                            {/* Rename. Double-click on the body does it too, but that
                                is not discoverable — and on a touchscreen it barely
                                exists. Hidden while asking to delete or already
                                editing, so the chip never offers two edits at once. */}
                            {!asking && !editing && (
                              <button
                                className="sx-pill-edit"
                                onClick={() => { disarmDelete(); setRenaming({ id: s.id, value: name }); }}
                                title={`Rename "${name}"`}
                                aria-label={`Rename loop ${name}`}
                              >
                                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <path d="M12 20h9" />
                                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                </svg>
                              </button>
                            )}
                            <button
                              className={`sx-pill-x${asking ? ' armed' : ''}`}
                              onClick={() => {
                                if (asking) { disarmDelete(); void removeSection(s.id); }
                                else armDelete(s.id);
                              }}
                              title={asking ? 'Yes, delete it' : `Delete "${name}"`}
                              aria-label={asking ? `Confirm delete ${name}` : `Delete loop ${name}`}
                            >
                              {asking ? (
                                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                  <path d="M4 12.5l5.5 5.5L20 6.5" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
                                  <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                              )}
                            </button>
                            {/* Cancel sits AFTER the confirm, so the pair reads
                                ✓ then ✕ — the body cancels too, but a destructive
                                prompt shouldn't rely on "click the question". */}
                            {asking && (
                              <button
                                className="sx-pill-cancel"
                                onClick={disarmDelete}
                                title="Cancel"
                                aria-label="Cancel delete"
                              >
                                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
                                  <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                              </button>
                            )}
                          </span>
                        );
                      })}
                      {/* A stepper, not a toolbar: one visible next action per
                          stage. Two bare pills side by side read as unrelated
                          actions and told a new user nothing about the order — and
                          save-without-a-range is exactly the 0:00-0:00 junk this row
                          used to fill up with. Saving now lives on the B marker. */}
                      {!thisSongLoops.length && !ab && <span className="sx-empty">No loops yet — grab the part you want to practice.</span>}
                      {ab && !armedSaved && !thisSongLoops.length && <span className="sx-empty">Drag A and B on the track, then hit Save on the green marker.</span>}
                      {ab && !armedSaved ? (
                        <button
                          className="sx-pill on"
                          onClick={() => { setArmedId(null); setAb(null); }}
                          title="Clear the A→B range"
                        >
                          A→B {fmtTime(ab.a)}–{fmtTime(ab.b)} · clear
                        </button>
                      ) : (
                        <button
                          className="sx-pill add"
                          onClick={() => {
                            const d = Math.max(0.01, loaded.duration);
                            const a = Math.min(Math.max(0, pos), Math.max(0, d - 2));
                            setArmedId(null); // a NEW loop is a draft, even if it lands on the same range
                            setDraftName('');
                            setAb({ a, b: Math.min(d, a + Math.max(2, Math.min(8, d * 0.25))) });
                            if (!mix.loop) patchMix({ ...mix, loop: true });
                          }}
                          title="Drop A and B markers on the track — drag them around the part you want to repeat"
                        >
                          + New loop
                        </button>
                      )}
                    </div>

                    {/* The transport is NOT rendered here — it lives at the foot of
                        <main> as a full-width bar. See `transportBar` below. */}
                  </div>

                </div>

                {/* Saved loops live in a slide-over rather than a permanent rail, so
                    the lanes get the full width. It overlays instead of pushing
                    the rack, and there's no scrim — you can keep playing and
                    scrubbing with the panel open. */}
                <aside className={`sx-drawer${sectionsOpen ? ' open' : ''}`} aria-hidden={!sectionsOpen}>
                  {/* No title here — the card below already carries
                      "Your loops · N saved". */}
                  <div className="sx-drawer-head">
                    <button className="sx-icobtn" onClick={() => setSectionsOpen(false)} title="Close" aria-label="Close loops">✕</button>
                  </div>
                  <div className="sx-drawer-body">
                    <RailSections rows={railRows.cut} raw={railRows.raw} scoped={railRows.scoped} onRemove={removeSection} onRemoveRaw={removeRawLoop} />
                    <div className="sx-card-2">
                      <div className="sx-tip-t">💡 Tip</div>
                      <p className="sx-tip-p">
                        Solo one stem and drop the speed to 0.75× to transcribe a lick. Transpose moves everything
                        together, so your loop stays in tune with the backing.
                      </p>
                    </div>
                  </div>
                </aside>
              </>
            )
          ) : (
            <>
              {/* ── SETUP ────────────────────────────────────────────── */}
              <div className="sx-head">
                <div>
                  <div className="sx-head-t">
                    {/* `.sx-back`, not a quieter variant: that styling is the
                        product of three rounds of this control being missed —
                        transparent-on-panel, then pill-shaped (it read as one
                        more selectable chip), then a 15% accent tint nobody
                        could see. The raised body + solid accent arrow is what
                        makes it findable; see the note on .sx-back. */}
                    {file && tab === 'upload' && (
                      <button className="sx-back sx-head-back" onClick={clearTrack} title="Pick a different song">
                        <i aria-hidden>←</i> Change song
                      </button>
                    )}
                    {/* The words are grouped so the button can centre against
                        the whole block. They stay baseline-aligned with each
                        other; the row above centres the two groups. */}
                    <span className="sx-head-txt">
                      <h1 className="sx-h1">Stem Separator</h1>
                      {/* The loaded song lives HERE and nowhere else on this
                          screen. It used to be repeated verbatim a few rows
                          down beside "Change song"; the control moved up to
                          join it rather than the title being printed twice. */}
                      {file && tab === 'upload' && <span className="sx-head-song">{cleanTrackName(file.name)}</span>}
                      {file && tab === 'upload' && <span className="sx-head-meta sx-mono">{fileMeta}</span>}
                    </span>
                  </div>
                  {/* The strapline sells the page to someone who just arrived.
                      Once a track is loaded it is spent copy competing for the
                      vertical room the actual step needs — and on the link tabs
                      "Upload a track" is the wrong instruction entirely. */}
                  {!focusMode && (
                    <p className="sx-sub">Upload a track, grab a section, and pull any instrument out clean.</p>
                  )}
                </div>
                {/* Quota's home is the left rail's card. This header copy is a
                    stand-in for when that card isn't on screen — collapsed rail
                    or mobile — so the count is never hidden, and the right-hand
                    column keeps a single clean left edge the rest of the time
                    (CSS decides; see .sx-quota-fallback). */}
                {isPro ? null : (
                  <div className="sx-credits sx-quota-fallback">
                    <span className="sx-credits-n">{remaining ?? '—'} of {usage?.daily_quota ?? 5} left</span>
                    <div className="sx-meter"><i style={{ width: `${Math.round(((remaining ?? 0) / (usage?.daily_quota || 5)) * 100)}%` }} /></div>
                    <Link className="sx-gopro" to="/pricing">Go Pro</Link>
                  </div>
                )}
              </div>

              {/* Grid, not two flex columns: the source tabs own row 1 of the
                  left column only, so the rail starts on row 2 — level with the
                  panel — however tall the tab row gets. */}
              <div className={`sx-cols sx-cols-setup${focusMode ? ' focus' : ''}`}>
                {/* The source band: which kind of source, and — once one is
                    loaded — the way out of it. "Change song" lived beside the
                    page title for a while and read as chrome hung off the H1;
                    here it sits with the control it undoes. */}
                <div className="sx-srcrow">
                  <div className="sx-seg sx-srcseg">
                    <button className={tab === 'upload' ? 'on' : ''} onClick={() => setTab('upload')}>{Icons.upload({ size: 15 })} Upload file</button>
                    <button className={tab === 'url' ? 'on' : ''} onClick={() => setTab('url')}>{Icons.youtube({ size: 15 })} YouTube</button>
                    <button className={tab === 'soundcloud' ? 'on' : ''} onClick={() => setTab('soundcloud')}>☁ SoundCloud</button>
                  </div>
                </div>

                <div className="sx-panel">
                    {/* Separating beats every source branch below.
                        It used to sit last, after the link-card test — and a
                        link capture closes its panel the moment the take is
                        kept, so the whole separation rendered as the "paste a
                        link" screen. From the outside that reads as the
                        capture having been thrown away, right up until the
                        mixer appears seconds later. */}
                    {mode === 'working' ? (
                      <div className="sx-work">
                        <div className="sx-work-t">
                          {goal === 'karaoke' ? 'Removing vocals…' : goal === 'backing' ? 'Building your backing track…' : 'Separating stems…'}
                        </div>
                        <div className="sx-work-s">{workPhase.label}</div>
                        <div className="sx-bar"><i style={{ width: `${workPhase.pct}%` }} /></div>
                        <div className="sx-work-s sx-mono">{fmtTime(regionLen)} · {workPhase.pct}%</div>
                      </div>
                    ) : tab !== 'upload' && !captureOpen && !captureOk ? (
                      /* ── link tabs where the audio can't be captured ──────
                         The same thing the capture stage already said, moved to
                         BEFORE the paste instead of after it. The old order made
                         you go and find a link, paste it, and wait for it to
                         load, only to be told to upload a file instead — the
                         constraint arrived after all of the effort.
                         Gated on captureOk, not on "is this a phone", so an
                         unsupported desktop browser is told the truth too; only
                         the advice differs, which is what `onPhone` picks. */
                      <div className="sx-linkcard">
                        <div className="sx-thumb" style={{ width: 46, height: 46 }}>
                          {tab === 'url' ? Icons.youtube({ size: 20 }) : <span style={{ fontSize: 20 }}>☁</span>}
                        </div>
                        <div className="sx-drop-t">
                          {tab === 'url' ? 'YouTube' : 'SoundCloud'} links need {onPhone ? 'a computer' : 'Chrome or Edge'}
                        </div>
                        <p className="sx-work-s" style={{ maxWidth: 430, margin: 0 }}>
                          {onPhone
                            ? `Pulling audio off a ${tab === 'url' ? 'YouTube' : 'SoundCloud'} page only works in Chrome or Edge on a computer. Send yourself the link and it opens there ready to go — or start from a song file on this phone.`
                            : `Pulling audio off a ${tab === 'url' ? 'YouTube' : 'SoundCloud'} page needs Chrome or Edge. In this browser, start from a song file — everything after that works the same.`}
                        </p>
                        {/* ── the bridge ──────────────────────────────────
                            Phones ended here: told to use a computer and left
                            to carry the link across themselves. Paste it once
                            and the share sheet takes it — the desktop opens on
                            `?src=`, drops it straight into the capture flow and
                            picks up exactly where this stopped. Only offered on
                            a phone: an unsupported DESKTOP browser is already on
                            the machine that can do this, so "send it to your
                            computer" would be nonsense there. */}
                        {onPhone && (
                          <div className="sx-sendrow">
                            <input
                              className="rl-input"
                              type="url"
                              inputMode="url"
                              spellCheck={false}
                              placeholder={tab === 'url' ? 'Paste the YouTube link' : 'Paste the SoundCloud link'}
                              value={sendLinkInput}
                              onChange={(e) => setSendLinkInput(e.target.value)}
                              aria-label={tab === 'url' ? 'YouTube link to send to your computer' : 'SoundCloud link to send to your computer'}
                            />
                            {sendLinkOk ? (
                              <ContinueOnDesktop
                                handoff={{ kind: 'link', url: sendLinkInput.trim() }}
                                title="Open it on your computer"
                                body={`The link opens the Studio with this ${tab === 'url' ? 'video' : 'track'} already loaded — nothing to paste again.`}
                                shareText={`Open this on your computer to practise ${tab === 'url' ? 'this video' : 'this track'} in Riffloop.`}
                                onShown={() => funnelEvent('rl_desktop_bridge_shown', { where: 'link', kind: tab === 'url' ? 'youtube' : 'soundcloud', tier: isPro ? 'pro' : 'free' }, 'studio')}
                                onSent={(how) => funnelEvent('rl_desktop_bridge_send', { where: 'link', how, kind: tab === 'url' ? 'youtube' : 'soundcloud', tier: isPro ? 'pro' : 'free' }, 'studio')}
                              />
                            ) : (
                              /* Deliberately quiet until the link parses: an
                                 armed "Send" that would hand the computer a
                                 broken URL is worse than no button. */
                              <p className="sx-work-s" style={{ margin: 0 }}>
                                {sendLinkInput.trim()
                                  ? `That doesn't look like a ${tab === 'url' ? 'YouTube video' : 'SoundCloud track'} link.`
                                  : 'Paste the link and we’ll hand it to your computer.'}
                              </p>
                            )}
                          </div>
                        )}
                        <button className="sx-btn-ghost" onClick={() => setTab('upload')}>
                          {Icons.upload({ size: 15 })} {onPhone ? 'Or upload a song from this phone' : 'Upload a song'}
                        </button>
                      </div>
                    ) : tab !== 'upload' && !captureOpen ? (
                      /* ── link card: confirm the track before embedding ── */
                      <div className="sx-linkcard">
                        <div className="sx-thumb" style={{ width: 46, height: 46 }}>
                          {tab === 'url' ? Icons.youtube({ size: 20 }) : <span style={{ fontSize: 20 }}>☁</span>}
                        </div>
                        <div className="sx-drop-t">
                          {tab === 'url' ? 'Practice a song straight from YouTube' : 'Practice a track from SoundCloud'}
                        </div>
                        <div className="sx-drop-s">Paste a link — pick a section, mute vocals or instruments, loop it.</div>

                        <div className="sx-linkrow">
                          <input
                            className="rl-input"
                            type="url"
                            spellCheck={false}
                            placeholder={tab === 'url' ? 'https://www.youtube.com/watch?v=…' : 'https://soundcloud.com/artist/track'}
                            value={linkInput}
                            onChange={(e) => setLinkInput(e.target.value)}
                            onPaste={(e) => {
                              // SoundCloud needs no lookup, so a pasted track link
                              // can go straight into the picker.
                              if (tab !== 'soundcloud') return;
                              const v = e.clipboardData.getData('text').trim();
                              if (!parseSoundCloudUrl(v)) return;
                              setLinkInput(v);
                              autoOpenRef.current = true;
                              e.preventDefault();
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter' && linkState === 'ok') openCapture(); }}
                            aria-label={tab === 'url' ? 'YouTube link' : 'SoundCloud link'}
                          />
                          {!!linkInput && <button className="sx-btn-ghost" onClick={clearLink}>Clear</button>}
                        </div>

                        {linkState === 'validating' && <div className="sx-linknote">Validating link…</div>}
                        {linkState === 'bad' && (
                          <div className="sx-linknote bad">
                            {tab === 'url'
                              ? 'This video can’t be loaded — it may be private, removed or region-locked. Try another link.'
                              : 'That doesn’t look like a SoundCloud track link. Use one like soundcloud.com/artist/track — single tracks only, not playlists.'}
                          </div>
                        )}

                        {linkMeta && (linkState === 'ok' || linkState === 'unknown') && (
                          <div className="sx-linkprev">
                            {linkMeta.thumb
                              ? <img className="sx-linkprev-thumb" src={linkMeta.thumb} alt="" loading="lazy" />
                              : <div className="sx-sec-ic" style={{ width: 56, height: 42, flex: '0 0 56px' }}>{Icons.music({ size: 18 })}</div>}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="sx-linkprev-t">{linkMeta.title}</div>
                              {/* Channel, then whatever /api/yt-meta could add.
                                  Each stat renders only when present, so the
                                  row degrades to plain channel-only if the
                                  proxy is unavailable. */}
                              <div className="sx-linkprev-a">
                                {linkMeta.author && <span>{linkMeta.author}</span>}
                                {linkMeta.views != null && (
                                  <span className="sx-linkprev-stat">
                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                      <path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12z" />
                                      <circle cx="12" cy="12" r="3" />
                                    </svg>
                                    {compactCount(linkMeta.views)} views
                                  </span>
                                )}
                                {linkMeta.likes != null && (
                                  <span className="sx-linkprev-stat">
                                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden>
                                      <path d="M12 20.5S3.5 15 3.5 9.2A4.2 4.2 0 0 1 12 7a4.2 4.2 0 0 1 8.5 2.2C20.5 15 12 20.5 12 20.5z" />
                                    </svg>
                                    {compactCount(linkMeta.likes)}
                                  </span>
                                )}
                                {!!linkMeta.durationSec && (
                                  <span className="sx-linkprev-stat sx-mono">{fmtTime(linkMeta.durationSec)}</span>
                                )}
                              </div>
                            </div>
                            <button className="sx-btn-primary" onClick={openCapture}>Continue →</button>
                          </div>
                        )}

                        {tab === 'url' && (
                          <p className="sx-linkfoot">
                            Prefer to stay on YouTube? The{' '}
                            <a href="https://chromewebstore.google.com/detail/riffloop-ai-stem-splitter/meinmcdiijnhaljldocknpglaehbmbmd" target="_blank" rel="noreferrer">Riffloop extension</a>{' '}
                            splits stems right on the video page.
                          </p>
                        )}
                      </div>
                    ) : tab !== 'upload' && captureOpen && linkMeta ? (
                      /* ── capture panel: the real player + section picker ── */
                      <>
                        <div className="sx-caphead">
                          <button
                            className="sx-back"
                            onClick={() => { stopLinkPlayback(); setCaptureOpen(false); }}
                            disabled={capturing}
                            title="Pick a different song"
                          >
                            <i aria-hidden>←</i> Change song
                          </button>
                          <div className="sx-caphead-t">
                            <b>{linkMeta.title}</b>
                            {(linkMeta.author || link.duration > 0) && (
                              <span>
                                {linkMeta.author}
                                {linkMeta.author && link.duration > 0 ? ' · ' : ''}
                                {link.duration > 0 ? fmtTime(link.duration) : ''}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Two columns that answer two different questions:
                            "which part of this song" on the left — the player
                            with its own section picker directly beneath it —
                            and "what do you want done to it" on the right.
                            The picker used to run full width UNDER the row,
                            which left a 250px hole below the video and pushed
                            itself under the sticky dock, so the one control the
                            step exists for was both off-screen and covered. */}
                        <div className={`sx-capstage${linkMeta.kind === 'sc' ? ' sc' : ''}`}>
                          <div className={`sx-embed${linkMeta.kind === 'sc' ? ' sc' : ''}`}>
                            {linkMeta.kind === 'yt'
                              ? <div ref={link.ytHostRef} />
                              : <iframe ref={link.scFrameRef} title="SoundCloud player" allow="autoplay" scrolling="no" frameBorder="no" />}
                          </div>
                          <div className="sx-capstage-side">
                        {!captureOk ? (
                          <div className="sx-work">
                            <p className="sx-work-s" style={{ maxWidth: 460 }}>
                              Grabbing audio from {linkMeta.kind === 'sc' ? 'SoundCloud' : 'YouTube'} needs Chrome or Edge on a
                              computer. On a phone, upload an audio file instead and everything else works the same.
                            </p>
                            <button className="sx-btn-primary" onClick={() => { setTab('upload'); setCaptureOpen(false); }}>
                              {Icons.upload({ size: 15 })} Upload a file instead
                            </button>
                          </div>
                        ) : capturing ? (
                          /* ── live capture ── */
                          <div className="sx-caplive">
                            {/* Coach mark for Chrome's share prompt. That dialog
                                is browser UI — the page can neither draw over it
                                nor read where its Allow button is — but Chrome
                                always anchors it centred under the toolbar, so
                                this sits in the clear space beside it and points
                                at where Allow lands. Aimed, not tracked.
                                Portalled to <body> on purpose: the shell's
                                `zoom: 0.9` would shrink these offsets by 10%
                                while Chrome's dialog stays at true viewport
                                pixels, so the arrow would miss. See the CSS. */}
                            {phase === 'permission' && createPortal(
                              <>
                                {/* Chrome's own scrim dims the card and the page
                                    by the same amount, so it never buys the card
                                    any separation. Darkening the page ourselves
                                    is the only lever we own — the card sits above
                                    this and becomes the one lit thing. */}
                                <div className="sx-allowveil" aria-hidden />
                                <div
                                  className="sx-allowtip"
                                  role="status"
                                  style={{ ['--sx-allow-y' as string]: `${allowY ?? 384}px` }}
                                >
                                  {/* A drawn marker arrow rather than a nub: it
                                      has to cross the gap to the button to read
                                      as pointing AT something. Redrawn on a loop
                                      — the motion is what gets noticed once the
                                      prompt has taken focus. */}
                                  {/* One quarter-turn, tail rising out of the card,
                                      ending dead horizontal at the tip — the final
                                      tangent is what the eye extends, and it runs
                                      straight through Allow. Tip at viewBox (56,20)
                                      = cardLeft−42 / cardTop−54; the CSS geometry
                                      contract is written against those two numbers. */}
                                  <svg className="sx-allowarrow" viewBox="0 0 128 96" fill="none" aria-hidden>
                                    <path className="sx-allowarrow-line" d="M116 90 C130 48 104 20 56 20" />
                                    <path className="sx-allowarrow-head" d="M56 20 L84 8 M56 20 L78 44" />
                                  </svg>
                                  <b>Hit “Allow” to start recording</b>
                                  <span>
                                    Keep “This tab” selected and leave tab audio on — that’s how the sound gets in.
                                  </span>
                                </div>
                              </>,
                              document.body,
                            )}
                            <div className="sx-bar">
                              <i style={{ width: `${regionLen > 0 ? Math.min(100, (capturedSec / regionLen) * 100) : 0}%` }} />
                            </div>
                            <div className="sx-capphase">
                              {phase === 'recording' && <span className="sx-capdot" />}
                              {phase === 'permission'
                                ? 'In Chrome’s dialog: keep “This tab” selected and turn on “Also share tab audio”.'
                                : phase === 'ad-wait'
                                  ? (linkMeta.kind === 'sc' ? 'Waiting for the track to start…' : 'Waiting for the song to start — ads are kept out of the take.')
                                  : phase === 'paused'
                                    ? `Capture paused — it resumes automatically when the ${linkMeta.kind === 'sc' ? 'track' : 'song'} plays on.`
                                    : phase === 'finishing'
                                      ? 'Wrapping up the take…'
                                      : `Capturing — ${fmtTime(Math.min(capturedSec, regionLen))} of ${fmtTime(regionLen)}`}
                            </div>
                            {(phase === 'recording' || phase === 'paused' || phase === 'ad-wait') && (
                              <div className="sx-capwarn">
                                It’s recording in real time — leave this {linkMeta.kind === 'sc' ? 'track' : 'video'} playing.
                                Don’t pause, seek, change speed, switch to another {linkMeta.kind === 'sc' ? 'track' : 'video'},
                                or stop sharing until it finishes.
                              </div>
                            )}
                            {phase !== 'finishing' && (
                              <div className="sx-caplive-actions">
                                {capturedSec >= 3 && (
                                  <button className="sx-btn-primary" onClick={keepWhatWeHave}>
                                    {Icons.check({ size: 15 })} Use what I’ve got
                                  </button>
                                )}
                                <button className="sx-btn-ghost" onClick={cancelCapture}>Cancel</button>
                              </div>
                            )}
                          </div>
                        ) : link.duration > 0 ? (
                          /* ── the choice, beside the player ── */
                          <>
                            {linkMeta.kind === 'sc' && !link.scUnlocked && (
                              <div className="sx-unlock">
                                <span className="sx-unlock-ic" aria-hidden="true">▶</span>
                                <span>
                                  <b>Press play on the SoundCloud player above once</b> — the browser needs one click on the
                                  player itself before this page can drive it. After that, everything below works.
                                </span>
                              </div>
                            )}
                            {goalChooser}
                          </>
                        ) : (
                          <div className="sx-work">
                            <div className="rl-spinner" />
                            <div className="sx-work-s">Loading the {linkMeta.kind === 'sc' ? 'track' : 'video'}…</div>
                          </div>
                        )}
                          </div>
                        </div>

                        {/* The picker runs the full width under the row: it is the
                            precision control here, and a 5-minute song across a
                            half-width column costs real accuracy per pixel. It fits
                            below the fold only because the decision column above it
                            was cut down (chips instead of tiles, one-line cards). */}
                        {captureOk && !capturing && link.duration > 0 && (
                          <div className="sx-wavebox">
                            <div className="sx-cap-head">
                              <span className="sx-lbl sx-steph">Choose your section</span>
                              {/* The drag instruction moved onto the waveform
                                  itself (Waveform's `dragHint` call-out), so
                                  this line is free to carry the standing
                                  constraint instead of repeating it. */}
                              {/* A warning slot, not a hint slot: it speaks only
                                  when the selection has hit the capture cap. */}
                              {regionLen >= MAX_CAPTURE_SEC && (
                                <span className="sx-field-hint warn">
                                  {`5-min max — split longer ${linkMeta.kind === 'sc' ? 'tracks' : 'songs'}`}
                                </span>
                              )}
                            </div>
                            <Waveform
                              peaks={abBars}
                              duration={link.duration}
                              region={region}
                              onRegion={auditionRegion}
                              capLabel={atSectionCap ? `${FREE_SECTION_SECONDS}s max — click to unlock full length` : null}
                              capPulse={capFlash}
                            onCapClick={() => { setUpgradeReason('length'); setUpgradeOpen(true); }}
                              onSeekPlay={(t) => {
                                const p = link.playerRef.current;
                                if (!p) return;
                                previewRef.current = true;
                                setPreviewingLink(true);
                                try { p.seekTo(t, true); p.playVideo(); } catch { /* ignore */ }
                              }}
                              accent="var(--sx-accent)"
                              playing={previewingLink}
                              playPos={link.pos}
                              height={onPhone ? 108 : 84}
                              axis={false}
                              lettered
                              dragHint={gd.hint}
                              bands={suggestions.map((sg) => ({
                                startSec: sg.start,
                                endSec: sg.end,
                                color: sg.kind === 'heat' ? '#f59e0b' : 'var(--sx-accent)',
                                label: sg.kind === 'heat' ? '🔥 Most replayed' : sg.label,
                              }))}
                            />
                            <div className="sx-row">
                              <div className="sx-ab">
                                <span className="sx-ab-k sx-mono">A</span>
                                <input
                                  className="sx-ab-in sx-mono"
                                  value={editA ?? fmtTime(region.start)}
                                  onFocus={() => setEditA(fmtTime(region.start))}
                                  onChange={(e) => setEditA(e.target.value)}
                                  onBlur={commitA}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  spellCheck={false}
                                  aria-label="Section start time"
                                />
                                <span className="sx-ab-k sx-mono" style={{ marginLeft: 6 }}>B</span>
                                <input
                                  className="sx-ab-in sx-mono"
                                  value={editB ?? fmtTime(region.end)}
                                  onFocus={() => setEditB(fmtTime(region.end))}
                                  onChange={(e) => setEditB(e.target.value)}
                                  onBlur={commitB}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  spellCheck={false}
                                  aria-label="Section end time"
                                />
                                <span className={`sx-ab-len sx-mono${atSectionCap ? ' capped' : ''}`}>
                                  = {fmtTime(regionLen)}{atSectionCap && <i className="sx-ab-cap">max</i>}
                                </span>
                              </div>
                              <button
                                className="sx-playbtn"
                                onClick={toggleLinkPreview}
                                aria-label={previewingLink ? 'Pause' : 'Play the selection'}
                                title={previewingLink ? 'Pause' : 'Play the selection'}
                              >
                                {previewingLink ? Icons.pause({ size: 18 }) : Icons.play({ size: 18 })}
                              </button>
                              {previewingLink && <span className="sx-playpos sx-mono">{fmtTime(link.pos)}</span>}
                            {/* The advice belongs beside the numbers it is about:
                                you read "= 0:23" and this says whether that is a
                                good length. It replaced a full card below the
                                picker whose two sentences had both been taken
                                over — the drag half by the call-out on the wave,
                                the speed half by this line. */}
                            <span className="sx-rowtip">
                              <i aria-hidden="true">i</i>
                              {/* State the ceiling BEFORE the drag, not when it bites —
                                  the same rule /splitter follows. Pro keeps the speed tip. */}
                              {goal !== 'loop' && !isPro
                                ? `Free separations cover up to ${FREE_SECTION_SECONDS} seconds — looping is unlimited.`
                                : 'Tighter selections separate cleaner — and faster.'}
                            </span>
                            </div>

                            {suggestions.length > 0 && (
                              <div className="sx-sugg">
                                <span className="sx-lbl">Jump to</span>
                                {suggestions.map((sg, i) => {
                                  const hot = sg.kind === 'heat';
                                  const glyph = hot ? '🔥' : /solo|guitar|riff/i.test(sg.label) ? '🎸' : '🎯';
                                  return (
                                    <button
                                      key={i}
                                      className={`sx-sugg-chip${hot ? ' hot' : ''}`}
                                      onClick={() => applySuggestion(sg)}
                                      title={`${sg.label} · ${fmtTime(sg.start)}–${fmtTime(sg.end)} — set the selection here`}
                                    >
                                      <span>{glyph}</span>
                                      {hot ? 'Most replayed' : sg.label}
                                      <span className="sx-mono" style={{ opacity: 0.6 }}>{fmtTime(sg.start)}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Quality and the action live in the dock — sticky at
                            the bottom of the step, and now clear of the picker
                            instead of parked on top of it. */}
                        {setupDock}
                      </>
                    ) : !file ? (
                      <div
                        className={`sx-drop${dragOver ? ' drag' : ''}`}
                        onClick={() => fileInput.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          const f = e.dataTransfer.files?.[0];
                          if (f) void pickFile(f);
                        }}
                      >
                        {decoding ? (
                          <>
                            <div className="rl-spinner" />
                            <div className="sx-drop-t">Reading your track…</div>
                          </>
                        ) : (
                          <>
                            <div className="sx-thumb" style={{ width: 46, height: 46 }}>{Icons.upload({ size: 20 })}</div>
                            {/* Two spellings, one shown at a time by CSS (no JS
                                branch, no second copy of the breakpoint): a phone
                                cannot be dropped onto, so telling it to is an
                                instruction it can't follow. See studio-next.css
                                @media (max-width: 560px). */}
                            <div className="sx-drop-t">
                              <span className="sx-drop-t-drag">Drop an audio file to begin</span>
                              <span className="sx-drop-t-tap">Add a song to get started</span>
                            </div>
                            <div className="sx-drop-s">MP3 · WAV · M4A · FLAC · OGG&nbsp; ·&nbsp; up to 50&nbsp;MB</div>
                            <button
                              className="sx-btn-primary sx-drop-btn"
                              onClick={(e) => { e.stopPropagation(); fileInput.current?.click(); }}
                            >
                              {Icons.file({ size: 16 })} Choose file
                            </button>
                          </>
                        )}
                        <input
                          ref={fileInput} type="file" accept="audio/*" hidden
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) void pickFile(f); e.target.value = ''; }}
                        />
                      </div>
                    ) : (
                      /* Loaded: the Moises-style focused step — the source it
                         is working on, the goal, the section, and the docked
                         action bar. The header row is the SAME control in the
                         SAME place as the link tabs' "Change song": leaving a
                         loaded source is one gesture wherever you started. */
                      <>
                        {goalChooser}

                        <div className="sx-wavebox">
                          <div className="sx-cap-head">
                            <span className="sx-lbl sx-steph">Choose your section</span>
                            {/* Nothing to say here any more — the size advice
                                moved down beside the A/B numbers. */}
                          </div>
                          <Waveform
                            peaks={srcPeaks}
                            duration={duration}
                            region={region}
                            onRegion={auditionUpload}
                            capLabel={atSectionCap ? `${FREE_SECTION_SECONDS}s max — click to unlock full length` : null}
                            capPulse={capFlash}
                            onCapClick={() => { setUpgradeReason('length'); setUpgradeOpen(true); }}
                            onSeekPlay={(t) => { const a = audioRef.current; if (a) { a.currentTime = t; setPreviewPos(t); void a.play().then(() => setPreviewing(true)); } }}
                            accent="var(--sx-accent)"
                            playing={previewing}
                            playPos={previewPos}
                            height={onPhone ? 140 : 112}
                            axis={false}
                            lettered
                            dragHint={gd.hint}
                          />
                          <div className="sx-row">
                            <div className="sx-ab">
                              <span className="sx-ab-k sx-mono">A</span>
                              <span className="sx-ab-v sx-mono">{fmtTime(region.start)}</span>
                              <span className="sx-ab-k sx-mono" style={{ marginLeft: 6 }}>B</span>
                              <span className="sx-ab-v sx-mono">{fmtTime(region.end)}</span>
                              <span className={`sx-ab-len sx-mono${atSectionCap ? ' capped' : ''}`}>
                                  = {fmtTime(regionLen)}{atSectionCap && <i className="sx-ab-cap">max</i>}
                                </span>
                            </div>
                            <button
                              className="sx-playbtn"
                              onClick={togglePreview}
                              aria-label={previewing ? 'Pause' : 'Play the selection'}
                              title={previewing ? 'Pause' : 'Play the selection'}
                            >
                              {previewing ? Icons.pause({ size: 18 }) : Icons.play({ size: 18 })}
                            </button>
                            {previewing && <span className="sx-playpos sx-mono">{fmtTime(previewPos)}</span>}
                          {/* The advice belongs beside the numbers it is about:
                              you read "= 0:23" and this says whether that is a
                              good length. It replaced a full card below the
                              picker whose two sentences had both been taken
                              over — the drag half by the call-out on the wave,
                              the speed half by this line. */}
                          <span className="sx-rowtip">
                            <i aria-hidden="true">i</i>
                            {/* State the ceiling BEFORE the drag, not when it bites —
                                the same rule /splitter follows. Pro keeps the speed tip. */}
                            {goal !== 'loop' && !isPro
                              ? `Free separations cover up to ${FREE_SECTION_SECONDS} seconds — looping is unlimited.`
                              : 'Tighter selections separate cleaner — and faster.'}
                          </span>
                          </div>
                        </div>

                        {setupDock}
                      </>
                    )}
                </div>

                {/* The rail belongs to the pre-load screen. Once a source is
                    loaded the step focuses down, Moises-style — sections come
                    back in the mixer. */}
                {!focusMode && <aside className="sx-rail">
                  {/* Mirrors the live rail: with no track loaded you get the
                      last thing you worked on; with one loaded, its sections. */}
                  {!file ? (
                    <div className="sx-card">
                      <div className="sx-section-label">
                        {library.length ? 'Continue where you left off' : 'Your loops'}
                      </div>
                      {library.length ? (
                        <div className="sx-sec-list">
                          {(() => {
                            const r = library[0];
                            const yt = ytIdOf(r.key);
                            return (
                              <button
                                type="button"
                                className="sx-trackrow"
                                onClick={() => void reopenTrack(r.key, r.sepKey)}
                                disabled={resuming}
                                title={`Resume ${r.title}`}
                              >
                                {yt ? (
                                  <img className="sx-sec-thumb" src={`https://i.ytimg.com/vi/${yt}/mqdefault.jpg`} alt="" loading="lazy" />
                                ) : (
                                  <div className="sx-sec-ic">{Icons.music({ size: 18 })}</div>
                                )}
                                <div className="sx-sec-mini">
                                  <b>{r.title}</b>
                                  <span className="sx-mono">
                                    {r.kind === 'YouTube' ? 'YouTube clip' : r.kind === 'SoundCloud' ? 'SoundCloud clip' : 'Upload'}
                                    {r.loops > 0 ? ` · ${r.loops} section${r.loops === 1 ? '' : 's'}` : ' · last session'}
                                  </span>
                                </div>
                                <MiniBars seed={hashSeed(r.key)} />
                              </button>
                            );
                          })()}
                        </div>
                      ) : savedTotal > 0 ? (
                        /* They DO have saved sections — the filter above just found
                           none whose audio is on this device (stems never leave the
                           machine that made them). Saying "no saved sections yet"
                           here would flatly contradict their Songs list. */
                        <p className="sx-empty">
                          Your stems stay on the device that made them, so there’s
                          nothing to reopen here.
                          <br />
                          Add the track again to pick up where you left off.
                        </p>
                      ) : (
                        <p className="sx-empty">
                          No saved sections yet.
                          <br />
                          Isolate a part to save it here.
                        </p>
                      )}
                    </div>
                  ) : (
                    <RailSections rows={railRows.cut} raw={railRows.raw} scoped={railRows.scoped} onRemove={removeSection} onRemoveRaw={removeRawLoop} />
                  )}

                  {/* Files this browser has loaded before. Only on the pre-load
                      screen — once a track is open the shortcut has nothing
                      left to shorten. */}
                  {!file && recents.length > 0 && (
                    <div className="sx-card">
                      <div className="sx-section-label">Recent uploads</div>
                      <div className="sx-sec-list">
                        {recents.map((r) => (
                          <div className="sx-uprow" key={r.id}>
                            <button
                              type="button"
                              className="sx-uprow-open"
                              onClick={() => void openRecent(r)}
                              disabled={decoding}
                              title={`Load ${r.name}`}
                            >
                              <div className="sx-sec-ic">{Icons.music({ size: 18 })}</div>
                              <div className="sx-sec-mini">
                                <b>{cleanTrackName(r.name)}</b>
                                <span className="sx-mono">
                                  Upload · {fmtTime(r.duration)}
                                  <i className="sx-upsize"> · {fmtBytes(r.size)}</i>
                                </span>
                              </div>
                              <UpWave peaks={r.peaks} />
                            </button>
                            <button
                              type="button"
                              className="sx-uprow-x"
                              onClick={() => void dropRecent(r.id)}
                              title="Forget this file"
                              aria-label={`Forget ${r.name}`}
                            >
                              {Icons.x({ size: 13 })}
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="sx-uphint">Kept on this device only.</p>
                    </div>
                  )}

                </aside>}
              </div>

              {/* Same persistent rights reminder + legal links the live Studio
                  carries under the setup surface. */}
              <Footer />
            </>
          )}
        </div>

        {transportBar}

        {/* mobile tab bar */}
        <div className="sx-mtabs">
          <button className={screen === 'setup' ? 'on' : ''} onClick={() => setScreen('setup')}>
            <Ico d={navItems[0].icon} size={17} sw={1.8} /><span>Separate</span>
          </button>
          <button className={screen === 'mixer' ? 'on' : ''} onClick={() => loaded && setScreen('mixer')} disabled={!loaded}>
            <Ico d={navItems[1].icon} size={17} sw={1.8} /><span>Mixer</span>
          </button>
          <button className={screen === 'library' ? 'on' : ''} onClick={() => setScreen('library')}>
            <Ico d={navItems[2].icon} size={17} sw={1.8} /><span>Songs</span>
          </button>
        </div>

        <audio ref={audioRef} src={srcUrl || undefined} onTimeUpdate={onAudioTime} onPause={() => setPreviewing(false)} hidden />

      <UpgradeModal
        open={upgradeOpen}
        reason={upgradeReason}
        anonymous={upgradeAnon}
        dailyQuota={usage?.daily_quota ?? 5}
        remaining={remaining ?? 0}
        resetsAt={usage?.resets_at ?? null}
        loopCap={loopCap}
        freeSeconds={FREE_SECTION_SECONDS}
        freeChordsSeconds={FREE_CHORDS_SECONDS}
        onClose={() => setUpgradeOpen(false)}
      />
      <RightsModal
        open={rightsOpen}
        onCancel={() => { pendingRun.current = null; setRightsOpen(false); }}
        onAccepted={() => {
          setRightsOpen(false);
          const p = pendingRun.current;
          pendingRun.current = null;
          if (p === 'link') void runLinkCapture();
          else if (p === 'upload') void runSeparation();
        }}
      />
      {gate && (
        <ExportGateModal
          gate={gate}
          onClose={() => {
            funnelEvent('rl_export_gate_dismissed', { ...exportEventBase(), kind: gate.kind === 'wav' ? 'wav' : gate.planNotice ? 'plan' : 'preview' }, 'studio');
            setGate(null);
          }}
          onUpgrade={() => {
            funnelEvent('rl_export_upgrade_click', { ...exportEventBase(), kind: gate.kind === 'wav' ? 'wav' : gate.planNotice ? 'plan' : 'preview' }, 'studio');
            setGate(null);
          }}
          /* The offer card opens checkout in this same tab, so the gate must
             stay mounted while it does — this records the same intent as
             onUpgrade above without unmounting the card mid-request. */
          onUpgradeIntent={() => {
            funnelEvent('rl_export_upgrade_click', { ...exportEventBase(), kind: gate.kind === 'wav' ? 'wav' : 'preview' }, 'studio');
          }}
          onProceed={() => {
            setGate(null);
            if (gate.kind === 'export') proceedExport(gate.target, gate.format);
          }}
        />
      )}
    </AppShell>
  );
}

// Moises-style plan-gate card, shown when a free user STARTS an export the plan
// constrains (locked WAV, or a section longer than the free preview window) —
// instead of a dead menu with an inline "Pro feature" note. The preview variant
// still lets the export through, trimmed, via the ghost button. The planNotice
// variant is awareness, not a constraint: the section is UNDER the cap, the
// export proceeds untouched, and the card just makes the cap visible once per
// session (on short sections nothing else ever mentions it).
function ExportGateModal({ gate, onClose, onProceed, onUpgrade, onUpgradeIntent }: {
  gate: ExportGate;
  onClose: () => void;
  onProceed: () => void;
  onUpgrade: () => void;
  onUpgradeIntent: () => void;
}) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { refresh } = useAuth();

  const wav = gate.kind === 'wav';
  const trimmed = gate.kind === 'export' && gate.trimmed;
  const needsRights = gate.kind === 'export' && gate.needsRights;
  const plan = gate.kind === 'export' && !!gate.planNotice;

  async function confirm() {
    // The attestation is recorded server-side before the file is handed over —
    // if that write fails we stay put rather than downloading anyway.
    if (needsRights) {
      setSubmitting(true);
      try {
        await authApi.ackDownload();
        await refresh();
      } catch (e) {
        console.error('ackDownload failed', e);
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }
    onProceed();
  }

  // The two export ceilings that are genuinely Pro-only show the offer card —
  // plans and checkout in the dialog instead of a link to /pricing. This does
  // NOT add an interruption: both already opened this same modal at this same
  // moment, and the trimmed variant keeps its way through as the card's
  // secondary action, so an export that used to complete still completes.
  //
  // Deliberately NOT routed here:
  //   • needsRights — the one-time attestation is a checkbox that must be
  //     recorded server-side BEFORE any file is handed over. It stays on the
  //     card below, which folds it into the same dialog. So a first-ever
  //     export shows the old card; every later trimmed export shows the offer.
  //   • planNotice — the section is UNDER the cap and exports untouched. It is
  //     awareness, not a ceiling; putting a checkout in front of an export that
  //     was never going to be stopped is the action-button wall we removed once
  //     already (round 3, 2026-09-03).
  if ((wav || trimmed) && !needsRights) {
    return (
      <FullSongUpsell
        onClose={onClose}
        variant="export"
        exportKind={wav ? 'wav' : 'trimmed'}
        exportSeconds={FREE_EXPORT_PREVIEW_SECONDS}
        onUpgradeIntent={onUpgradeIntent}
        secondary={
          wav
            ? null
            : { label: `Export the first ${FREE_EXPORT_PREVIEW_SECONDS} seconds`, onClick: () => { void confirm(); } }
        }
      />
    );
  }

  // ⚠️ The plan facet's HEADLINE STATES THE RULE, and that is the whole point.
  // It was written twice the other way and both read as confusing:
  //   1. "Full-length exports" — a Pro-benefit title on a card the user did
  //      not ask for; nothing said why it had appeared.
  //   2. "Your 0:22 section exports in full." — Moises' shape, and the shape is
  //      the bug. Their popup works because something is being CONSUMED ("4
  //      separations remaining this month") — a countdown justifies the
  //      interruption. Ours is a static cap the user has NOT hit, so that
  //      headline interrupts to deliver good news while Upgrade Plan is the
  //      loudest thing on screen, and it makes the reader compare 0:22 against
  //      a 60 they only meet on the next line.
  // Leading with the rule answers "why am I seeing this?" in the first line;
  // the section's own length then lands as reassurance rather than as a riddle.
  const title = wav ? 'Lossless exports' : trimmed ? 'Export the full section' : plan ? `Free exports are limited to ${FREE_EXPORT_PREVIEW_SECONDS} seconds` : 'Before you download';
  const confirmLabel = trimmed ? `Export the first ${FREE_EXPORT_PREVIEW_SECONDS} seconds` : plan ? 'Continue to export' : 'I agree, download';

  return (
    <div className="rl-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="sx-up-title">
      <div className="sx-upcard">
        <button className="sx-upx" onClick={onClose} aria-label="Close">×</button>
        <div className="sx-upart"><Ico d={I_DL} size={46} sw={1.5} /></div>
        <h3 id="sx-up-title">{title}</h3>
        <p>
          {wav
            ? 'WAV is part of Pro — uncompressed, full-quality files of every channel and mix, at full length.'
            : trimmed
              ? `On the free plan an export covers the first ${FREE_EXPORT_PREVIEW_SECONDS} seconds — taken automatically. Go Pro for full-length exports and lossless WAV.`
              : plan
                ? `This section is ${fmtTime(gate.secLen ?? 0)}, so it exports in full. Upgrade your plan and unlock full-length exports.`
                : 'This file is for your personal practice only. The underlying audio stays the property of its rights-holders, and exports are only available for audio you uploaded.'}
        </p>

        {/* One-time per account, folded into whichever card the export needs
            rather than queued behind it as a second modal. */}
        {needsRights && (
          <label className="sx-upcheck">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <span>
              I confirm this is for my personal practice only. I have the rights to process this
              audio and I won't share or redistribute the result.
            </span>
          </label>
        )}

        {/* On a trimmed export the upgrade is the headline and the download is
            the quiet way out; otherwise the download IS the action. */}
        {trimmed || wav || plan ? (
          <Link className="sx-upcta" to="/pricing" onClick={onUpgrade}>Upgrade Plan</Link>
        ) : null}
        {!wav && (
          <button
            className={trimmed || plan ? 'sx-upghost' : 'sx-upcta'}
            onClick={confirm}
            disabled={(needsRights && !checked) || submitting}
          >
            {submitting ? 'Saving…' : confirmLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// Flat placeholder while a lane's real peaks are still decoding.

/**
 * Transport glyphs, drawn to match the reference player: solid triangles for
 * play and the two skips, hairline strokes for volume, pause and loop. Kept
 * local rather than added to the shared `studioIcons` set — nothing outside
 * this page uses them, and the live Studio's transport must not change.
 */
const Tx = {
  volume: (muted: boolean) => (
    <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 9.3h3.2L11.4 5.6v12.8L7.2 14.7H4z" />
      {muted ? (
        <path d="M15.6 9.8l4.8 4.4M20.4 9.8l-4.8 4.4" />
      ) : (
        <>
          <path d="M15.2 9.5a3.6 3.6 0 0 1 0 5" />
          <path d="M18 7.2a7.2 7.2 0 0 1 0 9.6" />
        </>
      )}
    </svg>
  ),
  // A fretboard: nut plus strings and frets. Reads as "chords" at 17px in a way
  // a musical note does not — a note means audio, this means shapes.
  chords: () => (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <path d="M4.6 4.6h14.8" strokeWidth="2.3" />
      <path d="M8.3 4.6v14.8M12 4.6v14.8M15.7 4.6v14.8" />
      <path d="M4.6 10.5h14.8M4.6 15.4h14.8" />
    </svg>
  ),
  loops: () => (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 6.5h10.5L15 4" />
      <path d="M17.5 6.5A6.2 6.2 0 0 1 12 16" />
      <path d="M17 17.5H6.5L9 20" />
      <path d="M6.5 17.5A6.2 6.2 0 0 1 12 8" />
    </svg>
  ),
  back: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
      <path d="M12.4 12 21 6.3v11.4zM3 12l8.6-5.7v11.4z" />
    </svg>
  ),
  fwd: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
      <path d="M11.6 12 3 17.7V6.3zM21 12l-8.6 5.7V6.3z" />
    </svg>
  ),
  play: () => (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
      <path d="M6.5 4.2 20 12 6.5 19.8z" />
    </svg>
  ),
  pause: () => (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
      <path d="M9.3 4.8v14.4M14.7 4.8v14.4" />
    </svg>
  ),
  /**
   * Two overlapping rings, each open on the side facing the other and finished
   * with a single barb — traced off a pixel map of the reference glyph, which
   * is a pair of circular arrows rather than one rotate arrow. The whole shape
   * is 180°-rotationally symmetric: the right half is the left half mirrored
   * through (12,12).
   */
  loop: () => (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.25 15.08A6.16 6.16 0 1 1 12.36 6.9" />
      <path d="M8.92 3.2 12.36 6.9" />
      <path d="M9.75 8.92A6.16 6.16 0 1 1 11.64 17.1" />
      <path d="M15.08 20.8 11.64 17.1" />
    </svg>
  ),
};

/**
 * Saved loops. There is only one word for these in the UI — "loop" — even
 * though the backend calls the mix-carrying kind a Section. The difference a
 * user can feel isn't where it's stored, it's what comes back: a loop saved
 * from the mixer restores the fader mix too, and says so with a stem chip. One
 * saved before separating is just an in/out point, so it carries no chip.
 */
function RailSections({
  rows, raw, onRemove, onRemoveRaw, scoped = true,
}: {
  rows: Section[];
  raw: RawLoop[];
  onRemove: (id: string) => void;
  onRemoveRaw: (id: string) => void;
  /** False when the list spans every song — rows then name their source. */
  scoped?: boolean;
}) {
  const total = rows.length + raw.length;
  const stemName = (id: string) => STEM_UI.find((s) => s.id === id)?.name || id;
  return (
    <div className="sx-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="sx-card-title">Your loops</div>
        <span className="sx-mono" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--sx-text-3)' }}>{total} saved</span>
      </div>
      {!total && (
        <p className="sx-empty">
          No loops yet. In the mixer, hit <b>+ New loop</b>, drag the green <b>A</b> and <b>B</b> markers around
          the part you want, then <b>Save</b> — your stem balance is saved with it.
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {raw.map((r) => (
          <div key={r.id} className="sx-sec">
            <span className="sx-dot" style={{ ['--dc' as string]: 'var(--sx-accent)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="sx-sec-n"><span className="sx-sec-t">{r.label}</span></div>
              {!scoped && <div className="sx-sec-src">{cleanTrackName(r.sourceRef)}</div>}
              <div className="sx-sec-r sx-mono">{fmtTime(r.start)}–{fmtTime(r.end)} · {fmtTime(r.end - r.start)}</div>
            </div>
            <button className="sx-sec-b" onClick={() => onRemoveRaw(r.id)}>Delete</button>
          </div>
        ))}
        {rows.map((s) => (
          <div key={s.id} className="sx-sec">
            <span className="sx-dot" style={{ ['--dc' as string]: STEM_COLOR[s.stem as StemName] || 'var(--sx-accent)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="sx-sec-n">
                <span className="sx-sec-t">{s.label}</span>
                {s.stem && <i className="sx-sec-chip">{stemName(s.stem)}</i>}
              </div>
              {!scoped && <div className="sx-sec-src">{s.title || cleanTrackName(s.video_id)}</div>}
              <div className="sx-sec-r sx-mono">{fmtTime(s.start)}–{fmtTime(s.end)} · {fmtTime(s.end - s.start)}</div>
            </div>
            <button className="sx-sec-b" onClick={() => onRemove(s.id)}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LibraryView({ rows, onOpen, busy }: {
  rows: { key: string; title: string; kind: string; loops: number; last: string }[];
  /** Opens the song in the Studio — cached stems go to the mixer, a link
      source lands on the link tab already filled in. */
  onOpen: (key: string) => void;
  busy?: boolean;
}) {
  function ago(iso: string): string {
    const t = Date.parse(iso);
    if (!t) return '—';
    const d = Math.floor((Date.now() - t) / 1000);
    if (d < 90) return 'Just now';
    if (d < 3600) return `${Math.floor(d / 60)} minutes ago`;
    if (d < 86400) return `${Math.floor(d / 3600)} hours ago`;
    if (d < 172800) return 'Yesterday';
    if (d < 604800) return `${Math.floor(d / 86400)} days ago`;
    return new Date(t).toLocaleDateString();
  }
  return (
    <>
      <div className="sx-head">
        <div className="sx-head-t">
          <h1 className="sx-h1">Your songs</h1>
        </div>
      </div>
      {!rows.length ? (
        <div className="sx-card"><p className="sx-empty">Nothing saved yet — separate or loop a section and it shows up here.</p></div>
      ) : (
        <div className="sx-lib">
          <div className="sx-lib-h">
            <span />
            <span>Title</span>
            <span className="sx-lib-hide">Source</span>
            <span className="sx-lib-hide">Loops</span>
            <span className="sx-lib-hide">Last opened</span>
          </div>
          {rows.map((r) => (
            <button
              key={r.key}
              type="button"
              className="sx-lib-r"
              onClick={() => onOpen(r.key)}
              disabled={busy}
              title={`Open ${r.title}`}
            >
              <div className="sx-thumb" style={{ width: 40, height: 40, flex: '0 0 40px' }}>{Icons.music({ size: 18 })}</div>
              <div style={{ minWidth: 0 }}>
                <div className="sx-lib-t">{r.title}</div>
                <div className="sx-lib-s sx-mono">{r.loops} {r.loops === 1 ? 'loop' : 'loops'} · {ago(r.last)}</div>
              </div>
              <div className="sx-lib-hide"><span className="sx-src">{r.kind}</span></div>
              <div className="sx-lib-c sx-mono sx-lib-hide">{r.loops}</div>
              <div className="sx-lib-c sx-lib-hide" style={{ fontSize: 12.5 }}>{ago(r.last)}</div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

export default StudioNext;
